import { toJpeg } from 'html-to-image';
import { Settings } from 'lucide-react';
import React, { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import api from '../api/axiosInstance';
import homewindowLogo from '../image/homewindow_logo.png';
import { PermissionManager } from '../utils/PermissionManager';
import './css/estimate.css';
import homewindowLogoWhite from './image/homewindow.png';
import kccLogoWhite from './image/kcchomeCC.png';
import lxhausysLogoWhite from './image/lxhausys.png';
import bannerImage from './image/banner.jpg';
import boardIcon from './image/board.png';
import boardNewIcon from './image/board_new.png';
import errorBoardIcon from './image/error_board.png';
import errorBoardNewIcon from './image/error_board_new.png';
import homefestarImage from './image/home2026.png';
import printIcon from './image/print.png';
import logoutIcon from './image/logout.png';
import EstimatePreviewReadOnly from './EstimatePreviewReadOnly';
import {
  calculateAllShasiPrices,
  calculateDiscountData,
  calculateTotals,
  getPrivateInputAmount,
  type ShasiRow,
  type OtherRow,
  type TongbaRow,
  type AdditionalRow,
  type DiscountRates,
  type SelectedGrades,
  type BrandPrices,
  type Totals,
  type UnitPricesMap,
  type FixedVariablesMap,
  type ShasiCalcParams,
} from './services/estimateCalculation';
import { useTableRows } from './hooks/useTableRows';
import { useUnitPricesData } from './hooks/useUnitPricesData';
import { useItemsData, categorizeItems } from './hooks/useItemsData';
import { useSession } from './hooks/useSession';
import { useEstimateList, EstimateType } from './hooks/useEstimateList';
import { parseEstimateData } from './services/parseEstimateData';
import BrandSpecs from './components/BrandSpecs';
import PricingSummaryCards from './components/PricingSummaryCards';

import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import {
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

// 검색 입력 컴포넌트 (리렌더링 최적화)
const DebouncedSearchInput = React.memo(({
  onSearchChange,
  placeholder = "",
  initialValue = ""
}: {
  onSearchChange: (value: string) => void;
  placeholder?: string;
  initialValue?: string;
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [localValue, setLocalValue] = useState(initialValue);
  const debounceRef = useRef<NodeJS.Timeout | null>(null);

  // 초기값이 변경되면 로컬 상태도 업데이트 (모달 열릴 때 초기화용)
  useEffect(() => {
    setLocalValue(initialValue);
  }, [initialValue]);

  const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setLocalValue(value);  // 로컬 상태만 즉시 업데이트 (빠른 입력 반응)

    // 기존 타이머 클리어
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    // 빈 문자열이면 즉시 전달
    if (value === '') {
      onSearchChange('');
      return;
    }

    // debounce 적용하여 부모에게 전달
    debounceRef.current = setTimeout(() => {
      onSearchChange(value);
    }, 300);
  }, [onSearchChange]);

  // 컴포넌트 언마운트 시 타이머 정리
  useEffect(() => {
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, []);

  return (
    <div style={{ position: 'relative', flex: 1 }}>
      <span
        style={{
          position: 'absolute',
          left: 10,
          top: '50%',
          transform: 'translateY(-50%)',
          pointerEvents: 'none',
          color: '#94a3b8',
        }}
      >
        <svg
          width="18"
          height="18"
          viewBox="0 0 20 20"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
        >
          <circle cx="9" cy="9" r="7" />
          <line x1="14.2" y1="14.2" x2="18" y2="18" strokeLinecap="round" />
        </svg>
      </span>
      <input
        ref={inputRef}
        type="text"
        placeholder={placeholder}
        value={localValue}
        onChange={handleChange}
        className="search-input"
        style={{
          height: 36,
          borderRadius: 8,
          border: '1.5px solid #d3d8e1',
          background: '#fff',
          fontSize: 14,
          paddingLeft: 36,
          width: '100%',
        }}
      />
    </div>
  );
});

// ShasiRow, OtherRow, TongbaRow, AdditionalRow는 ./services/estimateCalculation.ts에서 import

export type EstimateItem = {
  productType: string;
  installLocation?: string;
  item: string;
  width?: number | null;
  height?: number | null;
  quantity?: number;
  brand?: string | null;
  price: number;
  priceWithTax: number;
  note?: string;
  estimateNotes?: string;
  color?: string;
  directInput?: string | null;
  autoHandleScope?: string;
  combinedFrameDoorOptions?: string;
  specialBenefitType?: string;
  windowSpecBrand?: string;
};

// Sortable Row Component for drag and drop
const SortableRow: React.FC<{ children: React.ReactNode; id: number }> = ({ children, id }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  // children을 React.Children.map으로 처리하여 첫 번째 td에만 드래그 리스너 추가
  const modifiedChildren = React.Children.map(children, (child, index) => {
    if (index === 0 && React.isValidElement(child) && child.type === 'td') {
      // 첫 번째 td (NO 컬럼)에만 드래그 속성 추가
      return React.cloneElement(child as React.ReactElement<Record<string, unknown>>, {
        ...attributes,
        ...listeners,
        style: { 
          cursor: 'grab',
          userSelect: 'none',
          backgroundColor: isDragging ? '#f0f0f0' : 'transparent'
        },
        title: '드래그하여 순서 변경'
      });
    }
    return child;
  });

  return (
    <tr ref={setNodeRef} style={style}>
      {modifiedChildren}
    </tr>
  );
};

const ReactEstimateForm = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const alertShownRef = useRef<{ [key: string]: boolean }>({});

  // DnD Sensors
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // 날짜시간 포맷팅 함수
  const formatDateTime = (estimate: EstimateType) => {
    if (estimate.timestamp) {
      const date = new Date(estimate.timestamp);
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      return `${year}-${month}-${day} ${hours}:${minutes}`;
    }
    // timestamp가 없으면 estimateDate만 표시
    return estimate.estimateDate;
  };

  // 주소 포맷팅 함수
  const formatAddress = (estimate: EstimateType) => {
    if (estimate.detailedAddress && estimate.detailedAddress.trim() !== '') {
      return `${estimate.address} ${estimate.detailedAddress}`;
    }
    return estimate.address;
  };

  // 브랜드 코드와 한국어 이름 매핑
  const brandMapping = {
    homeWindow: '홈윈도우',
    LX하우시스: 'LX하우시스',
    KCC홈CC: 'KCC홈씨씨',
  };

  // 단가/고정변수 데이터 관리
  const {
    unitPrices, isUnitPricesLoaded, isRefreshingPrices, fixedVariables,
    getAllUnitPricesData, refreshUnitPricesData, loadFixedVariables,
  } = useUnitPricesData();

  // 세션/인증/알림 관리
  const {
    userId, hasNewPosts, setHasNewPosts, hasNewErrorPosts, setHasNewErrorPosts,
    latestVersion, checkSession, getUserName,
  } = useSession(navigate);

  const [jusoApiKey, setJusoApiKey] = useState<string>('');

  // 품목 데이터 관리
  const {
    availableItems, isItemsLoaded, otherItems, tongbaItems, additionalItems,
    itemPrices, getAllItemsData,
  } = useItemsData();

  // 견적 목록/모달/페이징/캐싱/검색/필터/계약/공유 관리
  const {
    showLoadModal, openLoadModal, closeLoadModal,
    paginatedEstimates, totalEstimatePages, loadingEstimates, isFetchingInBackground,
    totalEstimateElements, serverTotalElements,
    currentEstimatePage, handleEstimatePageChange,
    searchInput, handleSearchChange,
    filterStartDate, setFilterStartDate, filterEndDate, setFilterEndDate,
    filterContractStatus, setFilterContractStatus,
    filterEstimator, setFilterEstimator, availableEstimators, isAdmin,
    selectedEstimates, setSelectedEstimates, contractedEstimates,
    handleContractCheckbox, showContractModal, showUncontractModal,
    confirmContract, confirmUncontract, cancelContract, cancelUncontract,
    sharedEstimateIds, receivedEstimateIds,
    showEstimatePreviewModal, setShowEstimatePreviewModal, previewEstimateNumber,
    estimatePreviewLoading, estimatePreviewData, setEstimatePreviewData, previewEstimate,
    loadedEstimateNumber, setLoadedEstimateNumber, loadEstimatesPage,
    showShareModal, setShowShareModal, selectedDepartment, setSelectedDepartment,
    selectedUserName, setSelectedUserName, departmentUsers, setDepartmentUsers,
    currentUserId,
  } = useEstimateList();

  useEffect(() => {
    // 초기 데이터 로딩
    const loadInitialData = async () => {
      try {
        // 세션 체크
        const isAuthenticated = await checkSession();
        if (!isAuthenticated) return;

        // 사용자 정보, 단가 데이터, 품명 목록, 고정변수를 병렬로 로딩
        const [userNameResult] = await Promise.all([
          getUserName(),
          getAllUnitPricesData(),
          getAllItemsData(),
          loadFixedVariables(),
        ]);
        if (userNameResult) {
          setEstimateInfo((prev) => ({
            ...prev,
            estimator: userNameResult.name,
            estimatorPhone: userNameResult.phone,
          }));
        }
      } catch (error) {
        console.error('초기 데이터 로딩 중 오류 발생:', error);
        alert('데이터를 불러오는 중 오류가 발생했습니다.');
      }
    };

    loadInitialData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate]);

  // 카카오톡 SDK 초기화
  useEffect(() => {
    if (window.Kakao && !window.Kakao.isInitialized()) {
      window.Kakao.init('YOUR_KAKAO_APP_KEY'); // 실제 앱키로 변경 필요
    }
  }, []);

  // 기본 정보 상태
  const [estimateInfo, setEstimateInfo] = useState({
    estimateNumber: '',
    estimateDate: (() => {
      const today = new Date();
      const year = today.getFullYear();
      const month = String(today.getMonth() + 1).padStart(2, '0');
      const day = String(today.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    })(),
    estimator: '',
    estimatorPhone: '1661-4830',
    estimateType: '-', // 견적유형 추가
    /*customerCode: 'C250630-N001',*/
    customerName: '',
    phone: '',
    address: '',
    detailedAddress: '',
    constructionStatus: '시공',
    scheduledDate: '',
    estimateNotes: '',
  });

  // CRM에서 전달된 견적번호 (자동 불러오기용)
  const [pendingEstimateNumber, setPendingEstimateNumber] = useState<string | null>(null);

  // CRM에서 전달된 고객 정보 URL 파라미터 자동 입력
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const customerName = params.get('customerName');
    const customerPhone = params.get('customerPhone');
    const customerAddress = params.get('customerAddress');
    const customerAddressDetail = params.get('customerAddressDetail');
    const estimateNumber = params.get('estimateNumber');

    if (customerName || customerPhone || customerAddress) {
      setEstimateInfo(prev => ({
        ...prev,
        ...(customerName && { customerName }),
        ...(customerPhone && { phone: customerPhone }),
        ...(customerAddress && { address: customerAddress }),
        ...(customerAddressDetail && { detailedAddress: customerAddressDetail }),
      }));
    }

    // 견적번호가 있으면 자동 불러오기 대기
    if (estimateNumber) {
      setPendingEstimateNumber(estimateNumber);
    }

    // URL 파라미터 정리 (새로고침 시 중복 입력 방지)
    if (customerName || customerPhone || customerAddress || estimateNumber) {
      window.history.replaceState({}, '', location.pathname);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // 단가/품목 데이터 로드 완료 후 견적번호 자동 불러오기
  useEffect(() => {
    if (pendingEstimateNumber && isUnitPricesLoaded && isItemsLoaded) {
      loadEstimate(pendingEstimateNumber);
      setPendingEstimateNumber(null);
    }
  }, [pendingEstimateNumber, isUnitPricesLoaded, isItemsLoaded]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleLogout = () => {
    api.post('/api/logout').then(() => {
      alert('로그아웃 되었습니다. 다시 로그인 해주세요.');
      navigate('/'); // 로그인 페이지 등으로 이동
    });
  };

  // Apps Script URL for Google Sheets export
  const GOOGLE_SHEETS_URL =
    'https://script.google.com/macros/s/AKfycbzjqJbvTFnulRQMSBJ6duHgWscnpYZA-wLB0pFGGafTBp2gZQD_EfwwH6vTHsclRtao/exec';

  // Function to export data (and optionally image) to Google Sheets
  function exportToGoogleSheets(data, imageDataUrl: string | null = null) {
    const payload = imageDataUrl
      ? { products: data, image: imageDataUrl }
      : data;
    fetch(GOOGLE_SHEETS_URL, {
      method: 'POST',
      mode: 'no-cors',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    })
      .then(() => {
        // Request sent successfully
      })
      .catch((error) => console.error('[exportToGoogleSheets] Error:', error));
  }

  // Format timestamp to MM/DD/YYYY HH:MM:SS
  function formatTimestamp(date) {
    const month = (date.getMonth() + 1).toString();
    const day = date.getDate().toString();
    const year = date.getFullYear();
    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');
    const seconds = date.getSeconds().toString().padStart(2, '0');
    return `${month}/${day}/${year} ${hours}:${minutes}:${seconds}`;
  }

  function collectProductData(): Record<string, string | number>[] {
    const products: Record<string, string | number>[] = [];

    /* const customerCode = document.querySelector('table.table2 tr:nth-child(2) td:nth-child(1) input')?.value || '';*/
    const estimateNumber =
      (
        document.querySelector(
          'table.table2 tr:nth-child(2) td:nth-child(2) input'
        ) as HTMLInputElement | null
      )?.value || '';
    const estimateDate =
      (
        document.querySelector(
          'table.table2 tr:nth-child(2) td:nth-child(3) input'
        ) as HTMLInputElement | null
      )?.value || '';
    const estimator =
      (
        document.querySelector(
          'table.table2 tr:nth-child(2) td:nth-child(4) input'
        ) as HTMLInputElement | null
      )?.value || '';
    const customerName =
      (
        document.querySelector(
          'table.table tr:nth-child(2) td:nth-child(1) input'
        ) as HTMLInputElement | null
      )?.value || '';
    const phone =
      (document.querySelector('#phone') as HTMLInputElement | null)?.value ||
      '';
    const address =
      (
        document.querySelector(
          'table.table tr:nth-child(2) td:nth-child(3) input[placeholder="주소검색"]'
        ) as HTMLInputElement | null
      )?.value || '';
    const detailedAddress =
      (
        document.querySelector(
          'table.table tr:nth-child(2) td:nth-child(3) input[placeholder="상세주소 입력"]'
        ) as HTMLInputElement | null
      )?.value || '';
    const constructionStatus =
      (
        document.querySelector(
          'table.table tr:nth-child(2) td:nth-child(4) select'
        ) as HTMLSelectElement | null
      )?.value || '';
    const scheduledDate =
      (
        document.querySelector(
          'table.table tr:nth-child(2) td:nth-child(5) input'
        ) as HTMLInputElement | null
      )?.value || '';
    const estimateNotes =
      (
        document.querySelector(
          '.flex-column:last-child textarea'
        ) as HTMLTextAreaElement | null
      )?.value || '';
    const timestamp = formatTimestamp(new Date());

    // 샷시 데이터 수집 (브랜드별로 분리)
    document
      .querySelectorAll('#shasiTable tr:not(:first-child)')
      .forEach((row, rowIndex) => {
        const tr = row as HTMLTableRowElement;
        const installLocation =
          (tr.cells[1].querySelector('input') as HTMLInputElement | null)
            ?.value || '';
        const item =
          (tr.cells[2].querySelector('select') as HTMLSelectElement | null)
            ?.value || '';
        const width =
          parseFloat(
            (
              tr.querySelector(
                'input[placeholder="W"]'
              ) as HTMLInputElement | null
            )?.value || ''
          ) || '';
        const height =
          parseFloat(
            (
              tr.querySelector(
                'input[placeholder="H"]'
              ) as HTMLInputElement | null
            )?.value || ''
          ) || '';
        const quantity =
          parseInt(
            (tr.cells[4]?.querySelector('input') as HTMLInputElement | null)
              ?.value || ''
          ) || '';
        const note =
          (tr.cells[8].querySelector('input') as HTMLInputElement | null)
            ?.value || '';
        
        // note에 행 인덱스 추가
        const noteWithIndex = note ? `${note}__IDX${rowIndex}` : `__IDX${rowIndex}`;

        if (item && item !== '-') {
          // 브랜드별 가격 배열
          const brandPrices = [
            {
              brand: 'HW',
              price:
                parseInt(
                  (
                    tr.cells[5]?.querySelector(
                      'input'
                    ) as HTMLInputElement | null
                  )?.value.replace(/,/g, '') || '0'
                ) || 0,
            },
            {
              brand: 'LX',
              price:
                parseInt(
                  (
                    tr.cells[6]?.querySelector(
                      'input'
                    ) as HTMLInputElement | null
                  )?.value.replace(/,/g, '') || '0'
                ) || 0,
            },
            {
              brand: 'KC',
              price:
                parseInt(
                  (
                    tr.cells[7]?.querySelector(
                      'input'
                    ) as HTMLInputElement | null
                  )?.value.replace(/,/g, '') || '0'
                ) || 0,
            },
          ];

          // 각 브랜드별로 개별 레코드 생성
          brandPrices.forEach((brandData) => {
            if (brandData.price > 0) {
              // 가격이 있는 브랜드만 저장
              products.push({
                /*CustomerCode: customerCode, */ EstimateNumber: estimateNumber,
                EstimateDate: estimateDate,
                Estimator: estimator,
                CustomerName: customerName,
                Phone: phone,
                Address: address,
                DetailedAddress: detailedAddress,
                ConstructionStatus: constructionStatus,
                ScheduledDate: scheduledDate,
                EstimateNotes: estimateNotes,
                Timestamp: timestamp,
                ProductType: '샷시',
                InstallLocation: installLocation,
                Item: item,
                Width: width,
                Height: height,
                Quantity: quantity,
                Brand: brandData.brand,
                Price: brandData.price,
                PriceWithTax: Math.round(brandData.price * 1.1), // 부가세 포함 가격 추가
                Note: noteWithIndex,
              });
            }
          });
        }
      });

    // 중문 & 기타 데이터 수집
    document
      .querySelectorAll('#otherTable tr:not(:first-child)')
      .forEach((row) => {
        const tr = row as HTMLTableRowElement;
        const installLocation =
          (tr.cells[1].querySelector('input') as HTMLInputElement | null)
            ?.value || '';
        const item =
          (tr.cells[2].querySelector('select') as HTMLSelectElement | null)
            ?.value || '';
        const width =
          parseFloat(
            (
              tr.querySelector(
                'input[placeholder="W"]'
              ) as HTMLInputElement | null
            )?.value || ''
          ) || '';
        const height =
          parseFloat(
            (
              tr.querySelector(
                'input[placeholder="H"]'
              ) as HTMLInputElement | null
            )?.value || ''
          ) || '';
        const color =
          (tr.cells[4].querySelector('input') as HTMLInputElement | null)
            ?.value || '';
        const quantity =
          parseInt(
            (tr.cells[5]?.querySelector('input') as HTMLInputElement | null)
              ?.value || ''
          ) || '';
        const price =
          parseInt(
            (
              tr.cells[6].querySelector('input') as HTMLInputElement | null
            )?.value.replace(/,/g, '') || '0'
          ) || '';
        const note =
          (tr.cells[7].querySelector('input') as HTMLInputElement | null)
            ?.value || '';

        if (item && item !== '-') {
          products.push({
            /*CustomerCode: customerCode, */ EstimateNumber: estimateNumber,
            EstimateDate: estimateDate,
            Estimator: estimator,
            CustomerName: customerName,
            Phone: phone,
            Address: address,
            DetailedAddress: detailedAddress,
            ConstructionStatus: constructionStatus,
            ScheduledDate: scheduledDate,
            EstimateNotes: estimateNotes,
            Timestamp: timestamp,
            ProductType: '중문 & 기타',
            InstallLocation: installLocation,
            Item: item,
            Width: width,
            Height: height,
            Color: color,
            Quantity: quantity,
            Price: price,
            PriceWithTax: Math.round(Number(price) * 1.1), // 부가세 포함 가격 추가
            Note: note
          });
        }
      });

    // 통바 데이터 수집
    document
      .querySelectorAll('#tongbaTable tr:not(:first-child)')
      .forEach((row) => {
        const tr = row as HTMLTableRowElement;
        const item =
          (tr.cells[1].querySelector('select') as HTMLSelectElement | null)
            ?.value || '';
        const width =
          parseFloat(
            (tr.cells[2].querySelector('input') as HTMLInputElement | null)
              ?.value || ''
          ) || '';
        const quantity =
          parseInt(
            (tr.cells[3].querySelector('input') as HTMLInputElement | null)
              ?.value || ''
          ) || '';
        const price =
          parseInt(
            (
              tr.cells[4].querySelector('input') as HTMLInputElement | null
            )?.value.replace(/,/g, '') || '0'
          ) || '';

        if (item && item !== '-') {
          products.push({
            /* CustomerCode: customerCode, */ EstimateNumber: estimateNumber,
            EstimateDate: estimateDate,
            Estimator: estimator,
            CustomerName: customerName,
            Phone: phone,
            Address: address,
            DetailedAddress: detailedAddress,
            ConstructionStatus: constructionStatus,
            ScheduledDate: scheduledDate,
            EstimateNotes: estimateNotes,
            Timestamp: timestamp,
            ProductType: '통바',
            Item: item,
            Width: width, // Spec을 Width로 변경
            Quantity: quantity,
            Price: price,
            PriceWithTax: Math.round(Number(price) * 1.1), // 부가세 포함 가격 추가
          });
        }
      });

    // 추가사항 데이터 수집
    document
      .querySelectorAll('#additionalTable tr:not(:first-child)')
      .forEach((row) => {
        const tr = row as HTMLTableRowElement;
        const item =
          (tr.cells[1].querySelector('select') as HTMLSelectElement | null)
            ?.value || '';
        const quantity =
          parseInt(
            (tr.cells[2].querySelector('input') as HTMLInputElement | null)
              ?.value || ''
          ) || '';
        const price =
          parseInt(
            (
              tr.cells[3].querySelector('input') as HTMLInputElement | null
            )?.value.replace(/,/g, '') || '0'
          ) || '';

        if (item && item !== '-') {
          // 직접입력 항목 구분
          let productType = '추가사항';
          let itemName = item;
          let customItemInput;

          if (item === '직접입력') {
            // 직접입력 필드에서 실제 내용 가져오기
            customItemInput = tr.cells[1].querySelector(
              'input[type="text"]'
            ) as HTMLInputElement | null;
            itemName = '직접입력';
          } else if (item === '직접입력(고객 비공개용)') {
            productType = '추가사항';
            itemName = '직접입력(고객 비공개용)';
          }

          products.push({
            /*CustomerCode: customerCode, */ EstimateNumber: estimateNumber,
            EstimateDate: estimateDate,
            Estimator: estimator,
            CustomerName: customerName,
            Phone: phone,
            Address: address,
            DetailedAddress: detailedAddress,
            ConstructionStatus: constructionStatus,
            ScheduledDate: scheduledDate,
            EstimateNotes: estimateNotes,
            Timestamp: timestamp,
            ProductType: productType,
            Item: itemName,
            Quantity: quantity,
            Price: price,
            PriceWithTax: Math.round(Number(price) * 1.1), // 부가세 포함 가격 추가
            directInput: customItemInput ? customItemInput.value : '', // 직접입력 내용 저장
            customItem: customItemInput ? customItemInput.value : '', // 직접입력 내용 저장
          });
        }
      });

    // 할인 내역 데이터 수집 (브랜드별로 분리)
    if (
      (discountData.homeWindow !== 0 ||
        discountData.LX하우시스 !== 0 ||
        discountData.KCC홈CC !== 0) &&
      (Number(discountRates.homeWindow) > 0 ||
        Number(discountRates.LX하우시스) > 0 ||
        Number(discountRates.KCC홈CC) > 0)
    ) {
      const discountBrands = [
        {
          brand: 'HW',
          amount: discountData.homeWindow,
          rate: discountRates.homeWindow,
        },
        {
          brand: 'LX',
          amount: discountData.LX하우시스,
          rate: discountRates.LX하우시스,
        },
        {
          brand: 'KC',
          amount: discountData.KCC홈CC,
          rate: discountRates.KCC홈CC,
        },
      ];
      discountBrands.forEach((brandDiscount) => {
        if (brandDiscount.amount !== 0) {
          const discountAmountWithTax = brandDiscount.amount;
          const discountAmountBase = Math.round(discountAmountWithTax / 1.1);
          products.push({
            /*CustomerCode: customerCode, */ EstimateNumber: estimateNumber,
            EstimateDate: estimateDate,
            Estimator: estimator,
            CustomerName: customerName,
            Phone: phone,
            Address: address,
            DetailedAddress: detailedAddress,
            ConstructionStatus: constructionStatus,
            ScheduledDate: scheduledDate,
            EstimateNotes: estimateNotes,
            Timestamp: timestamp,
            ProductType: '할인내역',
            Item: `${brandDiscount.rate}% 할인`,
            Quantity: 1,
            Brand: brandDiscount.brand,
            Price: discountAmountBase,
            PriceWithTax: discountAmountWithTax,
          });
        }
      });
    }

    return products;
  }

  // 체크박스 상태
  const [checkboxes, setCheckboxes] = useState<{
    homeWindow: boolean;
    lxHausys: boolean;
    kccHomeCC: boolean;
  }>({
    homeWindow: true,
    lxHausys: true,
    kccHomeCC: true,
  });

  // 할인 상태
  const [discount, setDiscount] = useState(0);

  // 할인 금액 상태
  const [discountAmounts, setDiscountAmounts] = useState({
    homeWindow: 0,
    lxHausys: 0,
    kccHomeCC: 0,
  });

  // 최종 견적 합계 상태
  const [totals, setTotals] = useState({
    homeWindow: { sum: 0, vat: 0, final: 0 },
    lxHausys: { sum: 0, vat: 0, final: 0 },
    kccHomeCC: { sum: 0, vat: 0, final: 0 },
  });

  // 가격 계산 중복 실행 방지 플래그
  const isCalculatingRef = useRef<boolean>(false);

  // 캡처 중 가격 재계산 방지 플래그 (캡처 시점의 값 보존)
  const isCapturingRef = useRef<boolean>(false);

  // 견적 불러오기 중 가격 재계산 방지 플래그 (저장된 가격 보존)
  const isLoadingEstimateRef = useRef<boolean>(false);

  // 테이블 행 생성 팩토리
  const createEmptyShasiRow = useCallback((): Omit<ShasiRow, 'id'> => ({
    installLocation: '', item: '-', width: '', height: '', quantity: '1',
    homeWindowPrice: 0, lxHausysPrice: 0, kccHomeCCPrice: 0, note: '',
  }), []);
  const createEmptyOtherRow = useCallback((): Omit<OtherRow, 'id'> => ({
    installLocation: '', item: '-', width: '', height: '', color: '', quantity: '', price: 0, note: '',
  }), []);
  const createEmptyTongbaRow = useCallback((): Omit<TongbaRow, 'id'> => ({
    item: '-', spec: '', quantity: '', price: 0,
  }), []);
  const createEmptyAdditionalRow = useCallback((): Omit<AdditionalRow, 'id'> => ({
    item: '-', quantity: '', price: 0, customItem: '', unitPrice: 0,
  }), []);

  // 샷시 데이터
  const {
    data: shasiData, setData: setShasiData,
    addRow: addShasiRow, removeRow: removeShasiRow, handleDragEnd: handleShasiDragEnd,
  } = useTableRows<ShasiRow>(
    [{ id: 1, installLocation: '', item: '-', width: '', height: '', quantity: '1', homeWindowPrice: 0, lxHausysPrice: 0, kccHomeCCPrice: 0, note: '' }],
    createEmptyShasiRow
  );

  // 중문 & 기타 데이터
  const {
    data: otherData, setData: setOtherData,
    addRow: addOtherRow, removeRow: removeOtherRow, handleDragEnd: handleOtherDragEnd,
  } = useTableRows<OtherRow>(
    [{ id: 1, installLocation: '', item: '-', width: '', height: '', color: '', quantity: '', price: 0, note: '' }],
    createEmptyOtherRow
  );

  // 통바 데이터
  const {
    data: tongbaData, setData: setTongbaData,
    addRow: addTongbaRow, removeRow: removeTongbaRow, handleDragEnd: handleTongbaDragEnd,
  } = useTableRows<TongbaRow>(
    [{ id: 1, item: '-', spec: '', quantity: '', price: 0 }],
    createEmptyTongbaRow
  );

  // 추가사항 데이터
  const {
    data: additionalData, setData: setAdditionalData,
    addRow: addAdditionalRow, removeRow: removeAdditionalRow, handleDragEnd: handleAdditionalDragEnd,
  } = useTableRows<AdditionalRow>(
    [{ id: 1, item: '-', quantity: '', price: 0, customItem: '', unitPrice: 0 }],
    createEmptyAdditionalRow
  );

  // 할인 데이터 state 추가
  const [discountData, setDiscountData] = useState({
    homeWindow: 0,
    LX하우시스: 0,
    KCC홈CC: 0,
  });

  // 사용자 인증 상태
  // const [currentUser, setCurrentUser] = useState(null);

  /* // useEffect로 사용자 인증 확인
  useEffect(() => {
    const user = JSON.parse(localStorage.getItem('currentUser'));
    if (!user) {
	  alert('세션이 만료되었습니다. 다시 로그인 해주세요.');
      window.location.href = 'http://localhost:8000';
      return;
    }
    setCurrentUser(user);
    setEstimateInfo(prev => ({
      ...prev,
      estimator: user.name
    }));

    // 1시간 후 자동 로그아웃
    const timeout = setTimeout(() => {
      localStorage.removeItem('currentUser');
	  alert('세션이 만료되었습니다. 다시 로그인 해주세요.');
      window.location.href = 'http://localhost:8000';
    }, 3600000);

    return () => clearTimeout(timeout);
  }, []);*/

  // 전화번호 포맷팅
  const formatPhoneNumber = (value) => {
    const numbers = value.replace(/[^\d]/g, '');
    if (numbers.length <= 3) return numbers;
    if (numbers.length <= 7)
      return `${numbers.slice(0, 3)}-${numbers.slice(3)}`;
    return `${numbers.slice(0, 3)}-${numbers.slice(3, 7)}-${numbers.slice(
      7,
      11
    )}`;
  };

  // 숫자 포맷팅
  const formatNumber = (number) => {
    return number.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  };

  // 할인율 상태 (브랜드별)
  const [discountRates, setDiscountRates] = useState({
    homeWindow: '',
    LX하우시스: '',
    KCC홈CC: '',
  });

  // 브랜드별 창호 스펙 등급 선택 상태
  const [selectedGrades, setSelectedGrades] = useState({
    LX: '프리미엄' as '프리미엄' | '스탠다드' | '베이직',
    홈윈도우: '프리미엄' as '프리미엄' | '스탠다드' | '베이직',
  });

  // 브랜드별 창호 스펙 행 hover 상태

  // Enter 키 처리 함수
  const handleEnterKey = (e: React.KeyboardEvent<HTMLInputElement | HTMLSelectElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      
      // 현재 폼 내의 모든 input과 select 요소 가져오기
      const form = e.currentTarget.closest('form') || document;
      const inputs = Array.from(form.querySelectorAll('input:not([disabled]), select:not([disabled])'));
      
      // 현재 요소의 인덱스 찾기
      const currentIndex = inputs.indexOf(e.currentTarget as HTMLInputElement | HTMLSelectElement);
      
      // 다음 활성화된 입력 요소로 포커스 이동
      if (currentIndex !== -1 && currentIndex < inputs.length - 1) {
        const nextInput = inputs[currentIndex + 1] as HTMLInputElement | HTMLSelectElement;
        nextInput.focus();
        
        // input이면 텍스트 선택
        if (nextInput.tagName === 'INPUT') {
          (nextInput as HTMLInputElement).select();
        }
      }
    }
  };

  // 샷시 데이터 업데이트
  const updateShasiData = (id, field, value) => {
    setShasiData((prevData) => {
      const newData = prevData.map((row) => {
        if (row.id === id) {
          const updatedRow = { ...row, [field]: value };
          
          // 너비/높이/수량은 숫자만 허용
          if (field === 'width' || field === 'height' || field === 'quantity') {
            const numericValue = value.replace(/[^0-9]/g, '');
            updatedRow[field] = numericValue;
          } else {
            updatedRow[field] = value;
          }
          
          // 품명 선택 시 수량은 항상 1로 설정 + 기존 너비/높이 재검증
          if (field === 'item') {
            updatedRow.quantity = '1';

            const newItem = value;
            const isBalcony = newItem && newItem.includes('발코니');
            const isGeneral = newItem && newItem.includes('일반');
            const isBalcony2W = newItem === '발코니_이중창_2W';
            const isFix = newItem && newItem.includes('FIX');
            const width = parseFloat(updatedRow.width) || 0;
            const height = parseFloat(updatedRow.height) || 0;

            // 너비 재검증
            if (width > 0) {
              if (width > 5400) {
                updatedRow.width = '5400';
              } else if (isBalcony2W && width < 320) {
                updatedRow.width = '320';
              } else if (!isBalcony2W && isBalcony && width < 800) {
                updatedRow.width = '800';
              } else if (isGeneral && width < 600) {
                updatedRow.width = '600';
              }
            }

            // 높이 재검증
            if (height > 0) {
              if (height > 2510) {
                updatedRow.height = '2510';
              } else if (isBalcony2W && height < 320) {
                updatedRow.height = '320';
              } else if (!isBalcony2W && isBalcony && height < 500) {
                updatedRow.height = '500';
              } else if (isGeneral && height < 400) {
                updatedRow.height = '400';
              }
            }

            // FIX 면적 체크
            if (isFix) {
              const w = parseFloat(updatedRow.width) || 0;
              const h = parseFloat(updatedRow.height) || 0;
              if (w > 0 && h > 0 && Math.ceil((w * h) / 90000) > 100) {
                updatedRow.width = '';
                updatedRow.height = '';
              }
            }
          }
          
          return updatedRow;
        }
        return row;
      });
      return newData;
    });
  };

  // 샷시 사이즈 검증 (onBlur에서 사용) — 규격 위반 시 alert 없이 최소/최대값으로 자동 보정
  const validateShasiSize = (id, field, value) => {
    setShasiData((prevData) => {
      const newData = prevData.map((row) => {
        if (row.id === id) {
          const updatedRow = { ...row };
          const isBalcony = row.item && row.item.includes('발코니');
          const isGeneral = row.item && row.item.includes('일반');
          const isBalcony2W = row.item === '발코니_이중창_2W';
          const isFix = row.item && row.item.includes('FIX');

          // FIX 품목의 자평 계산 (너비와 높이가 모두 있을 때)
          const checkFixArea = (width: string, height: string) => {
            const w = parseFloat(width) || 0;
            const h = parseFloat(height) || 0;
            if (w > 0 && h > 0) {
              const area = Math.ceil((w * h) / 90000);
              if (area > 100) {
                return { exceed: true, area };
              }
            }
            return { exceed: false, area: 0 };
          };

          if (field === 'width') {
            const numValue = parseFloat(value);
            let needsUpdate = false;
            let newValue = value;

            if (isFix && !isNaN(numValue)) {
              if (numValue > 5400) {
                newValue = '5400';
                needsUpdate = true;
              } else if (row.height) {
                const areaCheck = checkFixArea(value, row.height);
                if (areaCheck.exceed) {
                  newValue = '';
                  updatedRow.height = '';
                  needsUpdate = true;
                }
              }
            }
            else if (isBalcony2W && !isNaN(numValue) && numValue < 320 && numValue > 0) {
              newValue = '320';
              needsUpdate = true;
            }
            else if (!isFix && !isNaN(numValue) && numValue > 5400) {
              newValue = '5400';
              needsUpdate = true;
            }
            else if (!isBalcony2W && isBalcony && !isNaN(numValue) && numValue > 0 && numValue < 800) {
              newValue = '800';
              needsUpdate = true;
            }
            else if (isGeneral && !isNaN(numValue) && numValue > 0 && numValue < 600) {
              newValue = '600';
              needsUpdate = true;
            }

            if (needsUpdate) {
              updatedRow.width = newValue;
            }
          }
          else if (field === 'height') {
            const numValue = parseFloat(value);
            let needsUpdate = false;
            let newValue = value;

            if (isFix && !isNaN(numValue)) {
              if (numValue > 2510) {
                newValue = '2510';
                needsUpdate = true;
              } else if (row.width) {
                const areaCheck = checkFixArea(row.width, value);
                if (areaCheck.exceed) {
                  newValue = '';
                  updatedRow.width = '';
                  needsUpdate = true;
                }
              }
            }
            else if (isBalcony2W && !isNaN(numValue) && numValue < 320 && numValue > 0) {
              newValue = '320';
              needsUpdate = true;
            }
            else if (!isFix && !isNaN(numValue) && numValue > 2510) {
              newValue = '2510';
              needsUpdate = true;
            }
            else if (!isBalcony2W && isBalcony && !isNaN(numValue) && numValue > 0 && numValue < 500) {
              newValue = '500';
              needsUpdate = true;
            }
            else if (isGeneral && !isNaN(numValue) && numValue > 0 && numValue < 400) {
              newValue = '400';
              needsUpdate = true;
            }

            if (needsUpdate) {
              updatedRow.height = newValue;
            }
          }

          return updatedRow;
        }
        return row;
      });
      return newData;
    });
  };

  // 샷시 가격 재계산 함수 (견적 불러오기에서 직접 호출용)
  const recalculateShasiPrices = (
    inputShasiData: ShasiRow[],
    inputAdditionalData: AdditionalRow[],
    inputDiscountRates: DiscountRates,
    inputConstructionStatus: string,
    inputSelectedGrades?: SelectedGrades
  ): ShasiRow[] => {
    if (!isUnitPricesLoaded || Object.keys(unitPrices).length === 0) {
      return inputShasiData;
    }
    return calculateAllShasiPrices(inputShasiData, {
      unitPrices,
      fixedVariables,
      discountRates: inputDiscountRates,
      constructionStatus: inputConstructionStatus,
      selectedGrades: inputSelectedGrades || selectedGrades,
      additionalData: inputAdditionalData,
    });
  };

  useEffect(() => {
    // 캡처 중에는 가격 재계산 방지 (캡처 시점의 값 보존)
    if (isCapturingRef.current) {
      return;
    }

    // 견적 불러오기 중에는 가격 재계산 방지 (저장된 가격 보존)
    if (isLoadingEstimateRef.current) {
      return;
    }

    // unitPrices가 로딩되지 않았으면 계산하지 않음
    if (!isUnitPricesLoaded || Object.keys(unitPrices).length === 0) {
      return;
    }

    const newShasiData = calculateAllShasiPrices(shasiData, {
      unitPrices,
      fixedVariables,
      discountRates,
      constructionStatus: estimateInfo.constructionStatus,
      selectedGrades,
      additionalData,
    });

    // 할인 표시 금액 계산
    const newDiscountData = calculateDiscountData(shasiData, {
      unitPrices,
      fixedVariables,
      discountRates,
      selectedGrades,
    });
    setDiscountData(newDiscountData);

    const isDataChanged = JSON.stringify(newShasiData) !== JSON.stringify(shasiData);

    if (isDataChanged) {
      setShasiData(newShasiData);
      calculateEstimate(newShasiData, otherData, tongbaData, additionalData, discountRates);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    // shasiData를 dependency에 넣으면 무한 루프 발생!
    // 대신 가격 계산에 영향을 주는 값들만 감지
    JSON.stringify(shasiData.map(r => ({ id: r.id, item: r.item, width: r.width, height: r.height, quantity: r.quantity, price: r.homeWindowPrice }))),
    JSON.stringify(additionalData.map(r => ({ id: r.id, item: r.item, price: r.price }))),
    estimateInfo.constructionStatus,
    unitPrices,
    isUnitPricesLoaded,
    discountRates,
    fixedVariables,
    selectedGrades,
  ]);

  // 샷시 마지막 행 복사
  const copyShasiRow = () => {
    if (shasiData.length === 0) return;
    
    const lastRow = shasiData[shasiData.length - 1];
    const newId = Math.max(...shasiData.map((row) => row.id)) + 1;
    
    setShasiData((prev) => [
      ...prev,
      {
        ...lastRow,
        id: newId,
      },
    ]);
  };

  // 중문 & 기타 데이터 업데이트
  const updateOtherData = (id, field, value) => {
    setOtherData((prev) =>
      prev.map((row) => {
        if (row.id === id) {
          const updatedRow = { ...row, [field]: value };

          // 너비/높이는 숫자만 허용
          if (field === 'width' || field === 'height') {
            const numericValue = value.replace(/[^0-9]/g, '');
            updatedRow[field] = numericValue;
          }

          // 품명 선택 시 수량을 자동으로 1로 설정 ('-' 제외)
          if (field === 'item' && value !== '-' && value !== '') {
            if (!row.quantity || row.quantity === '' || row.quantity === '0') {
              updatedRow.quantity = '1';
            }
          }

          // DB에서 가져온 가격 정보 사용
          const item = updatedRow.item;
          const quantity = parseFloat(updatedRow.quantity) || 0;
          const installationType = estimateInfo.constructionStatus;

          let basePrice = 0;

          // itemPrices 로드 확인
          if (!itemPrices || Object.keys(itemPrices).length === 0) {
            console.warn('⚠️ itemPrices 아직 로드되지 않음 - 중문&기타 가격 계산 건너뜀');
            updatedRow.price = 0;
            return updatedRow;
          }

          // DB에서 가격 정보 조회 (공통 타입도 확인)
          if (itemPrices['중문&기타'] && itemPrices['중문&기타'][item]) {
            if (itemPrices['중문&기타'][item][installationType]) {
              basePrice = itemPrices['중문&기타'][item][installationType];
            } else if (itemPrices['중문&기타'][item]['공통']) {
              basePrice = itemPrices['중문&기타'][item]['공통'];
            }
          }

          updatedRow.price = Math.round((basePrice * quantity) / 1000) * 1000;

          return updatedRow;
        }
        return row;
      })
    );
  };

  // 중문&기타 사이즈 검증 (onBlur에서 사용)
  const validateOtherSize = (id, field, value) => {
    setOtherData((prev) =>
      prev.map((row) => {
        if (row.id === id) {
          const updatedRow = { ...row };
          
          if (field === 'width') {
            const numValue = parseFloat(value);
            if (!isNaN(numValue) && numValue > 5400) {
              const alertKey = `other-${id}-width-${value}`;
              if (!alertShownRef.current[alertKey]) {
                alertShownRef.current[alertKey] = true;
                alert('제작 가능 규격을 초과하였습니다.\n지원팀에 문의해 주세요.');
                setTimeout(() => {
                  delete alertShownRef.current[alertKey];
                }, 500);
              }
              updatedRow.width = '5400';
            }
          } else if (field === 'height') {
            const numValue = parseFloat(value);
            if (!isNaN(numValue) && numValue > 2510) {
              const alertKey = `other-${id}-height-${value}`;
              if (!alertShownRef.current[alertKey]) {
                alertShownRef.current[alertKey] = true;
                alert('제작 가능 규격을 초과하였습니다.\n지원팀에 문의해 주세요.');
                setTimeout(() => {
                  delete alertShownRef.current[alertKey];
                }, 500);
              }
              updatedRow.height = '2510';
            }
          }
          
          return updatedRow;
        }
        return row;
      })
    );
  };

  // 통바 데이터 업데이트
  const updateTongbaData = (id, field, value) => {
    setTongbaData((prev) =>
      prev.map((row) => {
        if (row.id === id) {
          const updatedRow = { ...row, [field]: value };

          // 규격(spec)은 숫자만 허용
          if (field === 'spec') {
            const numericValue = value.replace(/[^0-9]/g, '');
            updatedRow[field] = numericValue;
          }

          // 품명 선택 시 수량을 자동으로 1로 설정 ('-' 제외)
          if (field === 'item' && value !== '-' && value !== '') {
            if (!row.quantity || row.quantity === '' || row.quantity === '0') {
              updatedRow.quantity = '1';
            }
          }

          // DB에서 가져온 가격 정보 사용
          const item = updatedRow.item;
          const specValue = parseFloat(updatedRow.spec) || 0; // UI에서는 여전히 spec 필드명 사용
          const quantity = parseFloat(updatedRow.quantity) || 1;
          const installationType = estimateInfo.constructionStatus;

          if (item !== '-' && specValue > 0) {
            let basePrice = 0;

            // itemPrices 로드 확인
            if (!itemPrices || Object.keys(itemPrices).length === 0) {
              console.warn('⚠️ itemPrices 아직 로드되지 않음 - 통바 가격 계산 건너뜀');
              updatedRow.price = 0;
            } else {
              // DB에서 가격 정보 조회 (공통 타입도 확인)
              if (itemPrices['통바'] && itemPrices['통바'][item]) {
                if (itemPrices['통바'][item][installationType]) {
                  basePrice = itemPrices['통바'][item][installationType];
                } else if (itemPrices['통바'][item]['공통']) {
                  basePrice = itemPrices['통바'][item]['공통'];
                }
              }

              const multiplier = Math.ceil(specValue / 1000);
              updatedRow.price = basePrice * quantity * multiplier;
            }
          } else {
            updatedRow.price = 0;
          }

          return updatedRow;
        }
        return row;
      })
    );
  };

  // 추가사항 데이터 업데이트
  const updateAdditionalData = (id, field, value) => {
    setAdditionalData((prev) =>
      prev.map((row) => {
        if (row.id === id) {
          let updatedRow = { ...row, [field]: value };

          // 품명 변경 시 처리
          if (field === 'item' && row.item !== value) {
            const isDirectInput = value === '직접입력' || value === '직접입력(고객 비공개용)' || value === '비공개 금액_선택' || value === '비공개 금액_주택' || value === '비공개 금액_빌라';

            // 직접입력/비공개용/비공개 금액 항목으로 변경 시 수량을 1로 초기화
            if (isDirectInput) {
              updatedRow.quantity = '1';
            }

            // 일반 항목으로 변경 시 수량을 1로 설정 ('-' 제외)
            if (value !== '-' && value !== '' && !isDirectInput) {
              updatedRow.quantity = '1';
            }
          }

          // 원본 로직 적용
          const item = updatedRow.item;
          const quantity = parseFloat(updatedRow.quantity) || 1;
          const isDirectInput = item === '직접입력' || item === '직접입력(고객 비공개용)' || item === '비공개 금액_선택' || item === '비공개 금액_주택' || item === '비공개 금액_빌라';

          // 비공개 금액_주택/빌라 고정 가격 설정
          if (item === '비공개 금액_주택') {
            updatedRow.price = 600000;
            updatedRow.unitPrice = 600000;
          } else if (item === '비공개 금액_빌라') {
            updatedRow.price = 300000;
            updatedRow.unitPrice = 300000;
          }

          // 직접입력 항목 처리
          if (isDirectInput) {
            if (field === 'item') {
              // 직접입력 선택 시 초기화
              updatedRow.customItem = '';
              // 비공개 금액_주택/빌라는 고정 가격 유지
              if (value !== '비공개 금액_주택' && value !== '비공개 금액_빌라') {
                updatedRow.price = 0;
                updatedRow.unitPrice = 0;
              }
              updatedRow.priceInput = undefined; // priceInput 초기화하여 0 표시되도록
            } else if (field === 'price') {
              // 가격 변경 시 단가로 저장 (UI에 표시되는 값)
              updatedRow.price = value;
              updatedRow.unitPrice = value;
            } else if (field === 'quantity') {
              // 수량 변경 시 price(단가)는 유지
              // 아무것도 하지 않음
            } else if (field === 'customItem') {
              // customItem 필드 업데이트
              updatedRow.customItem = value;
            }
          } else {
            // 일반 항목 처리
            if (field === 'customItem') {
              updatedRow.customItem = value;
            } else if (item !== '-') {
              const installationType = estimateInfo.constructionStatus;
              let unitPrice = 0;

              // itemPrices 로드 확인
              if (!itemPrices || Object.keys(itemPrices).length === 0) {
                console.warn('⚠️ itemPrices 아직 로드되지 않음 - 추가사항 가격 계산 건너뜀');
                updatedRow.price = 0;
              } else {
                // DB에서 가격 정보 조회 (공통 타입도 확인)
                if (itemPrices['추가사항'] && itemPrices['추가사항'][item]) {
                  if (itemPrices['추가사항'][item][installationType]) {
                    unitPrice = itemPrices['추가사항'][item][installationType];
                  } else if (itemPrices['추가사항'][item]['공통']) {
                    unitPrice = itemPrices['추가사항'][item]['공통'];
                  }
                }

                updatedRow.price = unitPrice * quantity;
              }
            }
          }

          return updatedRow;
        }
        return row;
      })
    );
  };

  // 할인율 변경으로 인한 샷시 재계산 트리거 플래그
  const isDiscountTriggeredRecalcRef = useRef<boolean>(false);

  // 할인 금액 계산 함수 (브랜드별 할인율 적용)
  // 주의: 실제 할인 금액 계산은 useEffect(샷시 가격 재계산)에서 처리됨
  // 이 함수는 샷시 데이터 재계산 트리거 전 플래그 해제 용도로만 사용
  const calculateDiscounts = useCallback(() => {
    // 재귀 호출 방지 (할인율 변경 → setShasiData → calculateDiscounts 재호출 방지)
    if (isDiscountTriggeredRecalcRef.current) {
      isDiscountTriggeredRecalcRef.current = false; // 플래그 해제
      return;
    }
    // 할인 금액 계산은 useEffect에서 각 행 단위로 처리됨

    // [주석 처리] 기존 calculateDiscounts 로직 (총합 기준 할인 계산)
    // 기존에는 각 브랜드의 VAT 포함 가격 합계를 기준으로 할인율을 적용했음
    // const homeWindowDiscountBaseWithVAT = calculateDiscountBase(shasiData, 'homeWindowPrice', homeWindowMarkupRate);
    // const lxHausysDiscountBaseWithVAT = calculateDiscountBase(shasiData, 'lxHausysPrice', lxHausysMarkupRate);
    // const kccHomeCCDiscountBaseWithVAT = calculateDiscountBase(shasiData, 'kccHomeCCPrice', kccHomeCCMarkupRate);
    //
    // setDiscountData({
    //   homeWindow: -Math.round(homeWindowDiscountBaseWithVAT * (Number(discountRates.homeWindow) || 0) / 100 / 1000) * 1000,
    //   LX하우시스: -Math.round(lxHausysDiscountBaseWithVAT * (Number(discountRates['LX하우시스']) || 0) / 100 / 1000) * 1000,
    //   KCC홈CC: -Math.round(kccHomeCCDiscountBaseWithVAT * (Number(discountRates['KCC홈CC']) || 0) / 100 / 1000) * 1000,
    // });
  }, []);

  // 할인율 입력 변경 핸들러
  // 할인율 계산을 위한 debounce 타이머 ref
  const discountRateTimerRef = useRef<NodeJS.Timeout | null>(null);

  const handleDiscountRateChange = (brand, value) => {
    // 빈 값 처리
    if (value === '' || value === null || value === undefined) {
      setDiscountRates((prev) => ({ ...prev, [brand]: '' }));

      // 이전 타이머 취소
      if (discountRateTimerRef.current) {
        clearTimeout(discountRateTimerRef.current);
      }

      // 빈 값이면 즉시 할인 금액 재계산
      calculateDiscounts();
      // 할인율 변경 후 샷시 가격 재계산 트리거 (비공개 금액 분배 업데이트)
      isDiscountTriggeredRecalcRef.current = true;
      setShasiData(prev => [...prev]);
      return;
    }

    // 숫자 파싱 (앞의 0 제거됨)
    let rate = parseInt(value, 10);

    // NaN 체크
    if (isNaN(rate)) {
      rate = 0;
    }

    // fixed_variables에서 최대 할인율 가져오기
    let maxRate = 99;
    if (brand === 'homeWindow') {
      maxRate = fixedVariables.max_discount_homewindow || 40;
    } else if (brand === 'LX하우시스') {
      maxRate = fixedVariables.max_discount_lxhausys || 40;
    } else if (brand === 'KCC홈CC') {
      maxRate = fixedVariables.max_discount_kcchomecc || 15;
    }

    if (rate < 0) rate = 0;
    if (rate > maxRate) {
      alert(
        brand === 'homeWindow'
          ? `홈윈도우는 최대 ${maxRate}%까지만 입력할 수 있습니다.`
          : brand === 'LX하우시스'
          ? `LX하우시스는 최대 ${maxRate}%까지만 입력할 수 있습니다.`
          : brand === 'KCC홈CC'
          ? `KCC홈씨씨는 최대 ${maxRate}%까지만 입력할 수 있습니다.`
          : '최대 할인율을 초과할 수 없습니다.'
      );
      rate = maxRate;
    }

    // 즉시 UI 업데이트 (앞의 0 제거된 숫자로)
    setDiscountRates((prev) => ({ ...prev, [brand]: rate }));

    // 이전 타이머 취소
    if (discountRateTimerRef.current) {
      clearTimeout(discountRateTimerRef.current);
    }

    // 500ms 후에 할인 금액 재계산 (debounce)
    discountRateTimerRef.current = setTimeout(() => {
      calculateDiscounts();
      // 할인율 변경 후 샷시 가격 재계산 트리거 (비공개 금액 분배 업데이트)
      // 더미 업데이트를 통해 useEffect(Line 1422) 재실행
      isDiscountTriggeredRecalcRef.current = true;
      setShasiData(prev => {
        // 동일한 데이터지만 참조를 변경하여 useEffect 트리거
        return [...prev];
      });
    }, 500);
  };

  // 할인율 input blur 핸들러 - 포커스를 잃을 때 즉시 계산
  const handleDiscountRateBlur = () => {
    // 타이머가 있으면 취소하고 즉시 계산
    if (discountRateTimerRef.current) {
      clearTimeout(discountRateTimerRef.current);
      discountRateTimerRef.current = null;
    }
    calculateDiscounts();
    // 할인율 변경 후 샷시 가격 재계산 트리거 (비공개 금액 분배 업데이트)
    isDiscountTriggeredRecalcRef.current = true;
    setShasiData(prev => [...prev]);
  };

  // 할인비율이 0이 아닌 값이 하나라도 있는지 확인하는 함수
  const hasAnyDiscountRate = () => {
    return (
      Number(discountRates.homeWindow) > 0 ||
      Number(discountRates.LX하우시스) > 0 ||
      Number(discountRates.KCC홈CC) > 0
    );
  };

  // 유효한 데이터가 있는지 확인하는 헬퍼 함수들
  const hasValidOtherData = () => {
    return otherData.some(row => row.item && row.item !== '-' && row.item !== '선택');
  };

  const hasValidTongbaData = () => {
    return tongbaData.some(row => row.item && row.item !== '-' && row.item !== '선택');
  };

  const hasValidAdditionalData = () => {
    return additionalData.some(row => row.item && row.item !== '-' && row.item !== '선택');
  };

  // 이미지 미리보기 모달 상태
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [previewImageUrl, setPreviewImageUrl] = useState('');
  const [previewProductData, setPreviewProductData] = useState<Record<string, string | number>[]>([]);

  // 견적 불러오기 모달 상태
  // 견적번호 자동 생성 (견적 계산 시)
  const generateEstimateNumber = useCallback(async (customCustomerName?: string, customPhone?: string) => {
    // 파라미터로 전달된 값이 있으면 사용, 없으면 state 사용
    const customerName = customCustomerName || estimateInfo.customerName;
    const phone = customPhone || estimateInfo.phone;

    if (customerName && phone) {
      try {
        const response = await api.post(
          '/api/estimates/generate-number',
          new URLSearchParams({
            customerName: customerName,
            phone: phone,
          })
        );

        if (response.data) {
          const estimateNumber = response.data;
          setEstimateInfo((prev) => ({
            ...prev,
            estimateNumber,
          }));
        } else {
          console.error('견적번호 생성 실패:', response.status);
          // 실패 시 기존 방식으로 폴백
          const today = new Date();
          const year = today.getFullYear().toString().slice(2);
          const month = String(today.getMonth() + 1).padStart(2, '0');
          const date = String(today.getDate()).padStart(2, '0');
          const todayString = `${year}${month}${date}`;
          const firstChar = customerName.charAt(0) || 'X';
          const last4 =
            phone.replace(/-/g, '').slice(-4) || '0000';
          const randomSuffix = Math.floor(Math.random() * 99) + 1;
          const fallbackNumber = `${todayString}-${firstChar}-${last4}-${String(
            randomSuffix
          ).padStart(2, '0')}`;

          setEstimateInfo((prev) => ({
            ...prev,
            estimateNumber: fallbackNumber,
          }));
        }
      } catch (error) {
        console.error('견적번호 생성 중 오류:', error);
        // 네트워크 오류 시 기존 방식으로 폴백
        const today = new Date();
        const year = today.getFullYear().toString().slice(2);
        const month = String(today.getMonth() + 1).padStart(2, '0');
        const date = String(today.getDate()).padStart(2, '0');
        const todayString = `${year}${month}${date}`;
        const firstChar = customerName.charAt(0) || 'X';
        const last4 = phone.replace(/-/g, '').slice(-4) || '0000';
        const randomSuffix = Math.floor(Math.random() * 99) + 1;
        const fallbackNumber = `${todayString}-${firstChar}-${last4}-${String(
          randomSuffix
        ).padStart(2, '0')}`;

        setEstimateInfo((prev) => ({
          ...prev,
          estimateNumber: fallbackNumber,
        }));
      }
    }
  }, [estimateInfo.customerName, estimateInfo.phone]);

  // 견적 자동계산 (입력값 변경 시 자동 반영)
  const calculateEstimate = useCallback(async (
    customShasiData?: ShasiRow[],
    customOtherData?: OtherRow[],
    customTongbaData?: TongbaRow[],
    customAdditionalData?: AdditionalRow[],
    customDiscountRates?: DiscountRates,
    customCustomerName?: string,
    customPhone?: string
  ) => {
    // 중복 실행 방지
    if (isCalculatingRef.current) {
      return;
    }

    isCalculatingRef.current = true;

    try {
      // 매개변수가 있으면 사용, 없으면 state 사용 (Nullish coalescing)
      const shasiDataToUse = customShasiData ?? shasiData;
      const otherDataToUse = customOtherData ?? otherData;
      const tongbaDataToUse = customTongbaData ?? tongbaData;
      const additionalDataToUse = customAdditionalData ?? additionalData;

      // 견적번호 생성 (파라미터로 전달된 고객 정보 사용)
      await generateEstimateNumber(customCustomerName, customPhone);

      // 합계 계산 (순수 함수)
      const newTotals = calculateTotals(
        shasiDataToUse,
        otherDataToUse,
        tongbaDataToUse,
        additionalDataToUse
      );
      setTotals(newTotals);
    } finally {
      // 계산 완료 후 플래그 해제
      isCalculatingRef.current = false;
    }
  }, [
    shasiData,
    otherData,
    tongbaData,
    additionalData,
    discountRates,
    generateEstimateNumber,
  ]);

  // 견적서를 데이터베이스에 저장하는 함수
  const saveEstimateToDatabase = async () => {
    try {
      // 필수 정보 체크 (JPG 생성 시에는 조용히 실패)
      if (!estimateInfo.customerName?.trim() || !estimateInfo.phone?.trim()) {
        return null;
      }

      // 현재 로그인한 사용자의 견적자명을 세션에서 직접 가져오기
      let currentEstimator;
      try {
        const userResponse = await api.get('/api/UserName');
        if (userResponse.data) {
          const userData = userResponse.data;
          currentEstimator = userData.name;
        } else {
          currentEstimator = '견적자';
        }
      } catch (error) {
        console.error('사용자 정보 조회 실패:', error);
        currentEstimator = '견적자';
      }

      // 견적 기본 정보 준비
      const estimateData = {
        estimateNumber: estimateInfo.estimateNumber || 'TEMP-' + Date.now(),
        estimateDate: estimateInfo.estimateDate,
        estimator: currentEstimator, // 세션에서 가져온 현재 사용자명 사용
        estimateType: estimateInfo.estimateType || '-', // 견적유형 추가
        customerName: estimateInfo.customerName,
        phone: estimateInfo.phone,
        address: estimateInfo.address || '주소 미입력',
        detailedAddress: estimateInfo.detailedAddress || '',
        constructionStatus: estimateInfo.constructionStatus || '시공',
        scheduledDate: estimateInfo.scheduledDate || null,
        checkboxes: JSON.stringify(checkboxes), // JSON 문자열로 변환
        selectedGrades: JSON.stringify(selectedGrades), // 브랜드별 등급 JSON 문자열로 변환
        items: [] as EstimateItem[],
      };

      // 샷시 항목들 추가 (브랜드별로 분리)
      shasiData.forEach((row, rowIndex) => {
        if (row.item && row.item !== '-') {
          const width = row.width ? parseFloat(row.width) : null;
          const height = row.height ? parseFloat(row.height) : null;
          const quantity = parseInt(row.quantity) || 1;
          
          // note에 행 인덱스 추가 (복사된 행 구분용)
          const noteWithIndex = row.note ? `${row.note}__IDX${rowIndex}` : `__IDX${rowIndex}`;

          // 브랜드별로 3개 레코드 생성
          const brands = [
            { code: 'HW', price: row.homeWindowPrice || 0 },
            { code: 'LX', price: row.lxHausysPrice || 0 },
            { code: 'KC', price: row.kccHomeCCPrice || 0 },
          ];

          brands.forEach((brand) => {
            if (brand.price > 0) {
              // 가격이 있는 브랜드만 저장
              // [주석처리] 브랜드별 창호 스펙 정보 수집 - DB 저장 로직 비활성화
              // const brandSpecData = {
              //   auto_handle_scope: '',
              //   combined_frame_door_options: '',
              //   special_benefit_type: '',
              //   window_spec_brand: '',
              // };
              // if (brand.code === 'LX') {
              //   brandSpecData.auto_handle_scope = lxAutoHandleScope;
              //   const selectedOptions = Object.keys(lxCombinedFrameOptions).filter(key => lxCombinedFrameOptions[key]);
              //   brandSpecData.combined_frame_door_options = JSON.stringify({ lxProductionOption: lxProductionOption, options: selectedOptions });
              //   brandSpecData.special_benefit_type = lxSpecialBenefitType;
              //   brandSpecData.window_spec_brand = lxProductionOption;
              // } else if (brand.code === 'HW') {
              //   brandSpecData.auto_handle_scope = hwAutoHandleScope;
              //   const selectedOptions = Object.keys(hwCombinedFrameOptions).filter(key => hwCombinedFrameOptions[key]);
              //   brandSpecData.combined_frame_door_options = JSON.stringify({ options: selectedOptions });
              //   brandSpecData.special_benefit_type = hwSpecialBenefitType;
              //   brandSpecData.window_spec_brand = '홈윈도우';
              // } else if (brand.code === 'KC') {
              //   brandSpecData.auto_handle_scope = kccAutoHandleScope;
              //   const selectedOptions = Object.keys(kccCombinedFrameOptions).filter(key => kccCombinedFrameOptions[key]);
              //   brandSpecData.combined_frame_door_options = JSON.stringify({ kccBrandOption: kccBrandOption, options: selectedOptions });
              //   brandSpecData.special_benefit_type = kccSpecialBenefitType;
              //   brandSpecData.window_spec_brand = '홈씨씨';
              // }

              estimateData.items.push({
                productType: '샷시&망&랩핑',
                installLocation: row.installLocation || '',
                item: row.item,
                width: width,
                height: height,
                quantity: quantity,
                brand: brand.code,
                price: brand.price,
                priceWithTax: Math.round(brand.price * 1.1), // 부가세 포함 가격 계산
                note: noteWithIndex,
                estimateNotes: estimateInfo.estimateNotes || '', // 견적서 메모 추가
                // [주석처리] 브랜드별 창호 스펙 정보 - DB 저장 비활성화
                // autoHandleScope: brandSpecData.auto_handle_scope,
                // combinedFrameDoorOptions: brandSpecData.combined_frame_door_options,
                // specialBenefitType: brandSpecData.special_benefit_type,
                // windowSpecBrand: brandSpecData.window_spec_brand,
              });
            }
          });
        }
      });

      // 중문 & 기타 항목들 추가
      otherData.forEach((row, index) => {
        if (row.item && row.item !== '-') {
          const width = row.width ? parseFloat(row.width) : null;
          const height = row.height ? parseFloat(row.height) : null;
          const price = row.price || 0;

          estimateData.items.push({
            productType: '중문&기타',
            installLocation: row.installLocation || '',
            item: row.item,
            width: width,
            height: height,
            color: row.color || '',
            quantity: parseInt(row.quantity) || 1,
            brand: null, // 중문&기타는 브랜드 없음
            price: price,
            priceWithTax: Math.round(price * 1.1), // 부가세 포함 가격 계산
            estimateNotes: estimateInfo.estimateNotes || '', // 견적서 메모 추가
            note: row.note || '', // 비고 추가
          });
        }
      });

      // 통바 항목들 추가
      tongbaData.forEach((row, index) => {
        if (row.item && row.item !== '-') {
          const price = row.price || 0;
          estimateData.items.push({
            productType: '통바',
            item: row.item,
            width: row.spec ? parseFloat(row.spec) : null, // spec을 width로 변환
            quantity: parseInt(row.quantity) || 1,
            brand: null, // 통바는 브랜드 없음
            price: price,
            priceWithTax: Math.round(price * 1.1), // 부가세 포함 가격 계산
            estimateNotes: estimateInfo.estimateNotes || '', // 견적서 메모 추가
          });
        }
      });

      // 추가사항 항목들 추가
      additionalData.forEach((row, index) => {
        if (row.item && row.item !== '-') {
          const price = Number(row.price) || 0; // 명시적으로 숫자 변환

          // 직접입력 항목 구분
          let productType = '추가사항';
          let itemName = row.item;
          let directInput: string | null = null;

          if (row.item === '직접입력') {
            productType = '추가사항';
            itemName = '직접입력';
            directInput = row.customItem || '';
          } else if (row.item === '직접입력(고객 비공개용)') {
            productType = '추가사항';
            itemName = '직접입력(고객 비공개용)';
            directInput = row.customItem || '';
          } else if (row.item === '비공개 금액_선택') {
            productType = '추가사항';
            itemName = '비공개 금액_선택';
            directInput = row.customItem || '';
          } else if (row.item === '비공개 금액_주택') {
            productType = '추가사항';
            itemName = '비공개 금액_주택';
            // 비공개 금액_주택은 customItem 없음
          } else if (row.item === '비공개 금액_빌라') {
            productType = '추가사항';
            itemName = '비공개 금액_빌라';
            // 비공개 금액_빌라는 customItem 없음
          }

          estimateData.items.push({
            productType: productType,
            item: itemName,
            quantity: parseInt(row.quantity) || 1,
            brand: null, // 추가사항은 브랜드 없음
            price: price,
            priceWithTax: Math.round(price * 1.1), // 부가세 포함 가격 계산
            directInput: directInput, // 직접입력 내용 저장
            estimateNotes: estimateInfo.estimateNotes || '', // 견적서 메모 추가
          });
        }
      });

      // 할인 내역 추가 (브랜드별로 분리)
      if (
        (discountData.homeWindow !== 0 ||
          discountData.LX하우시스 !== 0 ||
          discountData.KCC홈CC !== 0) &&
        (Number(discountRates.homeWindow) > 0 ||
          Number(discountRates.LX하우시스) > 0 ||
          Number(discountRates.KCC홈CC) > 0)
      ) {
        const discountBrands = [
          {
            code: 'HW',
            amount: discountData.homeWindow,
            rate: discountRates.homeWindow,
          },
          {
            code: 'LX',
            amount: discountData.LX하우시스,
            rate: discountRates.LX하우시스,
          },
          {
            code: 'KC',
            amount: discountData.KCC홈CC,
            rate: discountRates.KCC홈CC,
          },
        ];
        discountBrands.forEach((brandDiscount) => {
          if (brandDiscount.amount !== 0) {
            const discountAmountWithTax = brandDiscount.amount;
            const discountAmountBase = Math.round(discountAmountWithTax / 1.1);
            estimateData.items.push({
              productType: '할인내역',
              item: `${brandDiscount.rate}% 할인`,
              quantity: 1,
              brand: brandDiscount.code,
              price: discountAmountBase,
              priceWithTax: discountAmountWithTax,
              estimateNotes: estimateInfo.estimateNotes || '',
            });
          }
        });
      }

      // 서버에 견적 저장 요청
      const response = await api.post('/api/estimates', estimateData);

      if (response.data) {
        const savedEstimate = response.data;
        return savedEstimate;
      } else {
        console.error('견적서 저장 실패:', response.status);
        return null;
      }
    } catch (error) {
      console.error('견적서 저장 중 오류 발생:', error);
      return null;
    }
  };

  // 견적 불러오기
  const loadEstimate = async (estimateNumber) => {
    // 견적 불러오기 중 가격 재계산 방지
    isLoadingEstimateRef.current = true;

    try {
      const response = await api.get(`/api/estimates/${estimateNumber}`);

      if (response.data) {
        // 순수 함수로 데이터 파싱 (원작성자 정보는 estimateData에서 추출)
        const parsed = parseEstimateData({
          estimateData: response.data,
          itemPrices,
          unitPrices,
          fixedVariables,
          isUnitPricesLoaded,
        });

        // setState 일괄 적용
        setEstimateInfo((prev) => ({ ...prev, ...parsed.estimateInfo }));
        setCheckboxes(parsed.checkboxes);
        setSelectedGrades(parsed.selectedGrades);
        setShasiData(parsed.shasiData);
        setOtherData(parsed.otherData);
        setTongbaData(parsed.tongbaData);
        setAdditionalData(parsed.additionalData);
        setDiscountData(parsed.discountData);
        setDiscountRates(parsed.discountRates);

        // 견적 계산 실행
        calculateEstimate(
          parsed.shasiData,
          parsed.otherData,
          parsed.tongbaData,
          parsed.additionalData,
          parsed.discountRates,
          parsed.estimateInfo.customerName,
          parsed.estimateInfo.phone
        );

        closeLoadModal();
        alert('견적서를 성공적으로 불러왔습니다!');

        // 상태 업데이트 완료 후 플래그 해제
        setTimeout(() => {
          isLoadingEstimateRef.current = false;
        }, 100);
      } else {
        console.error('견적 불러오기 실패');
        alert('견적을 불러오는데 실패했습니다.');
        isLoadingEstimateRef.current = false;
      }
    } catch (error) {
      console.error('견적 불러오기 중 오류:', error);
      alert('견적을 불러오는 중 오류가 발생했습니다.');
      isLoadingEstimateRef.current = false;
    }
  };

  // Summary 이미지 생성 및 업로드
  const generateAndUploadSummaryImage = async (element: HTMLElement) => {
    try {
      const summaryElement = element.querySelector('.estimate-summary-cards') as HTMLElement;
      if (!summaryElement) {
        throw new Error('estimate-summary-cards 요소 발견 불가능');
      }

      const width = document.getElementById('estimate-form')!.offsetWidth;
      const summaryTop = summaryElement.offsetTop;
      const summaryHeight = summaryElement.offsetHeight;
      const cropHeight = summaryTop + summaryHeight + 20;

      const summaryImageDataUrl = await toJpeg(element, {
        quality: 0.70,
        backgroundColor: '#ffffff',
        width,
        height: cropHeight,
        pixelRatio: 1.0,
        style: {
          margin: '0',
          padding: '40px',
          transform: 'none',
          transformOrigin: 'top left',
        },
      });

      const uploadResponse = await api.post('/api/estimates/upload-image', {
        imageDataUrl: summaryImageDataUrl,
        estimateNumber: estimateInfo.estimateNumber,
        imageType: 'summary'
      });

      if (uploadResponse.status !== 200) {
        console.error('Summary 이미지 업로드 실패');
      }
    } catch (error) {
      console.error('Summary 이미지 업로드 오류:', error);
    }
  };

  // JPG 다운로드
  const handleDownload = async () => {
    // 빨간색 border 효과 초기화
    const clearErrorBorders = () => {
      document.querySelectorAll('.error-border').forEach(element => {
        element.classList.remove('error-border');
      });
    };
    
    clearErrorBorders();
    
    // 필수 입력값 검증 및 focus 처리
    // 견적유형 검증
    if (!estimateInfo.estimateType || estimateInfo.estimateType === '-') {
      alert('견적유형을 선택해주세요.');
      const estimateInfoContainer = document.querySelector('.estimate-info-container');
      const estimateTypeSelect = estimateInfoContainer?.querySelector('.table2 tr:nth-child(2) td:nth-child(4) select') as HTMLSelectElement;
      if (estimateTypeSelect) {
        estimateTypeSelect.classList.add('error-border');
        estimateTypeSelect.focus();
        estimateTypeSelect.addEventListener('change', () => {
          estimateTypeSelect.classList.remove('error-border');
        }, { once: true });
      }
      return;
    }
    
    // 고객명 검증
    if (!estimateInfo.customerName?.trim()) {
      alert('고객명을 입력해주세요.');
      const table = document.querySelector('.table.table-no-margin');
      const customerNameInput = table?.querySelector('tr:nth-child(2) td:nth-child(1) input') as HTMLInputElement;
      if (customerNameInput) {
        customerNameInput.classList.add('error-border');
        customerNameInput.focus();
        customerNameInput.addEventListener('input', () => {
          customerNameInput.classList.remove('error-border');
        }, { once: true });
      }
      return;
    }
    
    // 연락처 검증
    if (!estimateInfo.phone?.trim()) {
      alert('연락처를 입력해주세요.');
      const phoneInput = document.querySelector('input[type="tel"]') as HTMLInputElement;
      if (phoneInput) {
        phoneInput.classList.add('error-border');
        phoneInput.focus();
        phoneInput.addEventListener('input', () => {
          phoneInput.classList.remove('error-border');
        }, { once: true });
      }
      return;
    }
    
    // 주소 검증
    if (!estimateInfo.address?.trim()) {
      alert('주소를 입력해주세요.');
      const addressInput = document.querySelector('input[placeholder*="주소"]') as HTMLInputElement;
      if (addressInput) {
        addressInput.classList.add('error-border');
        addressInput.focus();
        addressInput.addEventListener('input', () => {
          addressInput.classList.remove('error-border');
        }, { once: true });
      }
      return;
    }
    
    // 샷시 품명 및 규격 검증
    // 실제로 유효한 품명이 선택된 샷시 항목 확인
    const validShasiItems = shasiData.filter(row => 
      row.item && row.item !== '-' && row.item !== '선택' && row.item !== '랩핑추가'
    );
    
    // 유효한 샷시가 하나도 없는 경우에만 첫 번째 샷시 입력 요구
    // (모든 행이 빈 행이거나 랩핑추가만 있는 경우)
    if (validShasiItems.length === 0) {
      // 랩핑추가만 있는지 확인
      const hasOnlyWrapping = shasiData.some(row => row.item === '랩핑추가');
      
      if (!hasOnlyWrapping) {
        // 완전히 비어있는 경우
        alert('샷시 품명을 최소 1개 이상 선택해주세요.');
        const shasiTable = document.getElementById('shasiTable');
        const shasiSelects = shasiTable?.querySelectorAll('select');
        if (shasiSelects && shasiSelects.length > 0) {
          const firstSelect = shasiSelects[0] as HTMLSelectElement;
          firstSelect.classList.add('error-border');
          firstSelect.focus();
          firstSelect.addEventListener('change', () => {
            firstSelect.classList.remove('error-border');
          }, { once: true });
        }
        return;
      }
    }
    
    // 샷시 품명은 선택했지만 규격이 없는 경우 검증
    // 유효한 품명이 선택된 항목만 규격 검증
    for (let i = 0; i < shasiData.length; i++) {
      const row = shasiData[i];
      
      // 유효한 품명이 선택된 경우에만 규격 검증
      if (row.item && row.item !== '-' && row.item !== '선택' && row.item !== '랩핑추가') {
        // 너비 검증
        if (!row.width || row.width === '') {
          alert(`${i+1}번째 샷시의 너비(W)를 입력해주세요.`);
          const shasiTable = document.getElementById('shasiTable');
          const allRows = shasiTable?.querySelectorAll('tbody tr');
          const dataRows = Array.from(allRows || []).filter(tr => 
            tr.querySelector('input[placeholder="W"]')
          );
          
          if (dataRows[i]) {
            const widthInput = dataRows[i].querySelector('input[placeholder="W"]') as HTMLInputElement;
            if (widthInput) {
              widthInput.classList.add('error-border');
              widthInput.focus();
              widthInput.addEventListener('input', () => {
                widthInput.classList.remove('error-border');
              }, { once: true });
            }
          }
          return;
        }
        
        // 높이 검증
        if (!row.height || row.height === '') {
          alert(`${i+1}번째 샷시의 높이(H)를 입력해주세요.`);
          const shasiTable = document.getElementById('shasiTable');
          const allRows = shasiTable?.querySelectorAll('tbody tr');
          const dataRows = Array.from(allRows || []).filter(tr => 
            tr.querySelector('input[placeholder="H"]')
          );
          
          if (dataRows[i]) {
            const heightInput = dataRows[i].querySelector('input[placeholder="H"]') as HTMLInputElement;
            if (heightInput) {
              heightInput.classList.add('error-border');
              heightInput.focus();
              heightInput.addEventListener('input', () => {
                heightInput.classList.remove('error-border');
              }, { once: true });
            }
          }
          return;
        }
      }
    }
    
    // 통바 검증 - 제품이 선택된 경우만 규격 필수 (빈 행은 제외)
    for (let i = 0; i < tongbaData.length; i++) {
      const row = tongbaData[i];
      
      // 통바 품명이 선택된 경우에만 규격 필수 (기본값 제외)
      if (row.item && row.item !== '-' && row.item !== '선택') {
        if (!row.spec || row.spec === '') {
          alert(`${i+1}번째 통바의 길이를 입력해주세요.`);
          const tongbaTable = document.getElementById('tongbaTable');
          const allRows = tongbaTable?.querySelectorAll('tbody tr');
          const dataRows = Array.from(allRows || []).filter(tr => 
            tr.querySelector('td select')
          );
          
          if (dataRows[i]) {
            const specInput = dataRows[i].querySelectorAll('td input')[0] as HTMLInputElement;
            if (specInput) {
              specInput.classList.add('error-border');
              specInput.focus();
              specInput.addEventListener('input', () => {
                specInput.classList.remove('error-border');
              }, { once: true });
            }
          }
          return;
        }
      }
    }

    // 샷시 규격 범위 검증 (최소/최대값, FIX 면적)
    for (let i = 0; i < shasiData.length; i++) {
      const row = shasiData[i];

      if (row.item && row.item !== '-' && row.item !== '선택' && row.item !== '랩핑추가') {
        const isBalcony = row.item.includes('발코니');
        const isGeneral = row.item.includes('일반');
        const isBalcony2W = row.item === '발코니_이중창_2W';
        const isFix = row.item.includes('FIX');
        const width = parseFloat(row.width) || 0;
        const height = parseFloat(row.height) || 0;

        let errorMessage = '';
        let errorField: 'width' | 'height' = 'width';

        // 너비 범위 검증
        if (width > 0) {
          if (width > 5400) {
            errorMessage = `${i+1}번째 샷시: 최대 너비(5400mm)를 초과하였습니다.`;
            errorField = 'width';
          } else if (isBalcony2W && width < 320) {
            errorMessage = `${i+1}번째 샷시: 발코니_이중창_2W의 최소 너비는 320mm입니다.`;
            errorField = 'width';
          } else if (!isBalcony2W && isBalcony && width < 800) {
            errorMessage = `${i+1}번째 샷시: 발코니 품목의 최소 너비는 800mm입니다.`;
            errorField = 'width';
          } else if (isGeneral && width < 600) {
            errorMessage = `${i+1}번째 샷시: 일반 품목의 최소 너비는 600mm입니다.`;
            errorField = 'width';
          }
        }

        // 높이 범위 검증
        if (!errorMessage && height > 0) {
          if (height > 2510) {
            errorMessage = `${i+1}번째 샷시: 최대 높이(2510mm)를 초과하였습니다.`;
            errorField = 'height';
          } else if (isBalcony2W && height < 320) {
            errorMessage = `${i+1}번째 샷시: 발코니_이중창_2W의 최소 높이는 320mm입니다.`;
            errorField = 'height';
          } else if (!isBalcony2W && isBalcony && height < 500) {
            errorMessage = `${i+1}번째 샷시: 발코니 품목의 최소 높이는 500mm입니다.`;
            errorField = 'height';
          } else if (isGeneral && height < 400) {
            errorMessage = `${i+1}번째 샷시: 일반 품목의 최소 높이는 400mm입니다.`;
            errorField = 'height';
          }
        }

        // FIX 자평 검증
        if (!errorMessage && isFix && width > 0 && height > 0) {
          const area = Math.ceil((width * height) / 90000);
          if (area > 100) {
            errorMessage = `${i+1}번째 샷시: FIX 품목의 최대 면적(100자평)을 초과하였습니다.`;
            errorField = 'width';
          }
        }

        if (errorMessage) {
          alert(errorMessage);
          const shasiTable = document.getElementById('shasiTable');
          const allRows = shasiTable?.querySelectorAll('tbody tr');
          const placeholder = errorField === 'width' ? 'W' : 'H';
          const dataRows = Array.from(allRows || []).filter(tr =>
            tr.querySelector(`input[placeholder="${placeholder}"]`)
          );

          if (dataRows[i]) {
            const input = dataRows[i].querySelector(`input[placeholder="${placeholder}"]`) as HTMLInputElement;
            if (input) {
              input.classList.add('error-border');
              input.scrollIntoView({ behavior: 'smooth', block: 'center' });
              input.focus();
              input.addEventListener('input', () => {
                input.classList.remove('error-border');
              }, { once: true });
            }
          }
          return;
        }
      }
    }

    // 캡처 시작 - 가격 재계산 방지
    isCapturingRef.current = true;

    // 이미지 캡처 전 error-border 클래스 완전 제거
    clearErrorBorders();

    const element = document.getElementById('estimate-form');
    if (!element) {
      isCapturingRef.current = false; // 실패 시 복원
      return;
    }

    // React 상태를 먼저 업데이트하여 안내문구가 렌더링되도록 함
    setIsCaptureMode(true);
    // 강제로 리렌더링 유도 (setTimeout 50ms 후 이미지 캡처)
    await new Promise((resolve) => setTimeout(resolve, 50));
    
    // 캡처 직전 한번 더 error-border 제거 (안전장치)
    clearErrorBorders();

    // 견적 불러오기 후 JPG 생성 시, 견적번호를 새로 생성
    if (
      loadedEstimateNumber &&
      estimateInfo.estimateNumber === loadedEstimateNumber
    ) {
      await generateEstimateNumber(); // 새로운 번호로 갱신
      setLoadedEstimateNumber(null); // 이후에는 새 번호로 동작
    }

    // 견적서를 데이터베이스에 먼저 저장 (실패해도 계속 진행)
    const savedEstimate = await saveEstimateToDatabase();
    // 견적서 저장 여부와 관계없이 이미지 생성 진행

    // 캡처 전 숨길 UI(예: .add-row-btn 등) 숨기기
    const addRowBtns = element.querySelectorAll('.add-row-btn');
    addRowBtns.forEach((btn) => ((btn as HTMLElement).style.display = 'none'));

    // 브랜드 선택 select 숨기기
    const brandSelect = element.querySelector('.hook-brand-select');
    if (brandSelect) {
      (brandSelect as HTMLElement).style.display = 'none';
    }

    // 캡처 모드 클래스 추가 (이미 React 상태가 true이므로 DOM에도 추가)
    element.classList.add('capture-mode');

    // 값이 있는 input에만 표시 클래스 추가
    const inputs = element.querySelectorAll(
      'input[type="text"], input[type="tel"], input[type="date"]'
    );
    inputs.forEach((input) => {
      const inputEl = input as HTMLInputElement;
      const value = inputEl.value.trim();
      const placeholder = inputEl.placeholder || '';
      if (
        value &&
        value !== '' &&
        value !== placeholder &&
        !placeholder.includes(value)
      ) {
        inputEl.classList.add('has-value');
      }
    });

    // 레거시 데이터: disabled input으로 표시된 직접입력(고객 비공개용) 행 숨김
    const legacyAdditionalTable = element.querySelector('#additionalTable');
    if (legacyAdditionalTable) {
      const disabledInputs = legacyAdditionalTable.querySelectorAll('input[disabled]');
      disabledInputs.forEach((input) => {
        const inputEl = input as HTMLInputElement;
        if (inputEl.value === '직접입력(고객 비공개용)') {
          const row = inputEl.closest('tr');
          if (row) {
            (row as HTMLElement).style.display = 'none';
          }
        }
      });
    }

    // select 캡처용 span 생성 및 select 숨김
    const selects = element.querySelectorAll('select');
    const selectSpans: {
      td: HTMLTableCellElement;
      select: HTMLSelectElement;
      span: HTMLSpanElement;
    }[] = [];
    selects.forEach((select) => {
      const selectEl = select as HTMLSelectElement;
      const value = selectEl.value;
      const selectedOption = selectEl.options[selectEl.selectedIndex];
      const selectedText = selectedOption ? selectedOption.text : '';
      
      // 추가사항 테이블의 직접입력 처리
      if (selectEl.closest('#additionalTable')) {
        const row = selectEl.closest('tr');
        if (row) {
          const customItemInput = row.querySelector('input[type="text"][placeholder*="품명을 직접 입력하세요"]') as HTMLInputElement;
          
          if (value === '직접입력' && customItemInput && customItemInput.value.trim()) {
            // 직접입력: 입력받은 품명만 표시
            const span = document.createElement('span');
            span.className = 'select-capture-text';
            span.textContent = customItemInput.value.trim();
            span.style.display = 'flex';
            span.style.alignItems = 'center';
            span.style.justifyContent = 'center';
            span.style.width = '100%';
            span.style.height = '100%';
            span.style.textAlign = 'center';
            span.style.fontSize = window.getComputedStyle(select).fontSize;
            span.style.fontFamily = window.getComputedStyle(select).fontFamily;
            span.style.background = 'transparent';
            span.style.border = 'none';
            span.style.margin = '0';
            span.style.padding = '0';
            span.style.boxSizing = 'border-box';
            
            let td = select.closest('td');
            if (td) {
              td.removeChild(selectEl);
              td.appendChild(span);
              selectSpans.push({ td, select: selectEl, span });
            }
            
            // 직접입력 필드 숨김
            if (customItemInput) {
              customItemInput.style.display = 'none';
            }
            
            return; // 이 select는 처리 완료
          } else if (value === '직접입력(고객 비공개용)' || value === '비공개 금액_선택' || value === '비공개 금액_주택' || value === '비공개 금액_빌라') {
            // 고객 비공개용/비공개 금액 항목: 전체 행을 숨김
            if (row) {
              row.style.display = 'none';
            }
            return; // 이 select는 처리 완료
          }
        }
      }
      
      // 일반적인 select 처리
      const showText =
        value &&
        value !== '-' &&
        value !== '' &&
        value !== '선택' &&
        value !== '할인 선택' &&
        selectedText !== '-' &&
        selectedText !== '선택'
          ? selectedText
          : '';
      // 이미 span이 있으면 중복 생성 방지
      if (
        !selectEl.nextSibling ||
        !(selectEl.nextSibling as HTMLElement).classList ||
        !(selectEl.nextSibling as HTMLElement).classList.contains(
          'select-capture-text'
        )
      ) {
        const span = document.createElement('span');
        span.className = 'select-capture-text';
        span.textContent = showText;
        let td = select.closest('td');
        let tdHeight = td ? td.offsetHeight : select.offsetHeight;
        if (!tdHeight || tdHeight < 28) tdHeight = 28;
        span.style.display = 'flex';
        span.style.alignItems = 'center';
        span.style.justifyContent = 'center';
        span.style.height = tdHeight + 'px';
        span.style.width = '100%';
        span.style.textAlign = 'center';
        span.style.fontSize = window.getComputedStyle(select).fontSize;
        span.style.fontFamily = window.getComputedStyle(select).fontFamily;
        span.style.background = 'transparent';
        span.style.border = 'none';
        span.style.margin = '0';
        span.style.padding = '0';
        span.style.boxSizing = 'border-box';
        // select를 td에서 완전히 제거하고 span만 남김
        if (td) {
          td.removeChild(selectEl);
          td.appendChild(span);
          selectSpans.push({ td, select: selectEl, span });
        }
      }
      // has-value 클래스 처리
      if (showText) {
        selectEl.classList.add('has-value');
      }
    });

    // 가격이 0이 아닌 disabled input만 표시 (가격 필드)
    const disabledInputs = element.querySelectorAll('input[disabled]');
    disabledInputs.forEach((input) => {
      const inputEl = input as HTMLInputElement;
      const value = inputEl.value.replace(/,/g, '');
      if (
        value &&
        value !== '0' &&
        value !== '000' &&
        value !== '0,000' &&
        value !== '00,000' &&
        value !== '000,000'
      ) {
        inputEl.classList.add('has-value');
      }
    });

    // 고객 비공개용 항목을 숨긴 후 추가사항 테이블의 번호 재정렬
    const additionalTable = element.querySelector('#additionalTable');
    if (additionalTable) {
      // 헤더 행을 제외한 데이터 행들만 선택 (th가 없는 tr)
      const allRows = additionalTable.querySelectorAll('tbody tr');
      let visibleRowNumber = 1;
      
      allRows.forEach((row) => {
        // 헤더 행인지 확인 (th 요소가 있는지)
        const isHeaderRow = row.querySelector('th') !== null;
        if (isHeaderRow) return; // 헤더 행은 건너뛰기
        
        // 숨겨진 행인지 확인
        const rowElement = row as HTMLElement;
        if (rowElement.style.display === 'none') {
          return; // 숨겨진 행은 건너뛰기
        }
        
        // 첫 번째 td (번호 셀) 찾아서 번호 업데이트
        const numberCell = row.querySelector('td:first-child');
        if (numberCell) {
          numberCell.textContent = visibleRowNumber.toString();
          visibleRowNumber++;
        }
      });
    }

    window.scrollTo(0, 0);
    await new Promise((resolve) => setTimeout(resolve, 500)); // 더 긴 대기 시간으로 안정성 확보

    try {
      const imageDataUrl = await toJpeg(element, {
        quality: 0.95,
        backgroundColor: '#ffffff',
        width: 1200,
        height: element.offsetHeight,
        pixelRatio: 2.0,
        style: {
          margin: '0',
          padding: '40px',
          transform: 'none',
          transformOrigin: 'top left',
        },
      });

      // 파일명 만들기
      const estimateDate = estimateInfo.estimateDate || '날짜없음';
      const customerName = estimateInfo.customerName?.trim() || '고객명없음';
      const phone = estimateInfo.phone?.replace(/-/g, '') || '연락처없음';
      const fileName = `청암홈윈도우_${estimateDate}_${customerName}_${phone}.jpg`;

      // 전체 이미지 서버 업로드
      try {
        const uploadResponse = await api.post('/api/estimates/upload-image', {
          imageDataUrl: imageDataUrl,
          estimateNumber: estimateInfo.estimateNumber,
          imageType: 'full'
        });

        if (uploadResponse.status === 200) {
        } else {
          console.error('전체 이미지 업로드 실패');
        }
      } catch (uploadError) {
        console.error('전체 이미지 업로드 중 오류:', uploadError);
      }
      // 요약 이미지 생성 및 업로드
      await generateAndUploadSummaryImage(element);

      // TODO: 안정성 확인 시 Promise.all로 병렬 처리 요망

      // 다운로드
      const link = document.createElement('a');
      link.download = fileName;
      link.href = imageDataUrl;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // Google Sheets에 데이터 전송
      const products = collectProductData();
      if (products.length > 0) {
        exportToGoogleSheets(products, imageDataUrl);
      }

      // 미리보기 모달 표시
      setPreviewImageUrl(imageDataUrl);
      setPreviewProductData(products);
      setShowPreviewModal(true);
    } catch (error: unknown) {
      console.error('이미지 생성 중 오류:', error);
      alert(`이미지 생성 중 오류가 발생했습니다: ${error instanceof Error ? error.message : String(error)}`);
    } finally {
      // 복원
      element.classList.remove('capture-mode');
      setIsCaptureMode(false); // React 상태도 복원
      isCapturingRef.current = false; // 가격 재계산 방지 해제
      addRowBtns.forEach((btn) => ((btn as HTMLElement).style.display = ''));
      
      // 숨겨진 행들 복원
      const hiddenRows = element.querySelectorAll('#additionalTable tr[style*="display: none"]');
      hiddenRows.forEach((row) => {
        (row as HTMLElement).style.display = '';
      });
      
      // 숨겨진 직접입력 필드들 복원
      const hiddenInputs = element.querySelectorAll('#additionalTable input[style*="display: none"]');
      hiddenInputs.forEach((input) => {
        (input as HTMLElement).style.display = '';
      });
      
      // select 복원
      selectSpans.forEach(({ td, select, span }) => {
        if (span && span.parentNode)
          (span.parentNode as HTMLElement).removeChild(span);
        if (td && select) td.appendChild(select);
      });
    }
  };

  // 모달 닫기
  const handleClosePreview = () => {
    setShowPreviewModal(false);
    setPreviewImageUrl('');
    setPreviewProductData([]);
  };

  // 카카오톡 전송
  const handleKakaoShare = async () => {
    try {
      await api.post(
        `/api/estimates/numbers/${estimateInfo.estimateNumber}/kakao`
      , {
        lxHausysFinal: totals.lxHausys.final,
        homeWindowFinal: totals.homeWindow.final,
        kccHomeCCFinal: totals.kccHomeCC.final,
      });

      if (PermissionManager.getEstimateLoadScope() === 'OWN') {
        alert('카카오 알림톡이 고객님과 본인에게 발송되었습니다.');
      } else {
        alert('카카오 알림톡이 발송되었습니다.');
      }
    } catch (error: unknown) {
      console.error('카카오 알림톡 발송 오류:', error);
      alert('카카오 알림톡 발송에 실패했습니다. 확인 후 다시 시도해주세요.');
    }
  };

  // 문자메시지 전송
  const handleSmsShare = async () => {
    try {
      await api.post(
        `/api/estimates/numbers/${estimateInfo.estimateNumber}/sms`
      , {
        lxHausysFinal: totals.lxHausys.final,
        homeWindowFinal: totals.homeWindow.final,
        kccHomeCCFinal: totals.kccHomeCC.final,
      });

      if (PermissionManager.getEstimateLoadScope() === 'OWN') {
        alert('문자메시지가 고객님과 본인에게 발송되었습니다.');
      } else {
        alert('문자메시지가 발송되었습니다.');
      }
    } catch (error: unknown) {
      console.error('SMS 발송 오류:', error);
      alert('문자메시지 발송에 실패했습니다. 확인 후 다시 시도해주세요.');
    }
  };

  // 이미지 다시 다운로드
  const handleRedownload = () => {
    if (!previewImageUrl) return;

    const estimateDate = estimateInfo.estimateDate || '날짜없음';
    const customerName = estimateInfo.customerName?.trim() || '고객명없음';
    const phone = estimateInfo.phone?.replace(/-/g, '') || '연락처없음';
    const fileName = `청암홈윈도우_${estimateDate}_${customerName}_${phone}.jpg`;

    const link = document.createElement('a');
    link.download = fileName;
    link.href = previewImageUrl;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // 주소 검색 (도로명주소 API 사용)
  const openJusoPopup = () => {
    if (!jusoApiKey) {
      alert('주소 API 키를 불러오는 중입니다. 잠시 후 다시 시도해주세요.');
      return;
    }

    const confmKey = jusoApiKey; // application.properties에서 가져온 API 키
    const resultType = "4"; // 도로명+지번+상세건물명

    // returnUrl을 백엔드 서버로 설정
    // 백엔드 URL은 API 호스트와 동일
    const backendUrl = api.defaults.baseURL || process.env.REACT_APP_API_HOST || '';
    
    // returnUrl 설정 - 백엔드 서버의 jusoPopup.html을 가리켜야 함
    // API 요청이 프록시를 통해 가는 경우를 대비
    const returnUrl = backendUrl 
      ? `${backendUrl}/jusoPopup.html`
      : `${window.location.protocol}//${window.location.host}/jusoPopup.html`;

    // CSS URL 설정 - 환경에 따라 경로 설정
    // 운영서버: /juso.css, 로컬: /css/juso.css
    const isProduction = process.env.NODE_ENV === 'production';
    const cssUrl = isProduction 
      ? `${window.location.origin}/juso.css`
      : `${window.location.origin}/css/juso.css`;
    
    // 팝업 창 열기 (CSS URL 추가)
    const popup = window.open(
      `https://business.juso.go.kr/addrlink/addrLinkUrl.do?confmKey=${confmKey}&returnUrl=${encodeURIComponent(returnUrl)}&resultType=${resultType}&theme=css&cssUrl=${encodeURIComponent(cssUrl)}`,
      'jusoPopup',
      'width=570,height=420,scrollbars=yes,resizable=yes'
    );

    // 팝업이 차단되었는지 확인
    if (!popup || popup.closed || typeof popup.closed === 'undefined') {
      alert('팝업이 차단되었습니다. 팝업 차단을 해제해주세요.');
      return;
    }

    // 팝업 창에서 주소 선택 완료를 감지하는 이벤트 리스너 추가
    const checkPopupClosed = setInterval(() => {
      if (popup.closed) {
        clearInterval(checkPopupClosed);
      }
    }, 1000);
  };

  // 도로명주소 API 콜백 함수
  // 도로명주소 API 파라미터 순서: roadFullAddr, roadAddrPart1, roadAddrPart2, addrDetail 순
  const jusoCallBack = (
    roadFullAddr: string,
    roadAddrPart1: string,
    roadAddrPart2: string,
    addrDetail: string,
    engAddr: string,
    jibunAddr: string,
    zipNo: string,
    admCd: string,
    rnMgtSn: string,
    bdMgtSn: string,
    detBdNmList: string,
    bdNm: string,
    bdKdcd: string,
    siNm: string,
    sggNm: string,
    emdNm: string,
    liNm: string,
    rn: string,
    udrtYn: string,
    buldMnnm: string,
    buldSlno: string,
    mtYn: string,
    lnbrMnnm: string,
    lnbrSlno: string,
    emdNo: string
  ) => {
    // 주소 정보를 상태에 저장
    handleAddressCallback(roadAddrPart1, addrDetail);

    // 팝업 창 닫기
    if (window.opener && !window.opener.closed) {
      window.close();
    }
  };

  // 주소 콜백 처리 함수
  const handleAddressCallback = (roadAddr: string, addrDetail: string) => {
    setEstimateInfo((prev) => ({
      ...prev,
      address: roadAddr,
      detailedAddress: addrDetail || ''
    }));
  };

  // 주소 API 키 가져오기
  useEffect(() => {
    const fetchJusoApiKey = async () => {
      try {
        const response = await api.get('/api/juso/key');
        setJusoApiKey(response.data.key);
      } catch (error) {
        console.error('주소 API 키를 가져오는데 실패했습니다:', error);
      }
    };
    fetchJusoApiKey();
  }, []);

  // 전역 함수로 등록 (도로명주소 API에서 호출하기 위해)
  useEffect(() => {
    // 전역 함수 등록
    window.jusoCallBack = jusoCallBack;

    // 백엔드에서 전송된 메시지 리스너 추가
    const handleMessage = (event: MessageEvent) => {
      // JUSO_CALLBACK 타입의 메시지만 처리
      if (event.data && event.data.type === 'JUSO_CALLBACK') {
        const { roadAddrPart1, roadAddrPart2, addrDetail } = event.data.data;

        // roadAddrPart1과 roadAddrPart2를 조합하여 기본 주소 생성
        let combinedAddress = roadAddrPart1;
        if (roadAddrPart2 && roadAddrPart2.trim() !== '') {
          combinedAddress += ' ' + roadAddrPart2;
        }

        handleAddressCallback(combinedAddress, addrDetail);
      }
    };

    window.addEventListener('message', handleMessage);

    return () => {
      delete window.jusoCallBack;
      window.removeEventListener('message', handleMessage);
    };
  }, []);

  // 할인율, 할인 금액이 바뀔 때마다 자동으로 견적 계산
  // useEffect(() => {
  //   if (
  //     discountRates.homeWindow !== undefined &&
  //     discountRates.LX하우시스 !== undefined &&
  //     discountRates.KCC홈CC !== undefined &&
  //     (discountData.homeWindow !== undefined ||
  //       discountData.LX하우시스 !== undefined ||
  //       discountData.KCC홈CC !== undefined)
  //   ) {
  //     calculateEstimate();
  //   }
  //   // eslint-disable-next-line
  // }, [discountRates, discountData]);

  // 브랜드명 → 이미지로 변환하는 함수
  /* const getBrandLogo = (brand, style = { width: 90, height: 32, objectFit: "contain", display: "block", margin: "0 auto" }) => {
    switch (brand) {
      case '홈윈도우':
        return <img src={homewindowLogo} alt="홈윈도우" style={style} />;
      case 'LX하우시스':
        return <img src={lxhausysLogo} alt="LX하우시스" style={style} />;
      case 'KCC홈CC':
        return <img src={kccLogo} alt="KCC홈씨씨" style={style} />;
      default:
        return brand;
    }
  };*/

  // 견적 자동계산 (입력값 변경 시 자동 반영)
  useEffect(() => {
    // 견적 불러오기 후에만 계산
    if (showLoadModal === false) {
      calculateEstimate();
    }
  }, [showLoadModal, calculateEstimate]);

  // 고객명/연락처 변경 시 견적번호 자동 재생성
  const prevCustomerInfoRef = useRef<{ customerName: string; phone: string } | null>(null);
  useEffect(() => {
    const currentCustomerName = estimateInfo.customerName?.trim() || '';
    const currentPhone = estimateInfo.phone?.trim() || '';

    // 초기 렌더링 시에는 이전 값만 저장하고 종료
    if (prevCustomerInfoRef.current === null) {
      prevCustomerInfoRef.current = { customerName: currentCustomerName, phone: currentPhone };
      return;
    }

    // 고객명과 연락처가 모두 있어야 견적번호 생성
    if (!currentCustomerName || !currentPhone) {
      prevCustomerInfoRef.current = { customerName: currentCustomerName, phone: currentPhone };
      return;
    }

    // 이전 값과 비교하여 변경된 경우에만 견적번호 재생성
    const prevInfo = prevCustomerInfoRef.current;
    if (prevInfo.customerName !== currentCustomerName || prevInfo.phone !== currentPhone) {
      generateEstimateNumber(currentCustomerName, currentPhone);
    }

    // 현재 값을 이전 값으로 저장
    prevCustomerInfoRef.current = { customerName: currentCustomerName, phone: currentPhone };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [estimateInfo.customerName, estimateInfo.phone]);

  // 입력값 변경 시 자동 계산 (중문&기타, 통바, 추가사항, 할인율 변경 시)
  // 주의: shasiData는 1708번 줄에서 직접 처리되므로 여기서는 제외
  useEffect(() => {
    calculateEstimate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    otherData,
    tongbaData,
    additionalData,
    discountRates,
    // shasiData는 제외 - 1708번 줄에서 직접 처리
    // calculateEstimate를 의존성에서 제거 - 무한 루프 방지
  ]);

  // 시공유무 변경 시 중문&기타, 통바, 추가사항 가격 재계산
  useEffect(() => {
    const installationType = estimateInfo.constructionStatus;

    // 중문&기타 가격 재계산
    const newOtherData = otherData.map((row) => {
      if (row.item && row.item !== '-') {
        const quantity = parseFloat(row.quantity) || 0;
        let basePrice = 0;

        // DB에서 가격 정보 조회 (시공/제품납품에 따라)
        if (itemPrices['중문&기타'] && itemPrices['중문&기타'][row.item]) {
          if (itemPrices['중문&기타'][row.item][installationType]) {
            basePrice = itemPrices['중문&기타'][row.item][installationType];
          } else if (itemPrices['중문&기타'][row.item]['공통']) {
            basePrice = itemPrices['중문&기타'][row.item]['공통'];
          }
        }

        return {
          ...row,
          price: Math.round((basePrice * quantity) / 1000) * 1000
        };
      }
      return row;
    });

    // 통바 가격 재계산
    const newTongbaData = tongbaData.map((row) => {
      if (row.item && row.item !== '-') {
        const specValue = parseFloat(row.spec) || 0;
        const quantity = parseFloat(row.quantity) || 1;
        let basePrice = 0;

        // DB에서 가격 정보 조회 (시공/제품납품에 따라)
        if (itemPrices['통바'] && itemPrices['통바'][row.item]) {
          if (itemPrices['통바'][row.item][installationType]) {
            basePrice = itemPrices['통바'][row.item][installationType];
          } else if (itemPrices['통바'][row.item]['공통']) {
            basePrice = itemPrices['통바'][row.item]['공통'];
          }
        }

        if (specValue > 0) {
          // ✅ 버그 수정: multiplier 계산 추가 (updateTongbaData와 동일한 로직)
          const multiplier = Math.ceil(specValue / 1000);
          const totalPrice = basePrice * quantity * multiplier;
          return {
            ...row,
            price: Math.round(totalPrice / 1000) * 1000
          };
        }
      }
      return row;
    });

    // 추가사항 가격 재계산
    const newAdditionalData = additionalData.map((row) => {
      if (row.item && row.item !== '-' && row.item !== '직접입력' && row.item !== '직접입력(고객 비공개용)' && row.item !== '비공개 금액_선택' && row.item !== '비공개 금액_주택' && row.item !== '비공개 금액_빌라') {
        const quantity = parseFloat(row.quantity) || 1;
        let unitPrice = 0;

        // DB에서 가격 정보 조회 (시공/제품납품에 따라)
        if (itemPrices['추가사항'] && itemPrices['추가사항'][row.item]) {
          if (itemPrices['추가사항'][row.item][installationType]) {
            unitPrice = itemPrices['추가사항'][row.item][installationType];
          } else if (itemPrices['추가사항'][row.item]['공통']) {
            unitPrice = itemPrices['추가사항'][row.item]['공통'];
          }
        }

        const totalPrice = unitPrice * quantity;
        return {
          ...row,
          price: Math.round(totalPrice / 1000) * 1000
        };
      }
      return row;
    });

    // setState 호출
    setOtherData(newOtherData);
    setTongbaData(newTongbaData);
    setAdditionalData(newAdditionalData);

    // ✅ 즉시 calculateEstimate 호출 - 최신 데이터 직접 전달
    calculateEstimate(shasiData, newOtherData, newTongbaData, newAdditionalData, discountRates);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [estimateInfo.constructionStatus, itemPrices]);

  // capture-mode 여부를 DOM에서 직접 체크하는 함수
  const isReallyCaptureMode = () => {
    if (typeof window === 'undefined') return false;
    const el = document.getElementById('estimate-form');
    return el && el.classList.contains('capture-mode');
  };

  // capture-mode 여부를 체크하는 상태
  const [isCaptureMode, setIsCaptureMode] = useState(false);

  // 브랜드별 스펙 – LX하우시스 제작 옵션 상태
  const [lxProductionOption, setLxProductionOption] = useState('LX스탠다드');
  const [kccBrandOption, setKccBrandOption] = useState('KCC홈씨씨');
  
  // 자동핸들 상태 (브랜드별)
  const [lxAutoHandleScope, setLxAutoHandleScope] = useState('발코니창 무상적용');
  const [hwAutoHandleScope, setHwAutoHandleScope] = useState('발코니창 무상적용');
  const [kccAutoHandleScope, setKccAutoHandleScope] = useState('발코니창 무상적용');
  
  // 공틀 분합문 상태
  const [lxCombinedFrameOptions, setLxCombinedFrameOptions] = useState({
    '2W 양방향커플(주사용)+자동(반대창)': true,
    '3W 양방향커플(1번창)+양방향커플(3번창)': true
  });
  const [hwCombinedFrameOptions, setHwCombinedFrameOptions] = useState({
    '반자동핸들(무상)': true
  });
  const [kccCombinedFrameOptions, setKccCombinedFrameOptions] = useState({
    '반자동핸들(무상)': true
  });
  
  // 특별혜택 상태 (브랜드별)
  const [lxSpecialBenefitType, setLxSpecialBenefitType] = useState('안전방충망');
  const [hwSpecialBenefitType, setHwSpecialBenefitType] = useState('안전방충망');
  const [kccSpecialBenefitType, setKccSpecialBenefitType] = useState('-');
  
  // 방충망 옵션 상태 (브랜드별)
  const [lxScreenOption, setLxScreenOption] = useState('안전방충망');
  const [hwScreenOption, setHwScreenOption] = useState('안전방충망');
  
  // 특이사항 상태
  const [specialNotes, setSpecialNotes] = useState('청암홈윈도우의 모든 혜택사항은 무상 업그레이드 적용이며, 환급 대상에는 포함되지 않습니다.');

  useEffect(() => {
    const element = document.getElementById('estimate-form');
    if (!element) return;
    const observer = new MutationObserver(() => {
      setIsCaptureMode(element.classList.contains('capture-mode'));
    });
    observer.observe(element, { attributes: true, attributeFilter: ['class'] });
    setIsCaptureMode(element.classList.contains('capture-mode'));
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const banner = document.querySelector(
      '.top-capture-banner'
    ) as HTMLElement | null;
    const container = document.getElementById(
      'estimate-form'
    ) as HTMLElement | null;
  }, [isCaptureMode]);




  if (!isUnitPricesLoaded || !isItemsLoaded) {
    return (
      <div className="loading-container">
        <div>데이터를 불러오는 중입니다...</div>
      </div>
    );
  }

  return (
    <div id="estimate-form" className="container" style={{ padding: '10px 40px 40px 40px' }}>
      <div className="estimate-header">
        <div style={{ width: '100%' }}>
          <div style={{ textAlign: 'left' }}>
            <div style={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center',
              marginBottom: '10px'
            }}>
              <img
                src={homewindowLogo}
                alt="Home Window"
                className="estimate-logoImg logo-clickable"
                style={{ width: '200px', marginBottom: '0' }}
                onClick={() => navigate('/')}
              />
              
              {/* 오른쪽 버튼들 - 아이콘 + 텍스트 + 구분선 */}
              <div className="no-print header-buttons-container">
                {/* 오류 제보 버튼 */}
                <button
                  className="header-icon-text-button"
                  onClick={() => {
                    if (userId) {
                      localStorage.setItem(`lastErrorBoardCheck_${userId}`, new Date().toISOString());
                    }
                    setHasNewErrorPosts(false);
                    navigate('/error-board');
                  }}
                  title="오류 제보">
                  <img
                    src={hasNewErrorPosts ? errorBoardNewIcon : errorBoardIcon}
                    alt="오류 제보"
                  />
                  <span>오류 제보</span>
                </button>
                <div className="no-print hide-on-capture header-button-divider" />

                {/* 단가 관리 버튼 - role_id 1, 2만 표시 */}
                {PermissionManager.canAccessPriceManagement() && (
                  <>
                    <button
                      className="header-icon-text-button"
                      onClick={() => navigate('/admin')}
                      title="단가 관리">
                      <Settings size={20} />
                      <span>단가 관리</span>
                    </button>
                    <div className="no-print hide-on-capture header-button-divider" />
                  </>
                )}

                {/* 게시판 버튼 */}
                {PermissionManager.canAccessBoard() && (
                  <>
                    <button
                      className="header-icon-text-button"
                      onClick={() => {
                        // 사용자별로 현재 시간을 로컬스토리지에 저장
                        if (userId) {
                          localStorage.setItem(`lastBoardCheck_${userId}`, new Date().toISOString());
                        }
                        setHasNewPosts(false);
                        navigate('/board');
                      }}
                      title="게시판">
                      <img
                        src={hasNewPosts ? boardNewIcon : boardIcon}
                        alt="게시판"
                      />
                      <span>게시판</span>
                    </button>
                    <div className="no-print hide-on-capture header-button-divider" />
                  </>
                )}

                {/* 프린트 버튼 - 관리자, 지원팀, 상담팀만 표시 */}
                {PermissionManager.canPrint() && (
                  <>
                    <button
                      className="header-icon-text-button"
                      onClick={() => {
                        const printContent = document.getElementById('estimate-form');
                        if (!printContent) return;

                        // DOM 복제 후 select를 텍스트로 변환
                        const clonedContent = printContent.cloneNode(true) as HTMLElement;

                        // 원본의 select 값을 복제본에 텍스트로 반영
                        const originalSelects = printContent.querySelectorAll('select');
                        const clonedSelects = clonedContent.querySelectorAll('select');

                        clonedSelects.forEach((clonedSelect, index) => {
                          const originalSelect = originalSelects[index] as HTMLSelectElement;
                          if (originalSelect) {
                            const selectedText = originalSelect.options[originalSelect.selectedIndex]?.text || '';
                            const selectedValue = originalSelect.value || '';
                            // placeholder나 기본값인 경우 공백으로 처리
                            const placeholderValues = ['-', '연도', '월', '일', '선택', '선택하세요', ''];
                            const isPlaceholder = placeholderValues.includes(selectedText) ||
                                                  placeholderValues.includes(selectedValue) ||
                                                  selectedText.includes('선택');
                            const span = document.createElement('span');
                            span.textContent = isPlaceholder ? '' : selectedText;
                            span.style.cssText = 'display:inline-block;width:100%;text-align:center;font-size:inherit;';
                            clonedSelect.parentNode?.replaceChild(span, clonedSelect);
                          }
                        });

                        // 원본의 input 값을 복제본에 반영
                        const originalInputs = printContent.querySelectorAll('input');
                        const clonedInputs = clonedContent.querySelectorAll('input');

                        clonedInputs.forEach((clonedInput, index) => {
                          const originalInput = originalInputs[index] as HTMLInputElement;
                          if (originalInput) {
                            (clonedInput as HTMLInputElement).value = originalInput.value;
                            clonedInput.setAttribute('value', originalInput.value);
                          }
                        });

                        // 원본의 textarea 값을 복제본에 반영
                        const originalTextareas = printContent.querySelectorAll('textarea');
                        const clonedTextareas = clonedContent.querySelectorAll('textarea');

                        clonedTextareas.forEach((clonedTextarea, index) => {
                          const originalTextarea = originalTextareas[index] as HTMLTextAreaElement;
                          if (originalTextarea) {
                            (clonedTextarea as HTMLTextAreaElement).textContent = originalTextarea.value;
                          }
                        });

                        // 견적비고만 표시, 고객센터 제거
                        const notesContainer = clonedContent.querySelector('.estimate-notes-container') as HTMLElement;
                        if (notesContainer) {
                          notesContainer.removeAttribute('style');
                          notesContainer.style.cssText = 'display:block;margin-top:10px;';
                        }
                        const notesLeft = clonedContent.querySelector('.estimate-notes-left') as HTMLElement;
                        if (notesLeft) {
                          notesLeft.removeAttribute('style');
                          notesLeft.style.cssText = 'width:100%;';
                        }
                        // 고객센터 섹션 완전히 제거
                        const notesRight = clonedContent.querySelector('.estimate-notes-right') as HTMLElement;
                        if (notesRight) {
                          notesRight.remove();
                        }

                        // 할인비율 테이블: input을 값만 있는 span으로 변환
                        const discountTable = clonedContent.querySelector('.discount-table');
                        if (discountTable) {
                          const discountTds = discountTable.querySelectorAll('td');
                          discountTds.forEach((td) => {
                            const input = td.querySelector('input') as HTMLInputElement;
                            const label = td.querySelector('.discount-label');
                            if (input && label) {
                              const value = input.value || '0';
                              // input과 label을 하나의 텍스트로 합침
                              td.innerHTML = `<span style="white-space:nowrap;">${value}% 할인</span>`;
                            }
                          });
                        }

                        // placeholder 제거 (인쇄 시 표시 방지)
                        const allInputs = clonedContent.querySelectorAll('input, textarea');
                        allInputs.forEach((el) => {
                          el.removeAttribute('placeholder');
                        });

                        // date input이 비어있으면 텍스트로 변환 (빈 값)
                        const dateInputs = clonedContent.querySelectorAll('input[type="date"]');
                        dateInputs.forEach((dateInput, index) => {
                          const originalDateInputs = printContent.querySelectorAll('input[type="date"]');
                          const originalInput = originalDateInputs[index] as HTMLInputElement;
                          const value = originalInput?.value || '';
                          const span = document.createElement('span');
                          span.textContent = value ? value : '';
                          span.style.cssText = 'display:inline-block;width:100%;text-align:center;font-size:inherit;';
                          dateInput.parentNode?.replaceChild(span, dateInput);
                        });

                        // 규격 셀에서 값이 없으면 X도 숨김
                        const allTds = clonedContent.querySelectorAll('td');
                        allTds.forEach((td) => {
                          const divs = td.querySelectorAll('div');
                          divs.forEach((div) => {
                            const inputs = div.querySelectorAll('input');
                            const spans = div.querySelectorAll('span');
                            // 2개의 input과 X span이 있는 규격 셀인지 확인
                            if (inputs.length === 2) {
                              const input1 = inputs[0] as HTMLInputElement;
                              const input2 = inputs[1] as HTMLInputElement;
                              const val1 = input1.value || input1.getAttribute('value') || '';
                              const val2 = input2.value || input2.getAttribute('value') || '';
                              // 둘 다 비어있으면 전체 숨김
                              if (!val1.trim() && !val2.trim()) {
                                div.innerHTML = '';
                              }
                            }
                          });
                        });

                        const printWindow = window.open('', '_blank');
                        if (!printWindow) {
                          alert('팝업이 차단되었습니다. 팝업 차단을 해제해주세요.');
                          return;
                        }

                        // A4 크기 (1페이지 기준)
                        const A4_HEIGHT_1PAGE = 1047;
                        const A4_HEIGHT_2PAGE = 1047 * 2;
                        const A4_WIDTH_PX = 718;

                        printWindow.document.write(`
                          <!DOCTYPE html>
                          <html>
                          <head>
                            <title>견적서 인쇄</title>
                            <style>
                              @page {
                                size: A4;
                                margin: 5mm;
                              }
                              * {
                                box-sizing: border-box;
                              }
                              html, body {
                                width: 100%;
                                margin: 0;
                                padding: 0;
                                overflow: visible;
                              }
                              body {
                                font-family: 'Malgun Gothic', '맑은 고딕', sans-serif;
                                font-size: 11px;
                                line-height: 1.3;
                                color: #333;
                              }
                              #page-container {
                                page-break-inside: avoid;
                                break-inside: avoid;
                                margin: 0 auto;
                              }
                              #print-wrapper {
                                width: 200mm;
                                transform-origin: top left;
                              }
                              .container {
                                width: 100%;
                                padding: 3mm;
                              }

                              /* ===== 숨김 요소 ===== */
                              .main-title, .version-link, .subtitle,
                              #brand-spec-section,
                              .no-print, .hide-on-capture, .fixed-footer,
                              .header-buttons-container,
                              .add-row-btn, .text-right,
                              .top-capture-banner, .auto-update-message {
                                display: none !important;
                              }

                              /* 삭제 버튼이 있는 테이블의 마지막 열만 숨김 (할인비율 테이블 제외) */
                              #shasiTable tr th:last-child,
                              #shasiTable tr td:last-child,
                              #otherTable tr th:last-child,
                              #otherTable tr td:last-child,
                              #tongbaTable tr th:last-child,
                              #tongbaTable tr td:last-child,
                              #additionalTable tr th:last-child,
                              #additionalTable tr td:last-child {
                                display: none !important;
                              }

                              /* 테이블 내 버튼만 숨김 */
                              table button {
                                display: none !important;
                              }

                              /* ===== 견적정보 & 시공정보 레이아웃 ===== */
                              .estimate-info-container {
                                display: flex !important;
                                flex-direction: row !important;
                                gap: 15px;
                                margin-bottom: 8px;
                              }
                              .estimate-info-container > div:first-child {
                                flex: 0 0 62% !important;
                              }
                              .estimate-info-container > div:last-child {
                                flex: 0 0 35% !important;
                              }

                              /* ===== 테이블 기본 스타일 ===== */
                              table {
                                width: 100%;
                                border-collapse: collapse;
                                margin-bottom: 8px;
                                table-layout: fixed;
                              }
                              .table2 {
                                width: 100% !important;
                              }
                              th, td {
                                border: 1px solid #ccc;
                                padding: 4px 3px;
                                text-align: center;
                                vertical-align: middle;
                                font-size: 10px;
                                word-wrap: break-word;
                              }
                              th {
                                background-color: #e8e8e8;
                                font-weight: bold;
                              }

                              /* ===== Input/Select 스타일 ===== */
                              input, select, textarea {
                                border: none !important;
                                background: transparent !important;
                                width: 100%;
                                text-align: center;
                                font-size: 10px;
                                font-family: inherit;
                                padding: 2px;
                                -webkit-appearance: none;
                                -moz-appearance: none;
                                appearance: none;
                              }
                              select {
                                text-align-last: center;
                              }
                              textarea {
                                text-align: left;
                                min-height: 50px;
                                resize: none;
                              }

                              /* ===== 섹션 타이틀 ===== */
                              .section-title {
                                font-size: 12px;
                                font-weight: bold;
                                margin: 10px 0 5px 0;
                                padding: 4px 8px;
                                background-color: #f0f0f0;
                                border-left: 3px solid #333;
                              }
                              .section-title-main {
                                background-color: #d9d9d9;
                                margin-top: 12px;
                              }

                              /* ===== 견적합계 카드 ===== */
                              .estimate-summary-cards {
                                display: flex !important;
                                flex-direction: row !important;
                                justify-content: space-between;
                                gap: 10px;
                                margin: 10px 0;
                              }
                              .summary-card {
                                flex: 1;
                                border-radius: 6px;
                                overflow: hidden;
                                border: 2px solid #ccc;
                              }
                              .summary-card-inactive {
                                opacity: 0.4;
                              }
                              .summary-card-header {
                                padding: 8px;
                                display: flex;
                                justify-content: center;
                                align-items: center;
                              }
                              .summary-card-logo {
                                height: 20px;
                                width: auto;
                              }
                              .summary-card-logo-homeWindow {
                                height: 16px;
                              }
                              .summary-card-content {
                                padding: 8px;
                                background: #fff;
                              }
                              .summary-card-content div,
                              .summary-card-content span {
                                font-size: 10px !important;
                              }
                              .price-row, .price-row-no-margin, .price-row-total {
                                display: flex;
                                justify-content: space-between;
                                padding: 3px 5px;
                              }
                              .price-value-large {
                                font-size: 13px !important;
                                font-weight: bold;
                              }
                              .discount-badge {
                                text-align: center;
                                font-size: 9px;
                                padding: 3px;
                                background: #f5f5f5;
                                margin: 4px 0;
                                border-radius: 3px;
                              }
                              .discount-badge-arrow {
                                display: none;
                              }
                              .discount-value {
                                color: #dc3545;
                              }
                              hr.divider {
                                margin: 5px 0;
                                border: none;
                                border-top: 1px solid #ddd;
                              }

                              /* ===== 할인비율 테이블 ===== */
                              .discount-table {
                                width: 100% !important;
                              }
                              .discount-table th,
                              .discount-table td {
                                width: 33.33% !important;
                              }
                              .discount-table td {
                                text-align: center !important;
                                white-space: nowrap !important;
                              }
                              .discount-table td input[type="number"] {
                                width: 25px !important;
                                min-width: 25px !important;
                                max-width: 25px !important;
                                margin-right: 1px !important;
                                padding: 0 !important;
                                display: inline-block !important;
                                text-align: right !important;
                              }
                              .discount-table td .discount-label {
                                margin-left: 0 !important;
                                padding-left: 0 !important;
                                display: inline !important;
                              }

                              /* ===== 견적비고 & 고객센터 레이아웃 ===== */
                              .estimate-notes-container {
                                display: flex !important;
                                flex-direction: row !important;
                                justify-content: flex-start !important;
                                gap: 10px !important;
                                margin-top: 10px !important;
                              }
                              .estimate-notes-container > .estimate-notes-left {
                                flex: 1.5 !important;
                                margin-right: 0 !important;
                              }
                              .estimate-notes-container > .estimate-notes-right {
                                flex: 1 !important;
                                margin-left: 0 !important;
                              }

                              /* 인라인 스타일 오버라이드 - 견적비고/고객센터 제외 */
                              .estimate-info-container[style*="justify-content"] {
                                justify-content: space-between !important;
                              }

                              /* 고객센터 전화번호 크기 조정 */
                              [style*="font-size: 48px"] {
                                font-size: 28px !important;
                              }
                              [style*="font-size: 18px"] {
                                font-size: 12px !important;
                              }
                              [style*="font-size: 16px"] {
                                font-size: 11px !important;
                              }
                              [style*="font-size: 14px"] {
                                font-size: 10px !important;
                              }

                              /* ===== 인쇄 색상 보정 ===== */
                              @media print {
                                * {
                                  -webkit-print-color-adjust: exact !important;
                                  print-color-adjust: exact !important;
                                }
                              }
                            </style>
                          </head>
                          <body>
                            <div id="page-container"></div>
                            <div id="print-wrapper" style="position:absolute;left:-9999px;">
                              ${clonedContent.innerHTML}
                            </div>
                            <script>
                              window.onload = function() {
                                const wrapper = document.getElementById('print-wrapper');
                                const container = document.getElementById('page-container');
                                const contentHeight = wrapper.scrollHeight;
                                const contentWidth = wrapper.scrollWidth;
                                const maxWidth = ${A4_WIDTH_PX};
                                const onePageHeight = ${A4_HEIGHT_1PAGE};
                                const twoPageHeight = ${A4_HEIGHT_2PAGE};

                                // 높이 기준으로 scale 계산 (가로는 A4에 맞춤)
                                const scaleFor1Page = onePageHeight / contentHeight;
                                const scaleFor2Page = twoPageHeight / contentHeight;

                                let scaleY;
                                let pageMode;
                                let targetHeight;

                                // 1페이지에 맞추기 위한 scale이 0.55 이상이면 1페이지로 축소
                                if (scaleFor1Page >= 0.55) {
                                  scaleY = scaleFor1Page;
                                  pageMode = '1페이지';
                                  targetHeight = onePageHeight;
                                } else {
                                  // 2페이지에 꽉 차게
                                  scaleY = scaleFor2Page;
                                  pageMode = '2페이지';
                                  targetHeight = twoPageHeight;
                                }

                                // 가로는 A4 너비에 맞춤
                                const scaleX = maxWidth / contentWidth;

                                // 컨테이너를 A4 너비와 목표 높이로 설정
                                container.style.width = maxWidth + 'px';
                                container.style.height = targetHeight + 'px';
                                container.style.overflow = 'hidden';
                                container.style.position = 'relative';

                                // wrapper를 container 안으로 이동하고 가로/세로 각각 스케일 적용
                                wrapper.style.position = 'absolute';
                                wrapper.style.left = '0';
                                wrapper.style.top = '0';
                                wrapper.style.transform = 'scale(' + scaleX + ', ' + scaleY + ')';
                                wrapper.style.transformOrigin = 'top left';
                                container.appendChild(wrapper);
                              };
                            <\/script>
                          </body>
                          </html>
                        `);

                        printWindow.document.close();

                        setTimeout(() => {
                          printWindow.focus();
                          printWindow.print();
                          printWindow.close();
                        }, 800);
                      }}
                      title="인쇄">
                      <img
                        src={printIcon}
                        alt="인쇄"
                      />
                      <span>인쇄</span>
                    </button>
                    <div className="no-print hide-on-capture header-button-divider" />
                  </>
                )}

                {/* 로그아웃 버튼 */}
                <button
                  className="header-icon-text-button"
                  onClick={handleLogout}
                  title="로그아웃">
                  <img
                    src={logoutIcon}
                    alt="로그아웃"
                  />
                  <span>로그아웃</span>
                </button>
              </div>
            </div>
          </div>
          
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-end',
            }}>
            <div style={{ display: 'flex', alignItems: 'center', width: '100%', justifyContent: 'space-between' }}>
              <div>
                <div className="main-title">청암홈윈도우 견적서</div>
                <div
                  className="version-link"
                  onClick={() => navigate('/proposal-board')}>
                  Ver {latestVersion || '3.4.0'}
                </div>
                <div className="subtitle">
                  {isRefreshingPrices && (
                    <div
                      className="auto-update-message"
                      style={{
                        color: '#28a745',
                        fontSize: '12px',
                        fontWeight: 'bold',
                        marginTop: '5px',
                      }}>
                      🔄 단가 데이터 자동 업데이트 중...
                    </div>
                  )}
                </div>
              </div>

              {/* <div className="header-right-flex">
                {PermissionManager.canAccessAdminPage() && (
                  <button
                    className="estimate-adminBtn"
                    onClick={() => navigate('/admin')}
                    style={{
                      padding: '10px 20px',
                      backgroundColor: 'white',
                      color: 'black',
                      border: '1px solid #ccc',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      fontSize: '14px',
                      fontWeight: 'bold',
                    }}>
                    관리자 페이지
                  </button>
                )}
                {PermissionManager.getEstimateLoadScope() !==
                  'NONE' && (
                  <button
                    className="estimate-logoutBtn"
                    onClick={openLoadModal}>
                    견적 불러오기
                  </button>
                )}
              </div> */}
            </div>
          </div>
        </div>
      </div>
      {/* 견적정보 섹션 + 상단 문구를 flex로 배치 */}
      <div>
        {/* 견적정보 타이틀+상단 문구+버튼 (한 줄 flex, 표는 flex 바깥) */}
        {/* 견적정보와 시공정보를 나란히 배치 */}
        <div className="estimate-info-container">
          {/* 견적정보 테이블 */}
          <div style={{ width: '60%' }}>
            <div className="section-title section-title-no-margin">견적정보</div>
            <table className="table2" style={{ width: '100%', marginBottom: '0' }}>
              <tbody>
                <tr>
                  <th style={{ width: '35%' }}>견적번호</th>
                  <th style={{ width: '23%' }}>견적일자</th>
                  <th style={{ width: '15%' }}>견적자명</th>
                  <th style={{ width: '27%' }}>견적유형</th>
                </tr>
                 <tr>
                   <td>
                     <input
                       type="text"
                       value={estimateInfo.estimateNumber}
                       readOnly
                       className="bg-light-gray"
                     />
                   </td>
                   <td>
                     <input
                       type="text"
                       value={estimateInfo.estimateDate.replace(/-/g, '.')}
                       readOnly
                       className="bg-light-gray"
                     />
                   </td>
                   <td className="position-relative">
                     <input
                       type="text"
                       value={estimateInfo.estimator}
                       onChange={(e) =>
                         setEstimateInfo((prev) => ({
                           ...prev,
                           estimator: e.target.value,
                         }))
                       }
                       readOnly
                       className="bg-gray"
                     />
                   </td>
                   <td>
                     <select
                       value={estimateInfo.estimateType}
                       onChange={(e) =>
                         setEstimateInfo((prev) => ({
                           ...prev,
                           estimateType: e.target.value,
                         }))
                       }
                       style={{ width: '100%', height: '100%' }}
                     >
                       <option value="-">-</option>
                       <option value="현장실측 견적">현장실측 견적</option>
                       <option value="비대면 가견적">비대면 가견적</option>
                       <option value="비대면 현장실측">비대면 현장실측</option>
                     </select>
                   </td>
                 </tr>
               </tbody>
            </table>
          </div>

          {/* 시공정보 테이블 */}
          <div style={{ width: '36%', marginLeft: 'auto' }}>
            <div className="section-title section-title-no-margin">시공정보</div>
            <table className="table2" style={{ width: '100%', marginBottom: '0' }}>
              <tbody>
                <tr>
                  <th style={{ width: '35%' }}>시공유무</th>
                  <th style={{ width: '65%' }}>시공희망일</th>
                </tr>
                 <tr>
                   <td>
                     <select
                       value={estimateInfo.constructionStatus}
                       onChange={(e) =>
                         setEstimateInfo((prev) => ({
                           ...prev,
                           constructionStatus: e.target.value,
                         }))
                       }
                       style={{ width: '80px' }}>
                       <option value="시공">시공</option>
                       <option value="제품납품">제품납품</option>
                     </select>
                   </td>
                   <td>
                     <input
                       type="date"
                       value={estimateInfo.scheduledDate}
                       onChange={(e) =>
                         setEstimateInfo((prev) => ({
                           ...prev,
                           scheduledDate: e.target.value,
                         }))
                       }
                       onFocus={(e) => {
                         try {
                           if (e.target.showPicker && e.isTrusted) {
                             e.target.showPicker();
                           }
                         } catch (error) {
                           // showPicker 에러 무시
                         }
                       }}
                     />
                   </td>
                 </tr>
               </tbody>
             </table>
           </div>
         </div>

        {/* 고객정보 섹션 */}
        <div className="section-title section-title-no-margin">고객정보</div>
        {/* 고객정보 테이블 */}
        <table className="table table-no-margin" style={{ marginBottom: '20px' }}>
          <tbody>
            <tr>
              <th style={{ width: '10%' }}>고객명</th>
              <th style={{ width: '15%' }}>연락처</th>
              <th style={{ width: '75%' }}>주소</th>
            </tr>
            <tr>
              <td>
                <input
                  type="text"
                  value={estimateInfo.customerName}
                  onChange={(e) =>
                    setEstimateInfo((prev) => ({
                      ...prev,
                      customerName: e.target.value,
                    }))
                  }
                  onKeyDown={handleEnterKey}
                />
              </td>
              <td>
                <input
                  id="phone"
                  type="tel"
                  value={estimateInfo.phone}
                  onChange={(e) =>
                    setEstimateInfo((prev) => ({
                      ...prev,
                      phone: formatPhoneNumber(e.target.value),
                    }))
                  }
                  placeholder="010-0000-0000"
                  maxLength={13}
                  onKeyDown={handleEnterKey}
                />
              </td>
              <td>
                <div className="address-row-flex">
                  <input
                    type="text"
                    value={estimateInfo.address}
                    onClick={openJusoPopup}
                    className="address-input-container"
                    placeholder="주소검색"
                    readOnly
                  />
                  <input
                    type="text"
                    value={estimateInfo.detailedAddress}
                    onChange={(e) =>
                      setEstimateInfo((prev) => ({
                        ...prev,
                        detailedAddress: e.target.value,
                      }))
                    }
                    className="address-button-container"
                    placeholder="상세주소 입력"
                    onKeyDown={handleEnterKey}
                  />
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* 견적합계 섹션 */}
      <PricingSummaryCards
        checkboxes={checkboxes}
        setCheckboxes={setCheckboxes}
        selectedGrades={selectedGrades}
        discountRates={discountRates}
        totals={totals}
        formatNumber={formatNumber}
      />

      {/* 샷시 & 망 & 랩핑 섹션 */}
      <div className="section-title section-title-main shasi">
        샷시 & 망 & 랩핑
      </div>

      {/* 샷시 테이블 */}
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleShasiDragEnd}
      >
        <table className="table" id="shasiTable">
          <tbody>
            <tr>
              <th className="col-1">NO</th>
              <th className="col-10">설치위치</th>
              <th className="col-20">품명</th>
              <th className="col-15">규격(W x H)</th>
              <th className="col-5">수량</th>
              <th className="col-10">LX하우시스</th>
              <th className="col-10">홈윈도우</th>
              <th className="col-10">KCC홈씨씨</th>
              <th className="col-10">비고</th>
            </tr>
            <SortableContext
              items={shasiData.map(item => item.id)}
              strategy={verticalListSortingStrategy}
            >
              {shasiData.map((row, index) => (
                <SortableRow key={row.id} id={row.id}>
                  <td>{index + 1}</td>
              <td>
                <input
                  type="text"
                  value={row.installLocation}
                  onChange={(e) =>
                    updateShasiData(row.id, 'installLocation', e.target.value)
                  }
                  onKeyDown={handleEnterKey}
                />
              </td>
              <td>
                <select
                  className="shasi-item-select"
                  value={row.item}
                  onChange={(e) =>
                    updateShasiData(row.id, 'item', e.target.value)
                  }
                  onKeyDown={handleEnterKey}>
                  <option value="-">-</option>
                  {(() => {
                    const categories = categorizeItems(availableItems);
                    const elements: React.ReactElement[] = [];

                    // 발코니 카테고리
                    if (categories.발코니.length > 0) {
                      elements.push(
                        <optgroup key="발코니" label="─ 발코니 ─">
                          {categories.발코니.map((item) => (
                            <option key={item} value={item} className="external-option">
                              {item}
                            </option>
                          ))}
                        </optgroup>
                      );
                    }

                    // 일반 카테고리
                    if (categories.일반.length > 0) {
                      elements.push(
                        <optgroup key="일반" label="─ 일반 ─">
                          {categories.일반.map((item) => (
                            <option key={item} value={item}>
                              {item}
                            </option>
                          ))}
                        </optgroup>
                      );
                    }

                    // 공틀 카테고리
                    if (categories.공틀.length > 0) {
                      elements.push(
                        <optgroup key="공틀" label="─ 공틀 ─">
                          {categories.공틀.map((item) => (
                            <option key={item} value={item}>
                              {item}
                            </option>
                          ))}
                        </optgroup>
                      );
                    }

                    // 기타 카테고리
                    if (categories.기타.length > 0) {
                      elements.push(
                        <optgroup key="기타" label="─ 기타 ─">
                          {categories.기타.map((item) => (
                            <option key={item} value={item}>
                              {item}
                            </option>
                          ))}
                          {/* 랩핑추가가 DB에서 누락될 수 있으므로 항상 포함 (이미 기타 카테고리에 포함되어 있으므로 제거) */}
                        </optgroup>
                      );
                    } else {
                      // 기타 카테고리가 비어있더라도 랩핑추가는 표시 (이미 기타 카테고리에 포함되어 있으므로 제거)
                    }

                    // 추가항목 카테고리 (새로 추가된 항목들)
                    if (categories.추가항목.length > 0) {
                      elements.push(
                        <optgroup key="추가항목" label="─ 추가 항목 ─">
                          {categories.추가항목.map((item) => (
                            <option key={item} value={item}>
                              {item}
                            </option>
                          ))}
                        </optgroup>
                      );
                    }

                    return elements;
                  })()}
                </select>
              </td>
              <td>
                <div style={{ display: 'flex', gap: 5 }}>
                  <input
                    type="text"
                    inputMode="numeric"
                    placeholder="W"
                    value={row.item === '랩핑추가' ? '' : row.width}
                    onChange={(e) =>
                      updateShasiData(row.id, 'width', e.target.value)
                    }
                    onBlur={(e) => 
                      validateShasiSize(row.id, 'width', e.target.value)
                    }
                    onKeyDown={handleEnterKey}
                    disabled={row.item === '랩핑추가'}
                    style={{
                      backgroundColor:
                        row.item === '랩핑추가' ? '#f0f0f0' : 'white',
                    }}
                    max="5400"
                    title="최대 5400까지 입력 가능합니다"
                  />
                  <span style={{ alignSelf: 'center' }}>X</span>
                  <input
                    type="text"
                    inputMode="numeric"
                    placeholder="H"
                    value={row.item === '랩핑추가' ? '' : row.height}
                    onChange={(e) =>
                      updateShasiData(row.id, 'height', e.target.value)
                    }
                    onBlur={(e) => 
                      validateShasiSize(row.id, 'height', e.target.value)
                    }
                    onKeyDown={handleEnterKey}
                    disabled={row.item === '랩핑추가'}
                    style={{
                      backgroundColor:
                        row.item === '랩핑추가' ? '#f0f0f0' : 'white',
                    }}
                    max="2510"
                    title="최대 2510까지 입력 가능합니다"
                  />
                </div>
              </td>
              <td>
                <input
                  type="text"
                  inputMode="numeric"
                  value={row.quantity}
                  onChange={(e) =>
                    updateShasiData(row.id, 'quantity', e.target.value)
                  }
                  onKeyDown={handleEnterKey}
                  style={{ textAlign: 'center' }}
                  placeholder="1"
                />
              </td>
              <td>
                <input
                  type="text"
                  value={formatNumber(row.lxHausysPrice)}
                  disabled
                  style={{ backgroundColor: 'rgb(255, 255, 246)' }}
                />
              </td>
              <td>
                <input
                  type="text"
                  value={formatNumber(row.homeWindowPrice)}
                  disabled
                  style={{ backgroundColor: 'rgb(255, 255, 246)' }}
                />
              </td>
              <td>
                <input
                  type="text"
                  value={formatNumber(row.kccHomeCCPrice)}
                  disabled
                  style={{ backgroundColor: 'rgb(255, 255, 246)' }}
                />
              </td>
              <td className="row-delete-cell">
                <input
                  type="text"
                  value={row.note}
                  onChange={(e) =>
                    updateShasiData(row.id, 'note', e.target.value)
                  }
                  onKeyDown={handleEnterKey}
                />
                {!isCaptureMode && (
                  <button
                    className="row-delete-overlay"
                    onClick={() => removeShasiRow(row.id)}
                    title="행 삭제"
                  >
                    &ndash;
                  </button>
                )}
              </td>
            </SortableRow>
              ))}
            </SortableContext>
          </tbody>
        </table>
      </DndContext>
      <div className="text-right">
        <button 
          className="add-row-btn" 
          onClick={copyShasiRow}
          style={{ 
            marginRight: '5px',
            minWidth: '45px',
            width: '45px'
          }}
        >
          복사
        </button>
        <button className="add-row-btn" onClick={addShasiRow}>
          + 추가
        </button>
      </div>

      {/* 중문 & 기타 섹션 */}
      {(!isCaptureMode || hasValidOtherData()) && (
        <>
          <div className="section-title section-title-main">중문 & 기타</div>
          <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleOtherDragEnd}
      >
        <table className="table" id="otherTable">
          <tbody>
            <tr>
              <th className="col-1">NO</th>
              <th className="col-8">설치위치</th>
              <th className="col-19">품명</th>
              <th className="col-15">규격(W x H)</th>
              <th className="col-7">색상</th>
              <th className="col-5">수량</th>
              <th className="col-17">금액</th>
              <th className="col-12">비고</th>
              <th className="col-1">+/-</th>
            </tr>
            <SortableContext
              items={otherData.map(item => item.id)}
              strategy={verticalListSortingStrategy}
            >
              {otherData.map((row, index) => (
                <SortableRow key={row.id} id={row.id}>
                  <td>{index + 1}</td>
              <td>
                <input
                  type="text"
                  value={row.installLocation}
                  onChange={(e) =>
                    updateOtherData(row.id, 'installLocation', e.target.value)
                  }
                  onKeyDown={handleEnterKey}
                />
              </td>
              <td>
                <select
                  value={row.item}
                  onChange={(e) =>
                    updateOtherData(row.id, 'item', e.target.value)
                  }
                  onKeyDown={handleEnterKey}>
                  <option value="-">-</option>
                  {otherItems.map((item) => (
                    <option key={item} value={item}>
                      {item}
                    </option>
                  ))}
                </select>
              </td>
              <td>
                <div className="button-container">
                  <input
                    type="text"
                    inputMode="numeric"
                    placeholder="W"
                    value={row.width}
                    onChange={(e) =>
                      updateOtherData(row.id, 'width', e.target.value)
                    }
                    onBlur={(e) =>
                      validateOtherSize(row.id, 'width', e.target.value)
                    }
                    onKeyDown={handleEnterKey}
                    max="5400"
                    title="최대 5400까지 입력 가능합니다"
                  />
                  <span className="button-text">X</span>
                  <input
                    type="text"
                    inputMode="numeric"
                    placeholder="H"
                    value={row.height}
                    onChange={(e) =>
                      updateOtherData(row.id, 'height', e.target.value)
                    }
                    onBlur={(e) =>
                      validateOtherSize(row.id, 'height', e.target.value)
                    }
                    onKeyDown={handleEnterKey}
                    max="2510"
                    title="최대 2510까지 입력 가능합니다"
                  />
                </div>
              </td>
              <td>
                <input
                  type="text"
                  value={row.color}
                  onChange={(e) =>
                    updateOtherData(row.id, 'color', e.target.value)
                  }
                  onKeyDown={handleEnterKey}
                />
              </td>
              <td>
                <input
                  type="text"
                  inputMode="numeric"
                  value={row.quantity}
                  onChange={(e) => {
                    const value = e.target.value.replace(/[^0-9]/g, '');
                    updateOtherData(row.id, 'quantity', value);
                  }}
                  onKeyDown={handleEnterKey}
                />
              </td>
              <td>
                <input
                  type="text"
                  value={formatNumber(row.price)}
                  disabled
                  className="bg-yellow"
                />
              </td>
              <td>
                <input
                  type="text"
                  value={row.note}
                  onChange={(e) =>
                    updateOtherData(row.id, 'note', e.target.value)
                  }
                  onKeyDown={handleEnterKey}
                />
              </td>
              <td>
                <button onClick={() => removeOtherRow(row.id)}>-</button>
              </td>
                </SortableRow>
              ))}
            </SortableContext>
          </tbody>
        </table>
      </DndContext>
          <div className="text-right">
            <button className="add-row-btn" onClick={addOtherRow}>
              + 추가
            </button>
          </div>
        </>
      )}

      {/* 통바 & 추가사항 섹션 */}
      <div className="flex-row">
        {(!isCaptureMode || hasValidTongbaData()) && (
          <div className="flex-column">
            <div className="section-title section-title-main">통바</div>
            <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleTongbaDragEnd}
          >
            <table className="table" id="tongbaTable">
              <tbody>
                <tr>
                  <th className="col-1">NO</th>
                  <th className="col-28">품명</th>
                  <th className="col-17">길이</th>
                  <th className="col-13">수량</th>
                  <th className="col-25">금액</th>
                  <th className="col-1">+/-</th>
                </tr>
                <SortableContext
                  items={tongbaData.map(item => item.id)}
                  strategy={verticalListSortingStrategy}
                >
                  {tongbaData.map((row, index) => (
                    <SortableRow key={row.id} id={row.id}>
                      <td>{index + 1}</td>
                  <td>
                    <select
                      value={row.item}
                      onChange={(e) =>
                        updateTongbaData(row.id, 'item', e.target.value)
                      }
                      onKeyDown={handleEnterKey}>
                      <option value="-">-</option>
                      {tongbaItems.map((item) => (
                        <option key={item} value={item}>
                          {item}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td>
                    <input
                      type="text"
                      value={row.spec}
                      onChange={(e) =>
                        updateTongbaData(row.id, 'spec', e.target.value)
                      }
                      onKeyDown={handleEnterKey}
                    />
                  </td>
                  <td>
                    <input
                      type="text"
                      inputMode="numeric"
                      value={row.quantity}
                      onChange={(e) => {
                        const value = e.target.value.replace(/[^0-9]/g, '');
                        updateTongbaData(row.id, 'quantity', value);
                      }}
                      onKeyDown={handleEnterKey}
                    />
                  </td>
                  <td>
                    <input
                      type="text"
                      value={formatNumber(row.price)}
                      disabled
                      className="bg-yellow"
                    />
                  </td>
                  <td>
                    <button onClick={() => removeTongbaRow(row.id)}>-</button>
                  </td>
                    </SortableRow>
                  ))}
                </SortableContext>
              </tbody>
            </table>
          </DndContext>
          <div className="text-right">
            <button className="add-row-btn" onClick={addTongbaRow}>
              + 추가
            </button>
          </div>
        </div>
        )}

        {/* 추가사항 섹션 */}
        {(!isCaptureMode || hasValidAdditionalData()) && (
          <div className="flex-column">
            <div className="section-title section-title-main">추가사항</div>
            <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleAdditionalDragEnd}
          >
            <table className="table" id="additionalTable">
              <tbody>
                <tr>
                  <th className="col-1">NO</th>
                  <th className="col-30">품명</th>
                  <th className="col-12">수량</th>
                  <th className="col-28">금액</th>
                  <th className="col-1">+/-</th>
                </tr>
                <SortableContext
                  items={additionalData.map(item => item.id)}
                  strategy={verticalListSortingStrategy}
                >
                  {additionalData.map((row, index) => (
                    <SortableRow key={row.id} id={row.id}>
                      <td>{index + 1}</td>
                  <td>
                    {/* 직접입력(고객 비공개용)은 읽기 전용으로 표시 (기존 저장 데이터 호환) */}
                    {row.item === '직접입력(고객 비공개용)' ? (
                      <input
                        type="text"
                        value="직접입력(고객 비공개용)"
                        disabled
                        style={{
                          width: '100%',
                          padding: '5px',
                          border: '1px solid #ddd',
                          borderRadius: '3px',
                          backgroundColor: '#dee2e6',
                          fontSize: '14px'
                        }}
                      />
                    ) : (
                    <select
                      value={row.item}
                      onChange={(e) =>
                        updateAdditionalData(row.id, 'item', e.target.value)
                      }
                      onKeyDown={handleEnterKey}>
                      <option value="-">-</option>
                      <optgroup label="─ 추가사항 ─">
                        {additionalItems.map((item) => (
                          <option key={item} value={item}>
                            {item}
                          </option>
                        ))}
                      </optgroup>
                      <optgroup label="─ 직접입력 ─">
                        <option value="직접입력">직접입력</option>
                      </optgroup>
                      <optgroup label="─ 비공개 항목 ─">
                        <option value="비공개 금액_선택">비공개 금액_선택</option>
                        <option value="비공개 금액_주택">비공개 금액_주택</option>
                        <option value="비공개 금액_빌라">비공개 금액_빌라</option>
                      </optgroup>
                    </select>
                    )}
                    {row.item === '직접입력' && (
                      <input
                        type="text"
                        placeholder="품명 및 수량 입력"
                        value={row.customItem || ''}
                        onChange={(e) =>
                          updateAdditionalData(
                            row.id,
                            'customItem',
                            e.target.value
                          )
                        }
                        onKeyDown={handleEnterKey}
                        style={{
                          marginTop: '5px',
                          width: '100%',
                          padding: '5px',
                          border: '1px solid #ddd',
                          borderRadius: '3px',
                          fontSize: '14px'
                        }}
                      />
                    )}
                    {row.item === '직접입력(고객 비공개용)' && (
                      <input
                        type="text"
                        placeholder="품명 및 수량 입력"
                        value={row.customItem || ''}
                        onChange={(e) =>
                          updateAdditionalData(
                            row.id,
                            'customItem',
                            e.target.value
                          )
                        }
                        onKeyDown={handleEnterKey}
                        style={{
                          marginTop: '5px',
                          width: '100%',
                          padding: '5px',
                          border: '1px solid #ddd',
                          borderRadius: '3px',
                          fontSize: '14px'
                        }}
                      />
                    )}
                    {row.item === '비공개 금액_선택' && (
                      <input
                        type="text"
                        placeholder="품명 및 수량 입력"
                        value={row.customItem || ''}
                        onChange={(e) =>
                          updateAdditionalData(
                            row.id,
                            'customItem',
                            e.target.value
                          )
                        }
                        onKeyDown={handleEnterKey}
                        style={{
                          marginTop: '5px',
                          width: '100%',
                          padding: '5px',
                          border: '1px solid #ddd',
                          borderRadius: '3px',
                          fontSize: '14px'
                        }}
                      />
                    )}
                  </td>
                  <td>
                    <input
                      type="text"
                      inputMode="numeric"
                      value={(row.item === '직접입력' || row.item === '직접입력(고객 비공개용)' || row.item === '비공개 금액_선택' || row.item === '비공개 금액_주택' || row.item === '비공개 금액_빌라') ? (row.quantity || '1') : row.quantity}
                      disabled={row.item === '직접입력' || row.item === '직접입력(고객 비공개용)' || row.item === '비공개 금액_선택' || row.item === '비공개 금액_주택' || row.item === '비공개 금액_빌라'}
                      onChange={(e) => {
                        if (!(row.item === '직접입력' || row.item === '직접입력(고객 비공개용)' || row.item === '비공개 금액_선택' || row.item === '비공개 금액_주택' || row.item === '비공개 금액_빌라')) {
                          const value = e.target.value.replace(/[^0-9]/g, '');
                          updateAdditionalData(row.id, 'quantity', value);
                        }
                      }}
                      onKeyDown={handleEnterKey}
                      style={{
                        backgroundColor: (row.item === '직접입력' || row.item === '직접입력(고객 비공개용)' || row.item === '비공개 금액_선택' || row.item === '비공개 금액_주택' || row.item === '비공개 금액_빌라') ? '#dee2e6' : 'white'
                      }}
                    />
                  </td>
                  <td>
                    {row.item === '비공개 금액_선택' ? (
                      <select
                        value={row.price || ''}
                        onChange={(e) => {
                          const value = parseInt(e.target.value) || 0;
                          updateAdditionalData(row.id, 'price', value);
                          updateAdditionalData(row.id, 'priceInput', e.target.value);
                        }}
                        style={{
                          width: '100%',
                          padding: '5px',
                          border: '1px solid #ddd',
                          borderRadius: '3px',
                          fontSize: '14px',
                          backgroundColor: 'white'
                        }}
                      >
                        <option value="">금액 선택</option>
                        {[10000, 20000, 30000, 40000, ...Array.from({ length: 20 }, (_, i) => (i + 1) * 50000)].map((amount) => (
                          <option key={amount} value={amount}>
                            {amount.toLocaleString()}
                          </option>
                        ))}
                      </select>
                    ) : (row.item === '비공개 금액_주택' || row.item === '비공개 금액_빌라') ? (
                      <input
                        type="text"
                        value={row.item === '비공개 금액_주택' ? '600,000' : '300,000'}
                        disabled={true}
                        style={{
                          backgroundColor: 'rgb(255, 255, 246)',
                          textAlign: 'center'
                        }}
                      />
                    ) : (
                    <input
                      type="text"
                      placeholder={(row.item === '직접입력' || row.item === '직접입력(고객 비공개용)') ? '수량X단가 금액입력' : ''}
                      value={
                        (row.item === '직접입력' || row.item === '직접입력(고객 비공개용)')
                          ? (row.priceInput !== undefined
                              ? (row.priceInput === '' || row.priceInput === '-' ? row.priceInput : formatNumber(parseInt(row.priceInput)))
                              : (row.price === 0 ? '' : formatNumber(row.price)))
                          : formatNumber(row.price)
                      }
                      disabled={!(row.item === '직접입력' || row.item === '직접입력(고객 비공개용)')}
                      style={{
                        backgroundColor: (row.item === '직접입력' || row.item === '직접입력(고객 비공개용)') ? 'white' : 'rgb(255, 255, 246)',
                        fontSize: (row.item === '직접입력' || row.item === '직접입력(고객 비공개용)') ? '11px' : 'inherit'
                      }}
                      onFocus={(e) => {
                        // 포커스 시 동작 제거 (이미 빈 문자열로 표시됨)
                      }}
                      onChange={(e) => {
                        if (row.item === '직접입력' || row.item === '직접입력(고객 비공개용)') {
                          // 1. 콤마 제거
                          let rawValue = e.target.value.replace(/,/g, '');
                          // 2. 숫자와 '-'만 허용
                          let value = rawValue.replace(/[^0-9-]/g, '');
                          // 3. '-'는 맨 앞에만 허용
                          if (value.length > 0) {
                            const firstChar = value[0];
                            const rest = value.slice(1).replace(/-/g, '');
                            value = firstChar + rest;
                          }
                          // 4. priceInput에 원본 저장 (콤마 없는 상태)
                          updateAdditionalData(row.id, 'priceInput', value);
                          // 5. price에 숫자로 변환하여 저장
                          const numValue = parseInt(value);
                          updateAdditionalData(row.id, 'price', isNaN(numValue) ? 0 : numValue);
                        }
                      }}
                      onKeyDown={handleEnterKey}
                    />
                    )}
                  </td>
                  <td>
                    <button onClick={() => removeAdditionalRow(row.id)}>-</button>
                  </td>
                    </SortableRow>
                  ))}
                </SortableContext>
              </tbody>
            </table>
          </DndContext>
          <div className="text-right">
            <button className="add-row-btn" onClick={addAdditionalRow}>
              + 추가
            </button>
          </div>
        </div>
        )}
      </div>

      {/* [주석처리] 브랜드별 창호 스펙 - 새 구조로 재생성 예정 */}
      {false && (
      <div id="brand-spec-section">
      <div className="section-title section-title-main brand-spec-header">
        <span>브랜드별 창호 스펙</span>
        <span className="brand-spec-subtitle">
          브랜드 및 제작 옵션에 따른 사양을 확인하세요
        </span>
      </div>
      <table className="table brand-spec-table">
        <thead>
          <tr>
            <th>구분</th>
            <th>LX하우시스</th>
            <th>홈윈도우</th>
            <th>
              <select 
                className="brand-header-select"
                value={kccBrandOption}
                onChange={(e) => setKccBrandOption(e.target.value)}
              >
                <option value="KCC홈씨씨">KCC홈씨씨</option>
                <option value="KCC">KCC</option>
              </select>
            </th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>제작</td>
            <td>
              <select 
                className="brand-spec-select"
                value={lxProductionOption}
                onChange={(e) => setLxProductionOption(e.target.value)}
              >
                <option value="LX스탠다드">LX스탠다드</option>
                <option value="LX프리미엄">LX프리미엄</option>
                <option value="LX본사(제작창)">LX본사(제작창)</option>
                <option value="LX본사(완성창)">LX본사(완성창)</option>
              </select>
            </td>
            <td>홈윈도우</td>
            <td>{kccBrandOption === 'KCC' ? '청암홈윈도우' : '홈씨씨'}</td>
          </tr>
          <tr>
            <td>시공보증</td>
            <td>
              <span style={{ color: '#b72c2c', fontWeight: 'bold' }}>
                {lxProductionOption === 'LX본사(완성창)' ? 'LX본사(10년보증)' : '청암홈윈도우(15년 보증)'}
              </span>
            </td>
            <td><span style={{ color: '#b72c2c', fontWeight: 'bold' }}>청암홈윈도우(15년 보증)</span></td>
            <td>{kccBrandOption === 'KCC' ? <span style={{ color: '#b72c2c', fontWeight: 'bold' }}>청암홈윈도우(15년보증)</span> : '홈씨씨(13년 보증)'}</td>
          </tr>
          <tr>
            <td>유리</td>
            <td>
              외부 <span className="brand-spec-highlight" style={{ color: '#000', fontWeight: 'bold' }}>26T</span> / 내부 <span className="brand-spec-highlight" style={{ color: '#b72c2c', fontWeight: 'bold' }}>
                {(lxProductionOption === 'LX본사(제작창)' || lxProductionOption === 'LX본사(완성창)') ? '24T' : '26T'}
              </span>
            </td>
            <td>
              외부 <span className="brand-spec-highlight" style={{ color: '#000', fontWeight: 'bold' }}>26T</span> / 내부 <span className="brand-spec-highlight" style={{ color: '#b72c2c', fontWeight: 'bold' }}>24T</span>
            </td>
            <td>
              {kccBrandOption === 'KCC' ? (
                <>외부 <span className="brand-spec-highlight" style={{ color: '#000', fontWeight: 'bold' }}>26T</span> / 내부 <span className="brand-spec-highlight" style={{ color: '#b72c2c', fontWeight: 'bold' }}>24T</span></>
              ) : (
                <>외부 <span className="brand-spec-highlight" style={{ color: '#000', fontWeight: 'bold' }}>26T</span> / 내부 <span className="brand-spec-highlight" style={{ color: '#b72c2c', fontWeight: 'bold' }}>22T</span></>
              )}
            </td>
          </tr>
          <tr>
            <td>방충망</td>
            <td>
              {(lxProductionOption === 'LX스탠다드' || lxProductionOption === 'LX프리미엄') ? (
                <select 
                  className="brand-spec-select"
                  value={lxScreenOption}
                  onChange={(e) => setLxScreenOption(e.target.value)}
                >
                  <option value="안전방충망">안전방충망</option>
                  <option value="방범방충망">방범방충망</option>
                  <option value="블랙스텐망">블랙스텐망</option>
                </select>
              ) : (lxProductionOption === 'LX본사(제작창)' || lxProductionOption === 'LX본사(완성창)') ? 'LX방충망' : '안전방충망'}
            </td>
            <td>
              {(lxProductionOption === 'LX스탠다드' || lxProductionOption === 'LX프리미엄' || lxProductionOption === 'LX본사(제작창)' || lxProductionOption === 'LX본사(완성창)') ? (
                <select 
                  className="brand-spec-select"
                  value={hwScreenOption}
                  onChange={(e) => setHwScreenOption(e.target.value)}
                >
                  <option value="안전방충망">안전방충망</option>
                  <option value="방범방충망">방범방충망</option>
                  <option value="블랙스텐망">블랙스텐망</option>
                </select>
              ) : '안전방충망'}
            </td>
            <td>블랙스텐망</td>
          </tr>
          <tr>
            <td>윈드클로저</td>
            <td>{(lxProductionOption === 'LX프리미엄' || lxProductionOption === 'LX본사(제작창)' || lxProductionOption === 'LX본사(완성창)') ? 'LX정품(히든형)' : 'LX정품'}</td>
            <td>고급형</td>
            <td>고급형</td>
          </tr>
          <tr>
            <td>레일캡</td>
            <td>{lxProductionOption === 'LX스탠다드' ? 'LX정품' : 'LX정품(플랫형)'}</td>
            <td>고급형</td>
            <td>고급형</td>
          </tr>
          <tr>
            <td style={{ fontWeight: 'bold', textAlign: 'center' }}>스트라이커</td>
            <td style={{ textAlign: 'center' }}>{(lxProductionOption === 'LX프리미엄' || lxProductionOption === 'LX본사(제작창)' || lxProductionOption === 'LX본사(완성창)') ? 'LX정품(플랫형)' : 'LX정품'}</td>
            <td style={{ textAlign: 'center' }}>고급형</td>
            <td style={{ textAlign: 'center' }}>고급형</td>
          </tr>
          <tr>
            <td style={{ fontWeight: 'bold', textAlign: 'center' }}>자동핸들</td>
            <td style={{ textAlign: 'center' }}>
              <select 
                className="brand-spec-select"
                value={lxAutoHandleScope}
                onChange={(e) => setLxAutoHandleScope(e.target.value)}
              >
                <option value="발코니창 무상적용">발코니창 무상적용</option>
                <option value="내외부 무상 업그레이드 쿠폰적용">내외부 무상 업그레이드 쿠폰적용</option>
              </select>
            </td>
            <td style={{ textAlign: 'center' }}>
              <select 
                className="brand-spec-select"
                value={hwAutoHandleScope}
                onChange={(e) => setHwAutoHandleScope(e.target.value)}
              >
                <option value="발코니창 무상적용">발코니창 무상적용</option>
                <option value="내외부 무상 업그레이드 쿠폰적용">내외부 무상 업그레이드 쿠폰적용</option>
              </select>
            </td>
            <td style={{ textAlign: 'center' }}>
              <select 
                className="brand-spec-select"
                value={kccAutoHandleScope}
                onChange={(e) => setKccAutoHandleScope(e.target.value)}
              >
                <option value="발코니창 무상적용">발코니창 무상적용</option>
                <option value="내외부 무상 업그레이드 쿠폰적용">내외부 무상 업그레이드 쿠폰적용</option>
              </select>
            </td>
          </tr>
          <tr className="combined-frame-row">
            <td style={{ fontWeight: 'bold', textAlign: 'center' }}>공틀 분합문(핸들)</td>
            <td style={{ textAlign: 'center' }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '3px', alignItems: 'center' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '11px', lineHeight: '1.2' }}>
                  <input 
                    type="checkbox" 
                    style={{ width: '12px', height: '12px' }} 
                    checked={lxCombinedFrameOptions[(lxProductionOption === 'LX본사(제작창)' || lxProductionOption === 'LX본사(완성창)') ? '2W 양방향커플(양쪽)' : '2W 양방향커플(주사용)+자동(반대창)'] || false}
                    onChange={(e) => {
                      const key = (lxProductionOption === 'LX본사(제작창)' || lxProductionOption === 'LX본사(완성창)') ? '2W 양방향커플(양쪽)' : '2W 양방향커플(주사용)+자동(반대창)';
                      setLxCombinedFrameOptions(prev => ({ ...prev, [key]: e.target.checked }));
                    }}
                  />
                  {(lxProductionOption === 'LX본사(제작창)' || lxProductionOption === 'LX본사(완성창)') ? '2W 양방향커플(양쪽)' : '2W 양방향커플(주사용)+자동(반대창)'}
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '11px', lineHeight: '1.2' }}>
                  <input 
                    type="checkbox" 
                    style={{ width: '12px', height: '12px' }} 
                    checked={lxCombinedFrameOptions['3W 양방향커플(1번창)+양방향커플(3번창)'] || false}
                    onChange={(e) => {
                      setLxCombinedFrameOptions(prev => ({ ...prev, '3W 양방향커플(1번창)+양방향커플(3번창)': e.target.checked }));
                    }}
                  />
                  3W 양방향커플(1번창)+양방향커플(3번창)
                </label>
              </div>
            </td>
            <td style={{ textAlign: 'center' }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', alignItems: 'center' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                  <input 
                    type="checkbox" 
                    style={{ width: '12px', height: '12px' }} 
                    checked={hwCombinedFrameOptions['반자동핸들(무상)'] || false}
                    onChange={(e) => {
                      setHwCombinedFrameOptions(prev => ({ ...prev, '반자동핸들(무상)': e.target.checked }));
                    }}
                  />
                  반자동핸들(무상)
                </label>
              </div>
            </td>
            <td style={{ textAlign: 'center' }}>
              {kccBrandOption === 'KCC' ? (
                '-'
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', alignItems: 'center' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                    <input 
                      type="checkbox" 
                      style={{ width: '12px', height: '12px' }} 
                      checked={kccCombinedFrameOptions['반자동핸들(무상)'] || false}
                      onChange={(e) => {
                        setKccCombinedFrameOptions(prev => ({ ...prev, '반자동핸들(무상)': e.target.checked }));
                      }}
                    />
                    반자동핸들(무상)
                  </label>
                </div>
              )}
            </td>
          </tr>
          <tr>
            <td style={{ fontWeight: 'bold', textAlign: 'center' }}>안전스토퍼</td>
            <td style={{ textAlign: 'center' }}>{(lxProductionOption === 'LX본사(제작창)' || lxProductionOption === 'LX본사(완성창)') ? 'LX정품(SF스토퍼)' : 'LX정품(BF스토퍼)'}</td>
            <td style={{ textAlign: 'center' }}>BF스토퍼</td>
            <td style={{ textAlign: 'center' }}>BF스토퍼</td>
          </tr>
          {/* 홈페스타 혜택 */}
          <tr>
            <td style={{ fontWeight: 'bold', textAlign: 'center' }}>특별혜택</td>
            <td style={{ textAlign: 'center' }}>
              {(lxProductionOption === 'LX스탠다드' || lxProductionOption === 'LX프리미엄') ? (
                <select
                  style={{
                    padding: '5px',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    fontSize: '14px',
                    width: '90%'
                  }}
                  value={lxSpecialBenefitType}
                  onChange={(e) => setLxSpecialBenefitType(e.target.value)}
                >
                  <option value="안전방충망">안전방충망</option>
                  <option value="방범방충망">방범방충망</option>
                  <option value="주방시스템창">주방시스템창</option>
                  <option value="안전방충망+주방시스템창">안전방충망+주방시스템창</option>
                  <option value="방범방충망+주방시스템창">방범방충망+주방시스템창</option>
                  <option value="방범방충망+주방시스템창+터닝도어">방범방충망+주방시스템창+터닝도어</option>
                </select>
              ) : (
                '-'
              )}
            </td>
            <td style={{ textAlign: 'center' }}>
              <select
                style={{
                  padding: '5px',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  fontSize: '14px',
                  width: '90%'
                }}
                value={hwSpecialBenefitType}
                onChange={(e) => setHwSpecialBenefitType(e.target.value)}
              >
                <option value="안전방충망">안전방충망</option>
                <option value="방범방충망">방범방충망</option>
                <option value="주방시스템창">주방시스템창</option>
                <option value="안전방충망+주방시스템창">안전방충망+주방시스템창</option>
                <option value="방범방충망+주방시스템창">방범방충망+주방시스템창</option>
                <option value="방범방충망+주방시스템창+터닝도어">방범방충망+주방시스템창+터닝도어</option>
              </select>
            </td>
            <td style={{ textAlign: 'center' }}>-</td>
          </tr>
          <tr>
            <td style={{ fontWeight: 'bold', textAlign: 'center' }}>혜택1</td>
            <td colSpan={3} style={{ padding: '10px' }}>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '15px' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '5px', fontSize: '14px' }}>
                  <input
                    type="checkbox"
                    style={{ width: '12px', height: '12px' }}
                    defaultChecked={true}
                  />
                  TPS단열간봉
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: '5px', fontSize: '14px' }}>
                  <input
                    type="checkbox"
                    style={{ width: '12px', height: '12px' }}
                    defaultChecked={true}
                  />
                  아르곤가스
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: '5px', fontSize: '14px' }}>
                  <input
                    type="checkbox"
                    style={{ width: '12px', height: '12px' }}
                    defaultChecked={true}
                  />
                  로이유리
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: '5px', fontSize: '14px' }}>
                  <input
                    type="checkbox"
                    style={{ width: '12px', height: '12px' }}
                    defaultChecked={true}
                  />
                  내/외부창 모두 적용
                </label>
              </div>
            </td>
          </tr>
          <tr>
            <td style={{ fontWeight: 'bold', textAlign: 'center' }}>혜택2</td>
            <td colSpan={3} style={{ padding: '10px' }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '15px' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '5px', fontSize: '14px' }}>
                    <input
                      type="checkbox"
                      style={{ width: '12px', height: '12px' }}
                      defaultChecked={true}
                    />
                    NON실리콘
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '5px', fontSize: '14px' }}>
                    <input
                      type="checkbox"
                      style={{ width: '12px', height: '12px' }}
                      defaultChecked={true}
                    />
                    방충배수캡
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '5px', fontSize: '14px' }}>
                    <input
                      type="checkbox"
                      style={{ width: '12px', height: '12px' }}
                      defaultChecked={true}
                    />
                    윈드클로저(발코니창 전용)
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '5px', fontSize: '14px' }}>
                    <input
                      type="checkbox"
                      style={{ width: '12px', height: '12px' }}
                      defaultChecked={true}
                    />
                    레일캡
                  </label>
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '15px' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '5px', fontSize: '14px' }}>
                    <input
                      type="checkbox"
                      style={{ width: '12px', height: '12px' }}
                      defaultChecked={true}
                    />
                    자동핸들(발코니창 무상)
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '5px', fontSize: '14px' }}>
                    <input
                      type="checkbox"
                      style={{ width: '12px', height: '12px' }}
                      defaultChecked={true}
                    />
                    거주세대 실내 몰딩 마감
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '5px', fontSize: '14px' }}>
                    <input
                      type="checkbox"
                      style={{ width: '12px', height: '12px' }}
                      defaultChecked={true}
                    />
                    천장형 빨래건조대 서비스 교체
                  </label>
                </div>
              </div>
            </td>
          </tr>
          <tr>
            <td style={{ fontWeight: 'bold', textAlign: 'center' }}>특이사항</td>
            <td colSpan={3} style={{ textAlign: 'left', padding: '10px' }}>
              <textarea
                style={{
                  width: '100%',
                  minHeight: '60px',
                  padding: '8px',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  fontSize: '14px',
                  lineHeight: '1.5',
                  resize: 'vertical',
                  fontFamily: 'inherit'
                }}
                value={specialNotes}
                onChange={(e) => setSpecialNotes(e.target.value)}
                placeholder="특이사항을 입력하세요"
              />
            </td>
          </tr>
        </tbody>
      </table>
      </div>
      )}
      {/* 브랜드별 창호 스펙 주석처리 끝 */}

      {/* 새로운 브랜드별 창호 스펙 */}
      <BrandSpecs selectedGrades={selectedGrades} setSelectedGrades={setSelectedGrades} />

      {/* 할인내역 및 할인 선택 */}
      <div className="section-title section-title-main discount-section-title show-discount">
        할인비율
      </div>
      <table className="table discount-table show-discount">
        <thead>
          <tr>
            <th>LX하우시스</th>
            <th>홈윈도우</th>
            <th>KCC홈씨씨</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>
              <input
                type="number"
                min={0}
                max={40}
                value={discountRates.LX하우시스}
                onChange={(e) =>
                  handleDiscountRateChange('LX하우시스', e.target.value)
                }
                onBlur={handleDiscountRateBlur}
                onFocus={(e) => e.target.select()}
                onDoubleClick={(e) => {
                  setDiscountRates((prev) => ({ ...prev, LX하우시스: '' }));
                }}
                onWheel={(e) => e.currentTarget.blur()}
                onKeyDown={handleEnterKey}
                style={{ width: 50, textAlign: 'center', marginRight: 5 }}
              />
              <span className="discount-label">% 할인</span>
            </td>
            <td>
              <input
                type="number"
                min={0}
                max={40}
                value={discountRates.homeWindow}
                onChange={(e) =>
                  handleDiscountRateChange('homeWindow', e.target.value)
                }
                onBlur={handleDiscountRateBlur}
                onFocus={(e) => e.target.select()}
                onDoubleClick={(e) => {
                  setDiscountRates((prev) => ({ ...prev, homeWindow: '' }));
                }}
                onWheel={(e) => e.currentTarget.blur()}
                onKeyDown={handleEnterKey}
                style={{ width: 50, textAlign: 'center', marginRight: 5 }}
              />
              <span className="discount-label">% 할인</span>
            </td>
            <td>
              <input
                type="number"
                min={0}
                max={15}
                value={discountRates.KCC홈CC}
                onChange={(e) =>
                  handleDiscountRateChange('KCC홈CC', e.target.value)
                }
                onBlur={handleDiscountRateBlur}
                onFocus={(e) => e.target.select()}
                onDoubleClick={(e) => {
                  setDiscountRates((prev) => ({ ...prev, KCC홈CC: '' }));
                }}
                onWheel={(e) => e.currentTarget.blur()}
                onKeyDown={handleEnterKey}
                style={{ width: 50, textAlign: 'center', marginRight: 5 }}
              />
              <span className="discount-label">% 할인</span>
            </td>
          </tr>
        </tbody>
      </table>

      {/* 견적비고 및 고객센터 정보 */}
      <div className="estimate-notes-container" style={{ display: 'flex', justifyContent: 'space-between', marginTop: '20px' }}>
        <div className="estimate-notes-left" style={{ flex: 1.5, marginRight: '20px' }}>
          <div className="section-title" style={{ marginBottom: '10px' }}>견적비고</div>
          <textarea
            placeholder="견적비고가 있을 경우 입력해주세요"
            value={estimateInfo.estimateNotes || '- 샷시 철거, 폐기물 처리 비용 포함.\n- 위 견적서에 제시된 창호는 백색(색상)자재의 창호 기준입니다.\n- 견적은 7일간 유효합니다. '}
            onChange={(e) =>
              setEstimateInfo((prev) => ({
                ...prev,
                estimateNotes: e.target.value,
              }))
            }
            style={{
              width: '100%',
              height: '80px',
              padding: '10px',
              border: '1px solid #ddd',
              borderRadius: '4px',
              resize: 'vertical'
            }}
          />
        </div>
        <div className="estimate-notes-right" style={{
          flex: 1,
          marginLeft: '20px',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center'
        }}>
          <div style={{ textAlign: 'left' }}>
            <div className="section-title" style={{ marginBottom: '0px', fontWeight: 'bold', fontSize: '18px' }}>청암홈윈도우 '{estimateInfo.estimator || ''}' 영업팀장</div>
            <div style={{ fontSize: '48px', fontWeight: 'bold', color: '#d32f2f', marginBottom: '12px' }}>
              {estimateInfo.estimatorPhone || '1661-4830'}
            </div>
            <div style={{ fontSize: '16px', color: '#333', marginBottom: '5px', fontWeight: 'bold' }}>
              청암홈윈도우 고객센터 1644-4830
            </div>
            <div style={{ fontSize: '16px', color: '#666', marginBottom: '2px' }}>
              상담가능시간: 평일 08:00~18:00
            </div>
            <div style={{ fontSize: '14px', color: '#999' }}>
              *이후 시간은 AI상담사로 대체됩니다.
            </div>
            <div style={{ fontSize: '14px', color: 'black', marginTop: '12px', fontWeight: 'bold' }}>
              기업 940-052869-04-010 (주)청암홈윈도우
            </div>
          </div>
        </div>
      </div>

      {/* JPG 생성 버튼 */}
      {/* {PermissionManager.canDownloadJPG() && (
        <div style={{ textAlign: 'center', marginTop: 60 }}>
          <button
            className="jpg-button"
            onClick={handleDownload}
            style={{
              background: '#444',
              color: 'white',
              border: 'none',
              padding: '15px 55px',
              fontSize: 16,
              borderRadius: 51,
              cursor: 'pointer',
            }}>
            견적서 전송
          </button>
        </div>
      )} */}

      {/* 이미지 미리보기 모달 */}
      {showPreviewModal && (
        <div className="preview-modal-overlay">
          {/* 모달 헤더 */}
          <div className="modal-header">
            <h3 style={{ fontSize: 18, fontWeight: 600 }}>견적서 저장 완료</h3>
            {/* X버튼은 하단 버튼으로 이동 */}
          </div>

          {/* 이미지 미리보기 */}
          <div className="preview-content" style={{ position: 'relative' }}>
            {previewImageUrl && (
              <div
                className="preview-image-container"
                style={{
                  overflow: 'auto',
                  maxHeight: '70vh',
                  position: 'relative',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'flex-start',
                  padding: '20px',
                }}>
                <img
                  src={previewImageUrl}
                  alt="견적서 미리보기"
                  className="preview-image"
                  style={{
                    width: 'auto',
                    height: 'auto',
                    maxWidth: 'none',
                    borderRadius: 8,
                    transition: 'transform 0.3s ease',
                    transform: 'scale(2.5)',
                    transformOrigin: 'center top',
                  }}
                />
              </div>
            )}
          </div>

          {/* 하단 버튼들 */}
          <div
            className="modal-buttons-row"
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              gap: 10,
              background: '#fff',
              borderTop: '1px solid #eee',
              padding: '14px 0 12px 0',
              position: 'fixed',
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: 100,
            }}>
            {/* JPG 다운로드 */}
            <button
              onClick={handleRedownload}
              className="modal-btn modal-btn-download"
              style={{
                background: '#111',
                color: '#fff',
                fontWeight: 500,
                fontSize: 14,
                borderRadius: 8,
                padding: '0 18px',
                border: 'none',
                display: 'flex',
                alignItems: 'center',
                gap: 12,
                height: 40,
                minWidth: 120,
              }}>
              <svg
                width="18"
                height="18"
                viewBox="0 0 20 20"
                fill="none"
                stroke="#fff"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round">
                <path d="M10 4v8" />
                <path d="M6 10l4 4 4-4" />
                <rect
                  x="4"
                  y="16"
                  width="12"
                  height="2"
                  rx="1"
                  fill="#fff"
                  stroke="none"
                />
              </svg>
              JPG 다운로드
            </button>
            {/* 문자메시지 전송 */}
            {PermissionManager.canSendSMS() && (
              <button
                onClick={handleSmsShare}
                className="modal-btn modal-btn-sms"
                style={{
                  background: '#ef4444',
                  color: '#fff',
                  fontWeight: 500,
                  fontSize: 14,
                  borderRadius: 8,
                  padding: '0 18px',
                  border: 'none',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 12,
                  height: 40,
                  minWidth: 140,
                }}>
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 20 20"
                  fill="none"
                  stroke="#fff"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round">
                  <rect x="3" y="5" width="14" height="10" rx="2" />
                  <polyline points="3,5 10,12 17,5" />
                </svg>
                문자메시지 전송
              </button>
            )}
            {/* 카카오톡 전송 */}
            {PermissionManager.canSendKakao() && (
              <button
                onClick={handleKakaoShare}
                className="modal-btn modal-btn-kakao"
                style={{
                  background: '#ffe812',
                  color: '#222',
                  fontWeight: 500,
                  fontSize: 14,
                  borderRadius: 8,
                  padding: '0 18px',
                  border: 'none',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 12,
                  height: 40,
                  minWidth: 140,
                }}>
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 20 20"
                  fill="none"
                  stroke="#222"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round">
                  <circle cx="9" cy="9" r="7" />
                  <path d="M13 13l4 4" />
                </svg>
                카카오톡 전송
              </button>
            )}
            {/* 닫기(X) */}
            <button
              onClick={handleClosePreview}
              className="modal-btn modal-btn-close"
              style={{
                background: '#eee',
                color: '#888',
                border: 'none',
                borderRadius: '50%',
                width: 36,
                height: 36,
                fontSize: 18,
                fontWeight: 700,
                marginLeft: 6,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                minWidth: 0,
                minHeight: 0,
                padding: 0,
              }}
              title="닫기">
              <svg
                width="18"
                height="18"
                viewBox="0 0 20 20"
                fill="none"
                stroke="#888"
                strokeWidth="2.2"
                strokeLinecap="round">
                <circle cx="10" cy="10" r="9" fill="#eee" />
                <path d="M7 7l6 6M13 7l-6 6" />
              </svg>
            </button>
          </div>
        </div>
      )}

      {/* 견적 불러오기 모달 */}
      {showLoadModal && (
        <div className="modal-overlay">
          <div
            className="load-modal-container"
            style={{
              position: 'relative',
              maxHeight: '70vh',
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column',
            }}>
            <div className="load-modal-header">
              <h3>견적 불러오기</h3>
              <button onClick={closeLoadModal} className="modal-close-btn">
                ×
              </button>
            </div>

            {/* 컨텐츠 영역 - 필터/검색 UI는 항상 표시 */}
            <div style={{ display: 'flex', flexDirection: 'column', flex: 1, overflow: 'hidden' }}>
              {/* 백그라운드 업데이트 인디케이터 */}
              {isFetchingInBackground && (
                <div style={{
                  position: 'absolute',
                  top: 60,
                  right: 20,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                  padding: '4px 10px',
                  backgroundColor: '#e3f2fd',
                  borderRadius: 12,
                  fontSize: 12,
                  color: '#1976d2',
                  zIndex: 10
                }}>
                  <span style={{
                    width: 8,
                    height: 8,
                    borderRadius: '50%',
                    backgroundColor: '#1976d2',
                    animation: 'pulse 1s infinite'
                  }} />
                  업데이트 중...
                </div>
              )}
                {/* 검색창 - 스크롤 영역 밖에 위치 */}
                <div
                  className="filter-row-fixed"
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 8,
                        padding: '12px 0',
                        backgroundColor: '#fff',
                      }}>
                      <input
                        type="date"
                        value={filterStartDate}
                        onChange={(e) => setFilterStartDate(e.target.value)}
                        onFocus={(e) => {
                          try {
                            if (e.target.showPicker && e.isTrusted) {
                              e.target.showPicker();
                            }
                          } catch (error) {
                            // showPicker 에러 무시
                          }
                        }}
                        style={{
                          height: 36,
                          borderRadius: 8,
                          border: '1.5px solid #d3d8e1',
                          background: '#fff',
                          fontSize: 14,
                          padding: '0 10px',
                        }}
                      />
                      <span style={{ margin: '0 2px' }}>~</span>
                      <input
                        type="date"
                        value={filterEndDate}
                        onChange={(e) => setFilterEndDate(e.target.value)}
                        onFocus={(e) => {
                          try {
                            if (e.target.showPicker && e.isTrusted) {
                              e.target.showPicker();
                            }
                          } catch (error) {
                            // showPicker 에러 무시
                          }
                        }}
                        style={{
                          height: 36,
                          borderRadius: 8,
                          border: '1.5px solid #d3d8e1',
                          background: '#fff',
                          fontSize: 14,
                          padding: '0 10px',
                        }}
                      />
                      
                      {/* 계약/공유 상태 필터 드롭다운 */}
                      <select
                        value={filterContractStatus}
                        onChange={(e) => {
                          const value = e.target.value;
                          // 'shared' 필터는 권한이 있는 사용자만 사용 가능
                          if (value === 'shared' &&
                              PermissionManager.getEstimateLoadScope() !== 'ALL') {
                            setFilterContractStatus('all');
                          } else {
                            setFilterContractStatus(value);
                          }
                        }}
                        style={{
                          height: 36,
                          borderRadius: 8,
                          border: '1.5px solid #d3d8e1',
                          background: '#fff',
                          fontSize: 14,
                          padding: '0 10px',
                          marginLeft: 8,
                          minWidth: 160,
                          cursor: 'pointer'
                        }}
                      >
                        <option value="all">전체 견적</option>
                        <option value="contracted">계약 완료된 견적</option>
                        {/* 견적서를 공유한 견적 - 관리자, 지원팀, 상담팀만 표시 */}
                        {PermissionManager.getEstimateLoadScope() === 'ALL' && (
                          <option value="shared">견적서를 공유한 견적</option>
                        )}
                        <option value="received">견적서를 공유받은 견적</option>
                      </select>
                      
                      {/* 견적서 공유 버튼 - 권한별 표시 */}
                      {PermissionManager.getEstimateLoadScope() === 'ALL' && (
                        <button
                          style={{
                            marginLeft: 12,
                            padding: '8px 20px',
                            backgroundColor: selectedEstimates.size > 0 ? '#000' : '#ccc',
                            color: '#fff',
                            border: 'none',
                            borderRadius: 8,
                            fontSize: 14,
                            fontWeight: 500,
                            cursor: selectedEstimates.size > 0 ? 'pointer' : 'not-allowed',
                            opacity: selectedEstimates.size > 0 ? 1 : 0.6,
                            height: 36,
                            whiteSpace: 'nowrap',
                            transition: 'background-color 0.2s'
                          }}
                          onMouseEnter={(e) => {
                            if (selectedEstimates.size > 0) {
                              e.currentTarget.style.backgroundColor = '#333';
                            }
                          }}
                          onMouseLeave={(e) => {
                            if (selectedEstimates.size > 0) {
                              e.currentTarget.style.backgroundColor = '#000';
                            } else {
                              e.currentTarget.style.backgroundColor = '#ccc';
                            }
                          }}
                          disabled={selectedEstimates.size === 0}
                          onClick={() => {
                            if (selectedEstimates.size === 0) {
                              alert('공유할 견적서를 선택해주세요.');
                              return;
                            }
                            
                            // 견적서 공유 모달 열기
                            setShowShareModal(true);
                          }}
                        >
                          견적서 공유 {selectedEstimates.size > 0 && `(${selectedEstimates.size})`}
                        </button>
                      )}
                      
                      <div style={{ flex: 1 }} />
                      <div style={{ minWidth: 220 }}>
                        <DebouncedSearchInput
                          onSearchChange={handleSearchChange}
                          initialValue={searchInput}
                          placeholder=""
                        />
                      </div>
                </div>

                {/* 테이블 컨테이너 - 이 부분만 스크롤 */}
                <div className="load-modal-table-container" style={{ flex: 1, overflowY: 'auto' }}>
                  {loadingEstimates ? (
                    <div className="load-modal-loading">
                      견적 목록을 불러오는 중...
                    </div>
                  ) : !paginatedEstimates || paginatedEstimates.length === 0 ? (
                    <div className="load-modal-empty">
                      검색 결과가 없습니다.
                    </div>
                  ) : (
                    <table className="load-modal-table">
                          <thead>
                            <tr>
                              {PermissionManager.getEstimateLoadScope() === 'ALL' && (
                                <th style={{ width: '25px' }}>선택</th>
                              )}
                              <th style={{ 
                                width: PermissionManager.getEstimateLoadScope() === 'ALL' ? '160px' : '150px' 
                              }}>견적번호</th>
                              <th style={{ width: '60px' }}>고객명</th>
                              <th style={{ width: '100px', textAlign: 'center' }}>연락처</th>
                              <th style={{ 
                                width: PermissionManager.getEstimateLoadScope() === 'ALL' ? '350px' : '300px' 
                              }}>주소</th>
                              <th style={{ width: '120px' }}>견적일시</th>
                              <th style={{ width: '60px' }}>견적자</th>
                              <th style={{ width: '55px' }}>불러오기</th>
                              <th style={{ width: '35px' }}>계약</th>
                            </tr>
                          </thead>
                          <tbody>
                            {paginatedEstimates.length === 0 ? (
                              <tr>
                                <td
                                  colSpan={PermissionManager.getEstimateLoadScope() === 'ALL' ? 9 : 8}
                                  style={{
                                    textAlign: 'center',
                                    color: '#aaa',
                                  }}>
                                  데이터가 없습니다.
                                </td>
                              </tr>
                            ) : (
                              paginatedEstimates.map((estimate) => (
                                <tr key={estimate.estimate_id}>
                                  {PermissionManager.getEstimateLoadScope() === 'ALL' && (
                                    <td style={{ textAlign: 'center' }}>
                                      <input
                                        type="checkbox"
                                        checked={estimate.estimate_id ? selectedEstimates.has(estimate.estimate_id.toString()) : false}
                                        onChange={(e) => {
                                          if (!estimate.estimate_id) return;
                                          const newSelected = new Set(selectedEstimates);
                                          if (e.target.checked) {
                                            newSelected.add(estimate.estimate_id.toString());
                                          } else {
                                            newSelected.delete(estimate.estimate_id.toString());
                                          }
                                          setSelectedEstimates(newSelected);
                                        }}
                                        style={{ cursor: 'pointer' }}
                                        disabled={!estimate.estimate_id}
                                      />
                                    </td>
                                  )}
                                  <td>
                                    <span
                                      style={{
                                        cursor: 'pointer',
                                        color: '#2563eb',
                                        textDecoration: 'underline',
                                        fontWeight: '500'
                                      }}
                                      onClick={() => previewEstimate(estimate.estimateNumber)}
                                      title="미리보기"
                                    >
                                      {estimate.estimateNumber}
                                    </span>
                                  </td>
                                  <td>{estimate.customerName}</td>
                                  <td style={{ textAlign: 'center' }}>{estimate.phone}</td>
                                  <td style={{ textAlign: 'left' }}>{formatAddress(estimate)}</td>
                                  <td>{formatDateTime(estimate)}</td>
                                  <td>{estimate.estimator}</td>
                                  <td>
                                    <button
                                      onClick={() =>
                                        loadEstimate(estimate.estimateNumber)
                                      }
                                      className="load-btn load-btn-icon"
                                      title="불러오기">
                                      <svg
                                        width="20"
                                        height="20"
                                        viewBox="0 0 20 20"
                                        fill="none"
                                        stroke="#222"
                                        strokeWidth="2.2">
                                        <path
                                          d="M10 3v10m0 0l-4-4m4 4l4-4"
                                          strokeLinecap="round"
                                          strokeLinejoin="round"
                                        />
                                        <rect
                                          x="4"
                                          y="15"
                                          width="12"
                                          height="2"
                                          rx="1"
                                          fill="#222"
                                        />
                                      </svg>
                                    </button>
                                  </td>
                                  <td style={{ textAlign: 'center' }}>
                                    <div
                                      onClick={() => handleContractCheckbox(estimate.estimate_id?.toString() || '')}
                                      style={{
                                        width: '20px',
                                        height: '20px',
                                        border: '2px solid',
                                        borderColor: contractedEstimates.has(estimate.estimate_id?.toString() || '') ? '#28a745' : '#ccc',
                                        backgroundColor: contractedEstimates.has(estimate.estimate_id?.toString() || '') ? '#28a745' : 'transparent',
                                        borderRadius: '3px',
                                        cursor: 'pointer',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        margin: '0 auto',
                                        transition: 'all 0.2s ease'
                                      }}
                                    >
                                      {contractedEstimates.has(estimate.estimate_id?.toString() || '') ? (
                                        <svg
                                          width="12"
                                          height="12"
                                          viewBox="0 0 24 24"
                                          fill="none"
                                          stroke="white"
                                          strokeWidth="3"
                                          strokeLinecap="round"
                                          strokeLinejoin="round"
                                        >
                                          <polyline points="20,6 9,17 4,12" />
                                        </svg>
                                      ) : (
                                        <svg
                                          width="12"
                                          height="12"
                                          viewBox="0 0 24 24"
                                          fill="none"
                                          stroke="#ccc"
                                          strokeWidth="3"
                                          strokeLinecap="round"
                                          strokeLinejoin="round"
                                        >
                                          <polyline points="20,6 9,17 4,12" />
                                        </svg>
                                      )}
                                    </div>
                                  </td>
                                </tr>
                              ))
                            )}
                          </tbody>
                    </table>
                  )}
                </div>

                {/* 페이지네이션 - 스크롤 영역 밖에 위치 */}
                {totalEstimateElements > 0 && (
                  <div
                    className="modal-pagination-bottom-row">
                          {/* 좌측: 페이지네이션 버튼 */}
                          <div
                            className="pagination"
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 2,
                            }}>
                            {totalEstimatePages > 1 && (
                              <>
                                <button
                                  className="pagination-btn"
                                  disabled={currentEstimatePage === 1}
                                  onClick={() => handleEstimatePageChange(1)}>
                                  &lt;&lt;
                                </button>
                                <button
                                  className="pagination-btn"
                                  disabled={currentEstimatePage === 1}
                                  onClick={() =>
                                    handleEstimatePageChange(
                                      currentEstimatePage - 1
                                    )
                                  }>
                                  &lt;
                                </button>
                              </>
                            )}
                            {(() => {
                              const maxVisiblePages = 5;
                              const currentGroup = Math.ceil(currentEstimatePage / maxVisiblePages);
                              const startPage = (currentGroup - 1) * maxVisiblePages + 1;
                              const endPage = Math.min(startPage + maxVisiblePages - 1, totalEstimatePages);
                              
                              return Array.from(
                                { length: endPage - startPage + 1 },
                                (_, i) => startPage + i
                              ).map((page) => (
                                <button
                                  key={page}
                                  className={`pagination-btn${
                                    currentEstimatePage === page ? ' active' : ''
                                  }`}
                                  onClick={() => handleEstimatePageChange(page)}
                                  disabled={
                                    totalEstimatePages === 1 ||
                                    currentEstimatePage === page
                                  }>
                                  {page}
                                </button>
                              ));
                            })()}
                            {totalEstimatePages > 1 && (
                              <>
                                <button
                                  className="pagination-btn"
                                  disabled={
                                    currentEstimatePage === totalEstimatePages
                                  }
                                  onClick={() =>
                                    handleEstimatePageChange(
                                      currentEstimatePage + 1
                                    )
                                  }>
                                  &gt;
                                </button>
                                <button
                                  className="pagination-btn"
                                  disabled={
                                    currentEstimatePage === totalEstimatePages
                                  }
                                  onClick={() =>
                                    handleEstimatePageChange(totalEstimatePages)
                                  }>
                                  &gt;&gt;
                                </button>
                              </>
                            )}
                          </div>
                          {/* 가운데: 페이지 정보 */}
                          <div
                            className="pagination-info"
                            style={{ fontSize: 13, color: '#888' }}>
                            페이지 {currentEstimatePage} /{' '}
                            {Math.max(1, totalEstimatePages)} (총{' '}
                            {totalEstimateElements}건)
                          </div>
                          {/* 우측: 페이지당 개수 선택 */}
                          {/* <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                            <span style={{ fontSize: 13, color: '#888' }}>페이지당</span>
                            <select
                              className="rows-per-page-select"
                              value={estimatesPerPage}
                              onChange={e => { setCurrentEstimatePage(1); setEstimatesPerPage(Number(e.target.value)); }}
                              style={{ height: 32, fontSize: 13, borderRadius: 4, border: '1.5px solid #d3d8e1', padding: '0 8px', minWidth: 60 }}
                            >
                              {[10, 20, 30, 50].map(n => <option key={n} value={n}>{n}개</option>)}
                            </select>
                          </div> */}
                  </div>
                )}
              </div>
          </div>
        </div>
      )}

      {/* 견적서 공유 모달 */}
      {showShareModal && (
        <div className="modal-overlay">
          <div className="modal-container" style={{
            width: '400px',
            padding: '0',
            borderRadius: '8px',
            backgroundColor: '#fff',
          }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '20px',
              borderBottom: '1px solid #e1e4e8',
            }}>
              <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '600' }}>견적서 공유</h3>
              <button
                onClick={() => {
                  setShowShareModal(false);
                  setSelectedDepartment('');
                  setSelectedUserName('');
                }}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '24px',
                  cursor: 'pointer',
                  color: '#666',
                  padding: '0',
                  width: '30px',
                  height: '30px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                ×
              </button>
            </div>
            
            <div style={{ padding: '20px' }}>
              <div style={{ marginBottom: '20px' }}>
                <label style={{ 
                  display: 'block', 
                  marginBottom: '8px',
                  fontSize: '14px',
                  fontWeight: '500',
                  color: '#333'
                }}>
                  소속
                </label>
                <select
                  value={selectedDepartment}
                  onChange={async (e) => {
                    const role = e.target.value;
                    setSelectedDepartment(role);
                    setSelectedUserName('');
                    
                    if (role) {
                      try {
                        // 선택한 부서의 사용자 목록 가져오기
                        const response = await api.get('/api/by-role', {
                          params: { role }
                        });
                        
                        const data = response.data;

                        if (data && Array.isArray(data)) {
                          const users = data.map((user: { id: number; name: string }) => ({
                            id: user.id.toString(),
                            name: user.name
                          }));
                          setDepartmentUsers(users);
                        } else {
                          setDepartmentUsers([]);
                        }
                      } catch (error: unknown) {
                        console.error('사용자 목록 조회 실패:', error);
                        setDepartmentUsers([]);

                        const axiosError = error as { response?: { status?: number } };
                        if (axiosError.response?.status === 403) {
                          alert('권한이 없습니다.');
                        } else if (axiosError.response?.status === 404) {
                          alert('API 엔드포인트를 찾을 수 없습니다.');
                        } else {
                          alert('사용자 목록을 불러오는데 실패했습니다.');
                        }
                      }
                    } else {
                      setDepartmentUsers([]);
                    }
                  }}
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: '1px solid #d1d5db',
                    borderRadius: '4px',
                    fontSize: '14px',
                    backgroundColor: '#fff',
                  }}
                >
                  <option value="">소속을 선택하세요</option>
                  <option value="CA_ADMIN">청암홈윈도우 관리자</option>
                  <option value="SUPPORT">지원팀</option>
                  <option value="CONSULTANT">상담팀</option>
                  <option value="CA_MANAGER">영업팀장</option>
                  <option value="FREELANCER_MANAGER">프리랜서 영업팀장</option>
                  <option value="CONSTRUCTION">시공팀</option>
                </select>
              </div>
              
              <div style={{ marginBottom: '20px' }}>
                <label style={{ 
                  display: 'block', 
                  marginBottom: '8px',
                  fontSize: '14px',
                  fontWeight: '500',
                  color: '#333'
                }}>
                  이름
                </label>
                <select
                  value={selectedUserName}
                  onChange={(e) => setSelectedUserName(e.target.value)}
                  disabled={!selectedDepartment}
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: '1px solid #d1d5db',
                    borderRadius: '4px',
                    fontSize: '14px',
                    backgroundColor: selectedDepartment ? '#fff' : '#f5f5f5',
                    cursor: selectedDepartment ? 'pointer' : 'not-allowed',
                  }}
                >
                  <option value="">이름을 선택하세요</option>
                  {departmentUsers.map(user => (
                    <option key={user.id} value={user.id}>
                      {user.name}
                    </option>
                  ))}
                </select>
              </div>
              
              <div style={{ marginTop: '10px', marginBottom: '20px' }}>
                <div style={{
                  padding: '10px',
                  backgroundColor: '#f8f9fa',
                  borderRadius: '4px',
                  fontSize: '13px',
                  color: '#666',
                }}>
                  선택한 {selectedEstimates.size}개의 견적서를 공유합니다.
                </div>
              </div>
              
              <button
                onClick={async () => {
                  if (!selectedDepartment || !selectedUserName) {
                    alert('소속과 이름을 모두 선택해주세요.');
                    return;
                  }
                  
                  try {
                    // 선택된 견적 ID들을 숫자 배열로 변환
                    const selectedIds = Array.from(selectedEstimates).map(id => parseInt(id));
                    
                    // 견적서 공유 API 호출
                    const response = await api.post('/api/estimates/share', {
                      estimateIds: selectedIds,
                      targetUserId: parseInt(selectedUserName) // selectedUserName은 실제로 user_id
                    });
                    
                    if (response.data.successCount > 0) {
                      alert(`${response.data.successCount}개의 견적서가 공유되었습니다.`);
                      setShowShareModal(false);
                      setSelectedDepartment('');
                      setSelectedUserName('');
                      setSelectedEstimates(new Set());
                      // 견적 목록 새로고침
                      loadEstimatesPage();
                    } else {
                      alert('견적서 공유에 실패했습니다.');
                    }
                  } catch (error: unknown) {
                    console.error('견적서 공유 실패:', error);
                    const axiosErr = error as { response?: { data?: { message?: string } } };
                    alert(axiosErr.response?.data?.message || '견적서 공유 중 오류가 발생했습니다.');
                  }
                }}
                disabled={!selectedDepartment || !selectedUserName}
                style={{
                  width: '100%',
                  padding: '12px',
                  backgroundColor: selectedDepartment && selectedUserName ? '#000' : '#ccc',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '4px',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: selectedDepartment && selectedUserName ? 'pointer' : 'not-allowed',
                  transition: 'background-color 0.2s',
                }}
              >
                견적서 공유
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* 견적서 미리보기 모달 */}
      {showEstimatePreviewModal && (
        <div className="modal-overlay">
          <div className="modal-container" style={{
            width: '90%',
            maxWidth: '1200px',
            height: '90%',
            maxHeight: '800px',
            padding: '0',
            borderRadius: '8px',
            backgroundColor: '#fff',
            display: 'flex',
            flexDirection: 'column',
          }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '15px 20px',
              borderBottom: '1px solid #e1e4e8',
            }}>
              <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '600' }}>
                견적서 미리보기 - {previewEstimateNumber}
              </h3>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                {/* 인쇄 버튼 */}
                <button
                  onClick={() => {
                    const printContent = document.getElementById('estimate-preview-print-area');
                    if (!printContent) return;

                    const printWindow = window.open('', '_blank');
                    if (!printWindow) {
                      alert('팝업이 차단되었습니다. 팝업 차단을 해제해주세요.');
                      return;
                    }

                    // A4 크기: 210mm x 297mm, 여백 10mm 제외 시 190mm x 277mm
                    // 96 DPI 기준: 약 718px x 1047px
                    const A4_HEIGHT_PX = 1047;
                    const A4_WIDTH_PX = 718;

                    printWindow.document.write(`
                      <!DOCTYPE html>
                      <html>
                      <head>
                        <title>견적서 - ${previewEstimateNumber}</title>
                        <style>
                          @page {
                            size: A4;
                            margin: 10mm;
                          }
                          * {
                            box-sizing: border-box;
                            margin: 0;
                            padding: 0;
                          }
                          html, body {
                            width: 190mm;
                            height: 277mm;
                            overflow: hidden;
                          }
                          body {
                            font-family: 'Malgun Gothic', '맑은 고딕', sans-serif;
                            font-size: 9px;
                            line-height: 1.3;
                            color: #333;
                            padding: 0;
                            margin: 0;
                          }
                          #print-wrapper {
                            transform-origin: top left;
                            width: 190mm;
                          }
                          .container {
                            width: 100%;
                            max-width: 190mm;
                            margin: 0 auto;
                            padding: 3mm;
                          }
                          .estimate-header {
                            margin-bottom: 6px;
                          }
                          .estimate-logoImg {
                            width: 100px !important;
                            height: auto;
                          }
                          .estimate-title {
                            font-size: 18px;
                            font-weight: bold;
                            margin: 3px 0;
                          }
                          .section-title {
                            font-size: 10px;
                            font-weight: bold;
                            margin: 6px 0 3px 0;
                            padding: 2px 5px;
                            background-color: #f5f5f5;
                            border-left: 3px solid #333;
                          }
                          .section-title-main {
                            background-color: #e8e8e8;
                            border-left: 3px solid #000;
                            font-size: 10px;
                            margin-top: 8px;
                          }
                          .section-title-no-margin {
                            margin-top: 0;
                          }
                          .section-group {
                            margin-bottom: 6px;
                          }
                          table {
                            width: 100%;
                            border-collapse: collapse;
                            margin-bottom: 4px;
                            font-size: 7px;
                          }
                          th, td {
                            border: 1px solid #ddd;
                            padding: 2px 3px;
                            text-align: center;
                            vertical-align: middle;
                          }
                          th {
                            background-color: #f8f8f8;
                            font-weight: 600;
                            font-size: 7px;
                          }
                          td input, td textarea {
                            border: none;
                            background: transparent;
                            width: 100%;
                            text-align: center;
                            font-size: 7px;
                            padding: 0;
                            font-family: inherit;
                          }
                          .table2 th, .table2 td {
                            font-size: 7px;
                            padding: 1px 3px;
                          }
                          .estimate-summary-cards {
                            display: flex;
                            justify-content: space-between;
                            gap: 6px;
                            margin: 6px 0;
                          }
                          .summary-card {
                            flex: 1;
                            border-radius: 5px;
                            overflow: hidden;
                          }
                          .summary-card-inactive {
                            opacity: 0.4;
                          }
                          .summary-card-header {
                            padding: 4px;
                            display: flex;
                            justify-content: center;
                            align-items: center;
                          }
                          .summary-card-logo {
                            height: 14px;
                            width: auto;
                          }
                          .summary-card-logo-homeWindow {
                            height: 12px;
                          }
                          .summary-card-content {
                            padding: 4px;
                            background: #fff;
                          }
                          .price-row, .price-row-no-margin, .price-row-total {
                            display: flex;
                            justify-content: space-between;
                            padding: 1px 4px;
                            font-size: 7px;
                          }
                          .price-value-large {
                            font-size: 10px;
                            font-weight: bold;
                          }
                          .discount-badge {
                            text-align: center;
                            font-size: 7px;
                            padding: 1px;
                            background: #f0f0f0;
                            margin: 2px 0;
                            border-radius: 2px;
                          }
                          .discount-badge-arrow {
                            display: none;
                          }
                          .discount-value {
                            color: #dc3545;
                          }
                          .divider {
                            margin: 3px 0;
                            border: none;
                            border-top: 1px solid #ddd;
                          }
                          textarea {
                            width: 100%;
                            border: 1px solid #ddd;
                            padding: 4px;
                            font-size: 7px !important;
                            resize: none;
                            min-height: 30px;
                            font-family: inherit;
                          }
                          /* 할인내역 섹션 숨김 (인쇄 시 제외) */
                          #discount-section {
                            display: none !important;
                          }
                          /* 인라인 스타일 오버라이드 - 브랜드별 할인 적용 텍스트 */
                          .summary-card-content div[style*="font-size"] {
                            font-size: 6px !important;
                          }
                          .summary-card-content div {
                            font-size: 7px !important;
                          }
                          .summary-card-content span {
                            font-size: 7px !important;
                          }
                          .summary-card-content .price-value-large {
                            font-size: 9px !important;
                          }
                          /* 모든 인라인 폰트 크기 오버라이드 */
                          [style*="font-size: 11px"] {
                            font-size: 6px !important;
                          }
                          [style*="font-size: 14px"] {
                            font-size: 7px !important;
                          }
                          [style*="font-size: 16px"] {
                            font-size: 8px !important;
                          }
                          @media print {
                            body {
                              -webkit-print-color-adjust: exact !important;
                              print-color-adjust: exact !important;
                            }
                            .summary-card-header {
                              -webkit-print-color-adjust: exact !important;
                              print-color-adjust: exact !important;
                            }
                          }
                        </style>
                      </head>
                      <body>
                        <div id="print-wrapper">
                          ${printContent.innerHTML}
                        </div>
                        <script>
                          // 컨텐츠 로드 후 A4 전체 크기에 맞게 자동 확대/축소
                          window.onload = function() {
                            const wrapper = document.getElementById('print-wrapper');
                            // 실제 컨텐츠 크기 측정
                            const content = wrapper.querySelector('.container') || wrapper.firstElementChild || wrapper;
                            const rect = content.getBoundingClientRect();
                            const contentHeight = rect.height || wrapper.scrollHeight;
                            const contentWidth = rect.width || wrapper.scrollWidth;
                            const maxHeight = ${A4_HEIGHT_PX};
                            const maxWidth = ${A4_WIDTH_PX};

                            // 가로/세로 각각 A4에 맞게 scale 계산
                            let scaleX = maxWidth / contentWidth;
                            let scaleY = maxHeight / contentHeight;

                            // scale 제한: 최소 0.5, 최대 2.0
                            scaleX = Math.max(0.5, Math.min(scaleX, 2.0));
                            scaleY = Math.max(0.5, Math.min(scaleY, 2.0));

                            wrapper.style.transform = 'scale(' + scaleX + ', ' + scaleY + ')';
                            wrapper.style.transformOrigin = 'top left';
                          };
                        <\/script>
                      </body>
                      </html>
                    `);

                    printWindow.document.close();

                    // 이미지 로딩 및 스케일 계산 대기 후 인쇄
                    setTimeout(() => {
                      printWindow.focus();
                      printWindow.print();
                      printWindow.close();
                    }, 800);
                  }}
                  style={{
                    background: 'none',
                    border: '1px solid #d1d5db',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    color: '#666',
                    padding: '5px 10px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '5px',
                    fontSize: '13px',
                  }}
                  title="인쇄하기"
                >
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <polyline points="6 9 6 2 18 2 18 9"></polyline>
                    <path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"></path>
                    <rect x="6" y="14" width="12" height="8"></rect>
                  </svg>
                  인쇄
                </button>
                {/* 닫기 버튼 */}
                <button
                  onClick={() => {
                    setShowEstimatePreviewModal(false);
                    setEstimatePreviewData(null);
                  }}
                  style={{
                    background: 'none',
                    border: 'none',
                    fontSize: '24px',
                    cursor: 'pointer',
                    color: '#666',
                    padding: '0',
                    width: '30px',
                    height: '30px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  ×
                </button>
              </div>
            </div>
            
            <div style={{ 
              flex: 1,
              padding: '20px',
              overflowY: 'auto',
              backgroundColor: '#f8f9fa'
            }}>
              {estimatePreviewLoading ? (
                <div style={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  height: '100%',
                  fontSize: '16px',
                  color: '#666',
                }}>
                  견적서를 불러오는 중...
                </div>
              ) : estimatePreviewData ? (
                <div
                  id="estimate-preview-print-area"
                  style={{
                    width: '100%',
                    height: '100%',
                    backgroundColor: '#fff',
                    overflow: 'auto'
                  }}
                >
                  <EstimatePreviewReadOnly estimateData={estimatePreviewData} />
                </div>
              ) : (
                <div style={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  height: '100%',
                  fontSize: '16px',
                  color: '#666',
                }}>
                  견적서 데이터를 불러올 수 없습니다.
                </div>
              )}
            </div>
            
            <div style={{
              padding: '15px 20px',
              borderTop: '1px solid #e1e4e8',
              display: 'flex',
              justifyContent: 'flex-end',
              gap: '10px',
            }}>
              <button
                onClick={async () => {
                  await loadEstimate(previewEstimateNumber);
                  // 불러오기 성공 후 미리보기 모달 닫기
                  setShowEstimatePreviewModal(false);
                  setEstimatePreviewData(null);
                }}
                style={{
                  padding: '8px 20px',
                  backgroundColor: '#000',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '4px',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: 'pointer',
                }}
              >
                이 견적서 불러오기
              </button>
              <button
                onClick={() => {
                  setShowEstimatePreviewModal(false);
                  setEstimatePreviewData(null);
                }}
                style={{
                  padding: '8px 20px',
                  backgroundColor: '#fff',
                  color: '#333',
                  border: '1px solid #d1d5db',
                  borderRadius: '4px',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: 'pointer',
                }}
              >
                닫기
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 계약 확인 모달 */}
      {showContractModal && (
        <div className="modal-overlay">
          <div className="modal-container" style={{
            width: '400px',
            maxWidth: '90%',
            padding: '0',
            borderRadius: '8px',
            backgroundColor: 'white',
            position: 'relative'
          }}>
            {/* 모달 헤더 */}
            <div style={{
              padding: '20px 25px 15px',
              borderBottom: '1px solid #e1e4e8',
              backgroundColor: '#f8f9fa',
              borderRadius: '8px 8px 0 0'
            }}>
              <h3 style={{ 
                margin: '0', 
                fontSize: '18px', 
                fontWeight: '600',
                color: '#333'
              }}>
                계약 확인
              </h3>
            </div>
            
            {/* 모달 내용 */}
            <div style={{
              padding: '25px',
              textAlign: 'center'
            }}>
              <div style={{
                fontSize: '16px',
                color: '#333',
                marginBottom: '25px',
                lineHeight: '1.5'
              }}>
                계약 체크를 진행하시겠습니까?
              </div>
              
              <div style={{
                display: 'flex',
                gap: '10px',
                justifyContent: 'center'
              }}>
                <button
                  onClick={cancelContract}
                  style={{
                    padding: '10px 20px',
                    border: '1px solid #d1d5da',
                    backgroundColor: 'white',
                    color: '#586069',
                    borderRadius: '4px',
                    fontSize: '14px',
                    fontWeight: '500',
                    cursor: 'pointer',
                    minWidth: '80px'
                  }}
                >
                  취소
                </button>
                <button
                  onClick={confirmContract}
                  style={{
                    padding: '10px 20px',
                    border: '1px solid #28a745',
                    backgroundColor: '#28a745',
                    color: 'white',
                    borderRadius: '4px',
                    fontSize: '14px',
                    fontWeight: '500',
                    cursor: 'pointer',
                    minWidth: '80px'
                  }}
                >
                  확인
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 계약 해제 확인 모달 */}
      {showUncontractModal && (
        <div className="modal-overlay">
          <div className="modal-container" style={{
            width: '400px',
            maxWidth: '90%',
            padding: '0',
            borderRadius: '8px',
            backgroundColor: 'white',
            position: 'relative'
          }}>
            {/* 모달 헤더 */}
            <div style={{
              padding: '20px 25px 15px',
              borderBottom: '1px solid #e1e4e8',
              backgroundColor: '#f8f9fa',
              borderRadius: '8px 8px 0 0'
            }}>
              <h3 style={{ 
                margin: '0', 
                fontSize: '18px', 
                fontWeight: '600',
                color: '#333'
              }}>
                계약 해제
              </h3>
            </div>
            
            {/* 모달 내용 */}
            <div style={{
              padding: '25px',
              textAlign: 'center'
            }}>
              <div style={{
                fontSize: '16px',
                color: '#333',
                marginBottom: '10px',
                lineHeight: '1.5'
              }}>
                계약 해제를 진행하시겠습니까?
              </div>
            </div>
            
            {/* 모달 푸터 */}
            <div style={{
              padding: '15px 25px 20px',
              display: 'flex',
              justifyContent: 'center',
              gap: '10px',
              borderTop: '1px solid #e1e4e8',
              backgroundColor: '#f8f9fa',
              borderRadius: '0 0 8px 8px'
            }}>
              <button
                onClick={cancelUncontract}
                style={{
                  padding: '10px 20px',
                  border: '1px solid #d1d5db',
                  backgroundColor: 'white',
                  color: '#333',
                  borderRadius: '4px',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: 'pointer',
                  minWidth: '80px'
                }}
              >
                취소
              </button>
              <button
                onClick={confirmUncontract}
                style={{
                  padding: '10px 20px',
                  border: '1px solid #dc3545',
                  backgroundColor: '#dc3545',
                  color: 'white',
                  borderRadius: '4px',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: 'pointer',
                  minWidth: '80px'
                }}
              >
                확인
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Fixed Footer - 견적 불러오기 & 견적서 전송 버튼 */}
      {!isCaptureMode && (
        <div className="no-print fixed-footer">
          <div className="fixed-footer-buttons">
            {/* 왼쪽: 견적 불러오기 버튼 */}
            {PermissionManager.getEstimateLoadScope() !== 'NONE' && (
              <button className="footer-btn-load" onClick={openLoadModal}>
                견적 불러오기
              </button>
            )}

            {/* 오른쪽: 견적서 전송 버튼 */}
            {PermissionManager.canDownloadJPG() && (
              <button className="footer-btn-send" onClick={handleDownload}>
                견적서 전송
              </button>
            )}

            {/* 견적 초기화 버튼 */}
            <button
              className="footer-btn-reset"
              onClick={() => {
                if (window.confirm('입력된 모든 내용이 사라집니다. 초기화하시겠습니까?')) {
                  window.location.reload();
                }
              }}
            >
              견적 초기화 ⟳
            </button>
          </div>
        </div>
      )}

    </div>
  );
};

export default ReactEstimateForm;